import type { InvestorProfile, NavCounts, AnalysisCheck, CheckFinding, CheckVerdict, CheckedFigure, AnalysisFigures, FiguresFrom } from '../types';
import type { DealNumberField, DealRecordLinkView, DealRecordOutcome, FieldValuesView, RecordNumber } from '../types';
import type { LoiNotCheckedKey } from './fieldValues';
import type { ChangeSummary, FilingChanges } from '../types';
import type {
  CreateTourBody,
  FiledTourReportResponse,
  PatchTourBody,
  PatchTourPhotoBody,
  PromoteResult,
  PropertyDuplicateMatch,
  SaveTourPhotoBody,
  TourDetail,
  TourPhotoRow,
  TourPhotoUploadUrl,
  TourPhotoUploadUrlBody,
  TourReportResponse,
  TourRow,
  TourSummary,
} from '../types/tours';
import { landUpsideParams } from './feasibilityQuery';
import type {
  Assumption, AssumptionChange, FeasibilityCoverage, FeasibilityRow, LandUpsideQuery, LandUpsideRow,
  PlanningQuestion, ZoneNoticeView, ZoneStandardView,
} from '../types/feasibility';

const API_BASE = import.meta.env.VITE_API_URL || '/api';

// Mirrors backend/src/lib/subjectResolver.ts's SubjectType. There is no shared-types
// package wired between frontend and backend in this repo (shared/types/database.ts exists
// but nothing imports it), so this is kept in sync by hand - this is the one place on the
// frontend it is defined; everything else imports it from here rather than redeclaring the
// three-way union inline.
export type SubjectType = 'crm_deal' | 'property' | 'master_property';
/** DOC-READ (the 157th): the record on screen, sent with each chat message for the server's document reader only. */
export interface AssistantSubject { type: SubjectType; id: string }

// Mirrors TaskSuggestion in backend/src/services/threadAiService.ts, the shape the AI writes
// onto thread_messages.suggestion_data. Declared once and referenced by both thread message
// shapes below rather than inlined twice: the two must never drift, because ThreadPanel reads
// the same field off whichever of them it happens to hold.
export interface ThreadTaskSuggestion {
  title: string;
  assignee_id: string | null;
  due_date: string | null;
}

/** AIW2 - a Property Database record a My Deals row might be (`GET /api/properties/:id/master-candidates`). */
export interface MasterCandidate { id: string; address: string | null; city: string | null; apn: string | null; matched_on: ('apn' | 'address')[] }
/** LD1 Renewals - the server computes every date and the group (ruling 40); the screen shows them, never re-derives. */
export type RenewalGroup = 'call_now' | 'next_6_months' | 'later' | 'expiry_not_stated';
export interface RenewalRow {
  id: string; kind: 'lease' | 'amendment' | 'listing'; flag: 'lease not found' | null;
  tenant_name: string | null; landlord_name: string | null; address: string | null; unit: string | null; city: string | null;
  /** M11: the unit as the server labels it - "Suite 108" as the lease names it, "Unit C" for a bare one. */
  unit_label: string | null;
  master_property_id: string | null; apex_role: string | null; apex_agents: string[]; options_text: string | null;
  renewal_deal_id: string | null; has_document: boolean; expiration_page: number | null;
  current_expiration: string | null; call_by: string | null; call_by_reason: string | null; option_deadline: string | null;
  rent_today: number | null; rent_starts: string | null; live: boolean; group: RenewalGroup | null;
  /** I4: the agreement whose PDF states the expiry - the lease itself, or the amendment that set it - and how it set it. */
  expiration_agreement_id: string;
  expiration_set_by: { id: string | null; dated: string | null; by: 'new_expiration_date' | 'option_exercised' } | null;
  /** I6a (his answer): why the expiry is not stated ("p. 7, scan"). */
  expiration_unreadable: string | null;
}
export interface RenewalContact { id: string; name: string; email: string | null; phone: string | null }
export interface RenewalDetail {
  renewal: RenewalRow;
  facts: Record<string, unknown> & { cites: Record<string, number>; unreadable: Record<string, string> };
  amendments: Array<Record<string, unknown>>;
  tenant_contact: RenewalContact | null; landlord_contact: RenewalContact | null;
}
/**
 * DOCTRACK (his D15) - a signed agreement the app read and Renewals does not hold yet, mirrored by hand from
 * `backend/src/services/signedAgreementApply.ts`. ⭐ The decision is the SERVER's: the screen says it, never re-derives it.
 */
export type SignedReadDifference = { field: string; onFile: string; read: string };
export type SignedReadDecision =
  | { do: 'link'; agreementId: string }
  | { do: 'insert'; amendsId: string | null }
  | { do: 'disagrees'; agreementId: string; differences: SignedReadDifference[] }
  | { do: 'pick'; candidates: Array<{ id: string; label: string }> };
export interface SignedReadItem {
  filingId: string; title: string; kind: string; decision: SignedReadDecision;
  matches: { id: string; label: string } | null; amends: { id: string; label: string } | null;
}
export interface SignedReadDiff { agreementId: string; cardId: string; filingId: string; differences: SignedReadDifference[] }
/**
 * LISTINGS-AI - his Current Listings, mirrored by hand from `backend/src/services/currentListingsService.ts` (a frontend
 * file never imports backend source - Vercel's build fails it). ⭐ `paperwork_owed`, `confirm_stale` and `kb` are DECIDED
 * by the server; the screen shows them, never re-derives them. `kb_error` / `sync.last_error` arrive already plain.
 */
export type CurrentListingKb = 'in_ghl' | 'not_yet' | 'error' | 'not_sent';
export interface CurrentListingRow {
  id: string; address: string; city: string | null; deal: 'sale' | 'lease'; status: 'active' | 'in_escrow' | 'ended';
  price_text: string | null; size_sf: number | null; lot_acres: number | null; property_kind: string | null;
  public_note: string | null; contact: 'robert' | 'joseph';
  paperwork_owed: boolean; confirmed_at: string | null; confirm_stale: boolean;
  /** The exact words the sync sends; null for a row that is not told to callers. */
  faq: { question: string; answer: string } | null;
  kb: CurrentListingKb; kb_error: string | null;
}
export interface CurrentListingsResponse {
  success: boolean;
  listings: CurrentListingRow[];
  summary: { question: string; answer: string };
  sync: { enabled: boolean; last_run_at: string | null; last_error: string | null };
}
/** Only the fields that changed travel (the route's schema is strict; admin only - a 403 otherwise). */
export type CurrentListingPatch = Partial<Pick<CurrentListingRow, 'status' | 'price_text' | 'size_sf' | 'lot_acres' | 'public_note' | 'contact'>>;
/**
 * NB1 - `GET /api/net-lease/benchmark`: the server's one calculator (`backend/src/services/netLeaseBenchmarkService.ts`),
 * mirrored by hand like the other shapes here. ⭐ Ruling 40: the screen formats these numbers, never re-derives them.
 */
export type NetLeaseTerm = 5 | 10 | 15;
export interface NetLeaseBenchmark {
  tenant_input: string;
  match: { tenant: string; by: 'name' | 'alias'; alias: string | null } | null;
  facts: { sector: string; parent_rating: string; rating_tier: 'ig' | 'hy' | 'nr'; lease_type: string | null; escalations: string | null; locations: string | null } | null;
  source: { name: string; short: string; url: string | null; as_of: string; basis: 'asking' | 'closed' } | null;
  terms: Array<{ term_years: NetLeaseTerm; cap_low: number; cap_high: number }>;
  years_remaining: number | null;
  lease_ended: string | null;
  term: { wanted: NetLeaseTerm; used: NetLeaseTerm; substituted: boolean; shorter_than_published: boolean } | null;
  versus: { national_low: number; national_high: number; national_mid: number; cap: number; price: number; position: 'below' | 'inside' | 'above';
    delta_to_range_bps: number; delta_to_mid_bps: number; price_at_mid: number; premium_usd: number; spread_to_treasury_bps: number | null } | null;
  ten_year: { cap_low: number; cap_high: number; value_low: number; value_high: number } | null;
  treasury: { value: number; as_of: string; source: string } | null;
  land_price_per_sf: number | null;
  california: {
    basis: 'closed'; window: { from: string; to: string }; term_years: NetLeaseTerm; count: number;
    median: number | null; p25: number | null; p75: number | null;
    sales: Array<{ tenant_name: string; city: string | null; sale_date: string; cap: number; years_remaining: number; price: number | null }>;
    left_out: Record<string, number>;
    other_terms: number;
  } | null;
  flags: string[];
  notes: string[];
  paragraph: string;
  /** Every row of the panel, worded by the server (ruling 40; NB1 review I2 + I3). null when the ladder holds no such tenant. */
  lines: {
    national: string; california: string; deal: string; price: string | null; treasury: string | null; ten_year: string | null;
    rent_to_sales: string; land: string;
    /** null when the tenant matched by name. */
    alias: string | null;
    /** The notes no row already says. */
    notes: string[];
  } | null;
}
export interface NetLeaseBenchmarkQuery {
  tenant: string; lease_end: string | null; noi: number | null; price: number | null; lease_type: string | null;
  year_built: number | null; land_sf: number | null; loan: number | null; debt_service: number | null;
}

/** AIW2-ADD - the My Deals row's own address; the Add form opens on it. */
export interface MasterRowAddress { address: string | null; city: string | null; state: string | null; zip: string | null }

/**
 * Client portal admin shapes. Mirror backend/src/controllers/portalController.ts; there is no
 * shared-types package wired up in this repo, so these are kept in sync by hand.
 *
 * Note what is NOT here: a token field on PortalLinkRow. The database stores only a sha256
 * hash, so a link that already exists can never be re-read - the raw token is returned once,
 * by mintPortalLink, and nowhere else.
 */
export interface PortalLinkRow {
  id: string;
  portal_id: string;
  tier: string;
  label: string;
  expires_at: string | null;
  revoked_at: string | null;
  created_at: string;
}

export interface PortalAdminRow {
  id: string;
  slug: string;
  title: string;
  created_at: string;
  updated_at: string;
  /** AREA1: the contract's `meta.area` - set on an area page, null on every other portal. */
  area?: string | null;
  links?: PortalLinkRow[];
}

/** AREA1 - who opened an area's pages (backend portalAreaRollup.ts). "Opened" leaves out mail scanners. */
export interface AreaRollup {
  portals: number;
  opened: number;
  openedRaw: number;
  downloaded: number;
  perPortal: Array<{ id: string; slug: string; title: string; opened: boolean; views: number; rawViews: number; downloads: number; lastSeen: string | null }>;
}

export interface PortalActivityRow {
  id: string;
  link_id: string | null;
  event: string;
  doc_id: string | null;
  user_agent: string | null;
  at: string;
}

/**
 * A note as the BROKER sees it - hidden rows included, plus the label of the link that wrote
 * it, joined server-side.
 *
 * `link_label: null` is AMBIGUOUS on purpose and must not be rendered as a plain blank: it
 * means either the writing link was deleted, or the admin read of the links table failed and
 * every label came back empty. Neither case is "no link", so the UI says so on hover.
 */
export interface PortalNoteAdminRow {
  id: string;
  anchor_type: string;
  anchor_key: string;
  author_name: string;
  body: string;
  created_at: string;
  hidden_at: string | null;
  link_label: string | null;
}

/**
 * HM1 S3 - one row of the change queue. Mirrors `ChangeProposalRow` + `QueueRow` in
 * backend/src/services/changeProposalService.ts BY HAND, for the same reason every other type here is: a
 * frontend file that imports backend source is GREEN locally and RED on Vercel (tsc follows the type-only
 * import into express, whose node_modules exist here and not there). Two production builds died that way.
 */
export type ChangeProposalStatus = 'proposed' | 'approved' | 'rejected' | 'applied' | 'undone' | 'sending' | 'failed';
export type ChangeProposalKind =
  | 'portal_edit'
  | 'file_document'
  | 'property_field'
  | 'crm_stage'
  | 'task'
  | 'draft'
  | 'match'
  | 'lead_message'
  // CHAT-ACTIONS (backend PROPOSAL_KINDS, mirrored by hand): what the Assistant changes for him.
  | 'activity'
  | 'crm_deal'
  | 'deal_contact'
  | 'contact'
  | 'contact_field'
  | 'ghl_task'
  | 'ghl_tag'
  | 'ghl_workflow'
  | 'ghl_opportunity';

/**
 * LEADS1 - a `lead_message` proposal's payload. Mirrors backend/src/services/leadMessage/types.ts BY HAND (trap 20).
 * ⚠️ It reaches the page as `ChangeProposal.payload` (a JSON column) cast to this type, so nothing here is PROVEN by
 *    the compiler - the card reads it defensively (a missing list is empty, a non-string source is stringified).
 */
export interface LeadReceipt {
  quote: string;
  sourceType: string;
  sourceRef: string;
  field?: string | null;
  value: string;
  asOf?: string | null;
}
export interface LeadReceiptIssue {
  kind: 'unverified' | 'mismatch' | 'bad_receipt';
  text: string;
  detail: string;
}
export interface LeadDraftEdit {
  at: string;
  by: string;
  before: { body: string; subject: string | null };
  after: { body: string; subject: string | null };
}
export interface LeadMessagePayload {
  contactId: string;
  contactName: string | null;
  opportunityId?: string | null;
  propertyLabel: string | null;
  channel: 'sms' | 'email';
  subject: string | null;
  body: string;
  trigger: 'unanswered' | 'needs_decision' | 'broken_promise' | 'gone_quiet';
  why: string;
  decisionQuestion: string | null;
  receipts: LeadReceipt[];
  receiptCheck: { ok: boolean; issues: LeadReceiptIssue[]; checkedAt: string };
  edits?: LeadDraftEdit[];
  /** The lead's last three INBOUND messages at filing, oldest first (newest last), each cut to 300 characters. */
  recentInbound?: { at: string; body: string }[];
  /** Set on approve: when the worker may send. */
  sendAfter: string | null;
  /** Set on approve: true when the switch was off - recorded as "would have sent", nothing goes out. */
  shadow: boolean;
  snoozeUntil: string | null;
}

/**
 * LEADS1 - what the send worker writes into a finished lead message's `applied_ref`. Mirrors `LeadSendRecord` in
 * backend/src/services/leadMessage/types.ts BY HAND (trap 20). Read off `ChangeProposal.applied_ref` by a cast, so
 * nothing in it is proven: ⛔ only a literal `shadow: false` is a real send (the worker's own fail-closed reading).
 */
export interface LeadSendRecord {
  shadow: boolean;
  channel: 'sms' | 'email';
  /** Set on a real send. */
  sentAt?: string;
  /** Set instead of `sentAt` in shadow mode. */
  wouldHaveSentAt?: string;
  sendAttemptedAt?: string;
  ghlMessageId?: string | null;
}

export interface ChangeProposal {
  id: string;
  kind: ChangeProposalKind;
  target_type: string;
  target_id: string;
  payload: Record<string, unknown>;
  source_ref: string;
  proposed_by: string;
  status: ChangeProposalStatus;
  applied_ref: Record<string, unknown> | null;
  decided_by: string | null;
  decided_at: string | null;
  decided_note: string | null;
  created_at: string;
  /**
   * LEADS1 - the row's version. ⛔ A lead message is approved and edited AT THIS VERSION (`expectedUpdatedAt`): the
   * server refuses any other with a 409, so Joseph can never approve a body he was not shown.
   */
  updated_at: string;
  /** What this change would do, in words - built server-side, never a figure. */
  summary: string;
  /** Null when it can be approved; a sentence when its kind has no applier yet. */
  held: string | null;
  /** Set when an APPLIED change turned out to change nothing - "Applied" must not imply "wrote something". */
  outcome: string | null;
  /**
   * CQ1 - who asked, and who decided. Mirrors `ProposalActor` in the backend service BY HAND (trap 20).
   *
   * ⛔ `isAgent` is the ROLE, never the name. Anabel's `full_name` reads "Anabel (Hermes agent)" today and a
   *    name is editable text; the badge a reviewer trusts has to come from `profiles.role`.
   */
  proposedBy: ChangeProposalActor | null;
  /** Whoever made the LATEST decision - undo overwrites `decided_by`, so the VERB follows the status. */
  decidedBy: ChangeProposalActor | null;
}

export interface ChangeProposalActor {
  id: string;
  name: string;
  email: string;
  role: string;
  isAgent: boolean;
}

/**
 * VP9 - the in-app portal editor. Mirrors `EditorView` / `EditEntry` in backend/src/services/portalEdits.ts BY HAND:
 * a frontend file never imports backend source (the Vercel build has no backend node_modules - trap 20).
 */
export type PortalEditKind = 'status' | 'timelineAdd' | 'openItemAdd' | 'openItemEdit' | 'openItemClose' | 'import';

export interface PortalEditBy {
  id: string | null;
  email: string | null;
  name: string | null;
  via?: string;
}

export interface PortalEditEntry {
  id: string;
  seq: number;
  at: string;
  by: PortalEditBy;
  kind: PortalEditKind;
  target: string | null;
  before: unknown;
  after: unknown;
  source?: string | null;
  state: 'active' | 'undone' | 'absorbed' | 'superseded' | 'record';
  stateAt?: string;
  stateBy?: PortalEditBy;
  stateReason?: string;
  import?: {
    path: string;
    stamped: string[];
    kept: number[];
    absorbed: number[];
    superseded: number[];
    clashes: Array<{ seq: number; target: string; resolution: 'keep' | 'take-import' }>;
    /** The import rebuilt the previous import from the stored page, after something wrote around the editor. */
    rebased?: boolean;
    rebasedOver?: Array<{ seq: number; target: string }>;
  };
  /** Only the latest active change to a thing may be undone; the server decides and says so here. */
  undoable: boolean;
}

export interface PortalEditorView {
  portal: { id: string; slug: string; title: string; updatedAt: string };
  /** False when the stored contract is not the last import plus its changes - edit nothing until it is re-imported. */
  consistent: boolean;
  supports: { status: boolean; timeline: boolean; openItems: boolean; openItemsWhy: string | null };
  states: Array<{ state: string; group: string; groupLabel: string }>;
  timelineKinds: string[];
  deals: Array<{ id: string; name: string; city: string | null; state: string | null; group: string | null; label: string | null; asOf: string | null }>;
  timeline: Array<{ id: string | null; date: string | null; kind: string | null; title: string | null; dealIds: string[] }>;
  openItems: Array<{ id: string | null; dealId: string | null; item: string | null; why: string | null; owner: string | null; closedAt: string | null }>;
  history: PortalEditEntry[];
}

export interface PortalEditBody {
  expectedUpdatedAt: string;
  kind: Exclude<PortalEditKind, 'import'>;
  target?: string;
  fields?: Record<string, unknown>;
  source?: string | null;
}

export class APIError extends Error {
  constructor(
    public status: number,
    message: string,
    /**
     * The PARSED response body, not just its `error` string. Added for A5 (duplicate
     * prevention): the 409 answer to a create carries a `candidates` array, and without this
     * the whole feature is dead on arrival - `request()` used to read `data.error` for the
     * message and drop `data` on the floor, so the candidate list never reached the caller
     * and the duplicate dialog would have had nothing to render.
     *
     * Optional on purpose: every existing `new APIError(status, message)` call site keeps
     * compiling untouched. Callers that want the body must narrow it themselves - see
     * `isDuplicateConflict` in utils/duplicateChoice.ts, which is the only sanctioned way to
     * decide that a failure is a duplicate. Do NOT sniff the message string for that: the
     * message survives, but the candidates do not.
     */
    public body?: unknown
  ) {
    super(message);
  }
}

/**
 * The server could not be REACHED - no signal, a dropped connection, or /auth/refresh answering 5xx. It is
 * NOT a rejected session, and nothing that throws it clears one.
 *
 * ⭐ Why it is its own class (WT1, 2026-09-23): reopening the installed app inside a tilt-up building LOGGED
 *    HIM OUT, because every failure on the way to the server read as "the session is bad". With the session
 *    gone, the walk-through queue on the phone could not drain until he found signal AND typed his password.
 *    Callers tell "offline, try later" from "refused" with `instanceof NetworkError`, never by the message.
 *
 * ⛔ It extends TypeError, not plain Error, ON PURPOSE: a fetch that never reached the server has always
 *    rejected with a TypeError (the Fetch spec's own class), and screens written before this class branch on
 *    exactly that - ContractBuilder's "Could not reach the server. Nothing was generated." is the reassurance
 *    that no legal document was filed. Extending Error silently sent those screens down their "the server
 *    said something" branch instead.
 */
export class NetworkError extends TypeError {
  constructor(message = 'No connection - check your signal and try again.') {
    super(message);
    this.name = 'NetworkError';
  }
}

/**
 * Every key the session lives under. Cleared together, always.
 *
 * `auth_user` is the cached profile AuthContext restores when the app opens with no signal. It is part of
 * the session so a logout (or a rejected refresh) can never leave one user's name and role behind for the
 * next person to open the app.
 */
export const SESSION_KEYS = ['token', 'refresh_token', 'token_expires_at', 'auth_user'] as const;

export const clearSession = (): void => {
  SESSION_KEYS.forEach((k) => localStorage.removeItem(k));
};

/**
 * End THIS device's session on the server (AUD-H5, the 124th). Sign out used to clear the browser and nothing else, so the
 * session stayed live and a copied token outlived the button. The backend's logout ends only this session (scope local).
 * ⛔ Best effort and bounded: the token is read NOW, before the caller clears storage; 4 s caps the wait; any failure is
 *   swallowed - an offline Sign out must still sign out.
 */
export const revokeServerSession = async (): Promise<void> => {
  const token = localStorage.getItem('token');
  if (!token) return;
  const controller = new AbortController();
  const timer = setTimeout(() => controller.abort(), 4000);
  try {
    await fetch(`${API_BASE}/auth/logout`, {
      method: 'POST',
      headers: { Authorization: `Bearer ${token}` },
      signal: controller.signal,
    });
  } catch {
    // best effort - the browser is cleared regardless
  } finally {
    clearTimeout(timer);
  }
};

/** Persist a session returned by /auth/login or /auth/refresh. */
export const storeSession = (session: {
  access_token: string;
  refresh_token?: string;
  expires_at?: number;
}): void => {
  localStorage.setItem('token', session.access_token);
  // Supabase ROTATES the refresh token on every exchange, so this must be overwritten each
  // time. Keeping the original would work for exactly one refresh and then start failing an
  // hour later - indistinguishable from the bug this whole mechanism fixes.
  if (session.refresh_token) localStorage.setItem('refresh_token', session.refresh_token);
  if (session.expires_at) localStorage.setItem('token_expires_at', String(session.expires_at));
};

/**
 * In-flight refresh, shared by every caller. Backlog B17.
 *
 * SINGLE-FLIGHT IS LOAD-BEARING, not an optimisation. A page typically has several requests
 * in the air at once; when the access token expires they ALL 401 within milliseconds of each
 * other. Without this, each one starts its own refresh, and because Supabase rotates the
 * refresh token on use, the first exchange invalidates the token the others are still
 * holding - so the first succeeds and the rest fail, clearing the session and bouncing the
 * user to /login. That is the exact failure this was meant to remove, made harder to
 * reproduce because it only shows up under concurrency.
 *
 * Every concurrent caller receives the SAME outcome, 'unreachable' included - so six requests
 * that 401 together while the phone has no signal all keep the session, and none of them logs
 * the others out.
 */
type RefreshOutcome = 'renewed' | 'rejected' | 'unreachable';

let refreshInFlight: Promise<RefreshOutcome> | null = null;

const doRefresh = async (): Promise<RefreshOutcome> => {
  const refreshToken = localStorage.getItem('refresh_token');
  if (!refreshToken) return 'rejected';

  let res: Response;
  try {
    // Raw fetch, NOT request() - going through request() would recurse straight back into
    // the 401 handler below.
    res = await fetch(`${API_BASE}/auth/refresh`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ refresh_token: refreshToken }),
    });
  } catch {
    // ⛔ A network failure is NOT an expired session. This branch used to return false, which
    //    logged the user out - the WT1 cold start inside a building. Now the session is KEPT and
    //    the caller hears NetworkError; the next request with signal refreshes normally.
    return 'unreachable';
  }

  // A 5xx is the server (or its host) failing, not a verdict on the token: keep the session. So is a 429:
  // /auth/refresh shares /auth/login's limiter, and a few mistyped passwords from the same office must not
  // log out someone whose refresh token is good.
  if (res.status >= 500 || res.status === 429) return 'unreachable';
  if (!res.ok) return 'rejected';

  let body: { session?: { access_token?: string; refresh_token?: string; expires_at?: number } } | null;
  try {
    body = await res.json();
  } catch {
    // A 2xx that is not JSON did not come from this API (a captive portal, a proxy page) - the
    // server was not reached, so it cannot have refused the session.
    return 'unreachable';
  }
  if (!body?.session?.access_token) return 'rejected';

  storeSession(body.session as { access_token: string; refresh_token?: string; expires_at?: number });
  return 'renewed';
};

const refreshOnce = (): Promise<RefreshOutcome> => {
  if (!refreshInFlight) {
    refreshInFlight = doRefresh().finally(() => {
      refreshInFlight = null;
    });
  }
  return refreshInFlight;
};

async function request<T>(
  endpoint: string,
  options: RequestInit = {},
  isRetry = false
): Promise<T> {
  const token = localStorage.getItem('token');

  let response: Response;
  try {
    response = await fetch(`${API_BASE}${endpoint}`, {
      ...options,
      headers: {
        'Content-Type': 'application/json',
        ...(token && { Authorization: `Bearer ${token}` }),
        ...options.headers,
      },
    });
  } catch (err) {
    // An abort is the caller's own choice, not a lost connection - hand it back untouched.
    if (err instanceof Error && err.name === 'AbortError') throw err;
    throw new NetworkError();
  }

  let data: any;
  try {
    data = await response.json();
  } catch (parseError) {
    // A 2xx that is not JSON stays a loud failure, as before. A FAILED answer that is not JSON (a
    // proxy's HTML 502 page, an empty 404) is still a failure with a status - it used to surface
    // as a SyntaxError, which no caller could tell apart from a bug.
    if (response.ok) throw parseError;
    data = null;
  }

  if (!response.ok) {
    if (response.status === 401) {
      // Try exactly once to renew, then replay the original request. `isRetry` is what makes
      // "once" true - without it a genuinely revoked session would loop refresh/retry until
      // the stack gave out. /auth/login and /auth/refresh are excluded because a 401 from
      // either means the credentials themselves are bad, not that the session aged out.
      const renewable = !isRetry && endpoint !== '/auth/refresh' && endpoint !== '/auth/login';

      if (renewable) {
        const outcome = await refreshOnce();
        if (outcome === 'renewed') return request<T>(endpoint, options, true);
        // ⛔ Could not REACH the server to renew: keep the session and say "offline". Only a
        //    refresh the server actually refused may log him out (the WT1 cold start).
        if (outcome === 'unreachable') throw new NetworkError();
      }

      clearSession();
      window.location.href = '/login';
    }
    throw new APIError(response.status, data?.error || 'Request failed', data ?? undefined);
  }

  return data;
}

/**
 * The deal types `crm_deals` accepts. Mirrors the backend; kept here because there is no
 * shared-types package wired between frontend and backend in this repo.
 *
 * The promotion proposal can only ever GUESS among 'sale' | 'lease' | 'acquisition' (those are
 * the only ones a GoHighLevel opportunity-name prefix implies). The other two are pickable by
 * hand and are never proposed - which is why `proposedDealType` is the narrower union.
 */
export type CrmDealType = 'sale' | 'lease' | 'listing' | 'acquisition' | 'disposition';

/**
 * What the server can say about promoting a GHL opportunity into a `crm_deals` row.
 *
 * `alreadyLinkedDealId` non-null means the promotion already happened - the UI must offer the
 * contract builder for that deal and NOT offer to promote a second time.
 *
 * `proposedDealType: null` is the common case (most opportunity names carry no prefix) and it
 * is NOT a defect: the deal type decides which AIR form gets built, so absent a prefix the
 * choice belongs to the broker, never to a default.
 */
export interface PromotionProposal {
  opportunityId: string;
  name: string | null;
  monetaryValue: number | null;
  prefix: string | null;
  proposedDealType: 'sale' | 'lease' | 'acquisition' | null;
  alreadyLinkedDealId: string | null;
}

export interface PromotionResult {
  dealId: string;
  /** false = an existing deal was returned rather than a new row being written. */
  created: boolean;
  dealType: CrmDealType;
}

/**
 * REFUSE RATHER THAN GUESS, at the wire boundary.
 *
 * Every field below decides something that is expensive to get wrong, and the failure mode of
 * reading it off an unrecognised body is always SILENT - a blank, not an error:
 *
 *   - `alreadyLinkedDealId` ABSENT is not the same as `null`. Absent reads as falsy, the page
 *     concludes "not promoted yet" and offers to promote an opportunity that ALREADY HAS A
 *     DEAL. That is why presence is checked with `in` and never with a truthiness or `!= null`
 *     test - the two cases have opposite meanings and only `in` separates them.
 *   - `dealId` absent -> the confirmation renders a link to `/contracts/undefined`.
 *   - `created` absent -> the banner claims a deal was created when nothing was written.
 *
 * `name`, `monetaryValue` and `prefix` are deliberately NOT required: they feed display copy
 * only, and the copy already handles null. Requiring them would turn a cosmetic gap into a
 * refusal, which is the wrong direction.
 *
 * These throw, which the page already catches - so a shape it does not recognise becomes a
 * visible banner with the promote control withheld, instead of a blank field on the record a
 * legal document is built from.
 */
const isRecord = (v: unknown): v is Record<string, unknown> =>
  typeof v === 'object' && v !== null;

const shapeError = (detail: string) =>
  new Error(
    `The promotion API answered in a shape this client does not recognise (${detail}). ` +
      'Refusing rather than guessing - no deal was read or written.'
  );

/**
 * The wire shape is the BACKEND's: `{ success, proposal: { …snake_case } }` for the GET and a
 * FLAT `{ success, message, deal_id, created, deal_type }` for the POST.
 *
 * That is not a preference, it is this repo's actual convention - `crm-deals` responses are
 * `{ success, <namedKey>: … }` throughout (`{success, briefing}`, `{success, cockpit}`,
 * `{success, money}`) and there is not one `{success, data}` site in the controller family.
 * These readers are the single place the wire names are spoken; everything above them uses the
 * camelCase `PromotionProposal` / `PromotionResult` the rest of the frontend expects.
 */
function readPromotionProposal(body: unknown): PromotionProposal {
  const d = isRecord(body) ? body.proposal : undefined;
  if (!isRecord(d)) throw shapeError('no `proposal` object');
  // Presence, not value. Both of these are legitimately null and null must survive - an ABSENT
  // `already_linked_deal_id` read as falsy would offer to promote an opportunity that already
  // has a deal, producing a SECOND crm_deals row for one opportunity.
  for (const key of ['proposed_deal_type', 'already_linked_deal_id'] as const) {
    if (!(key in d)) throw shapeError(`\`${key}\` missing`);
  }
  // `monetary_value` is DECIMAL(15,2); PostgREST returns numerics as STRINGS. A
  // `typeof === 'number'` test alone would silently blank every real figure - the
  // jsonb string-numerics trap already on record in this repo.
  const rawValue = d.monetary_value;
  const monetaryValue =
    typeof rawValue === 'number' ? rawValue
      : typeof rawValue === 'string' && rawValue.trim() !== '' && Number.isFinite(Number(rawValue))
        ? Number(rawValue)
        : null;
  return {
    opportunityId: typeof d.opportunity_id === 'string' ? d.opportunity_id : '',
    name: typeof d.name === 'string' ? d.name : null,
    monetaryValue,
    prefix: typeof d.prefix === 'string' ? d.prefix : null,
    proposedDealType: (d.proposed_deal_type ?? null) as PromotionProposal['proposedDealType'],
    alreadyLinkedDealId: (d.already_linked_deal_id ?? null) as string | null,
  };
}

function readPromotionResult(body: unknown): PromotionResult {
  // FLAT, not nested - the POST has no envelope key of its own.
  const d = isRecord(body) ? body : undefined;
  if (!isRecord(d)) throw shapeError('not an object');
  if (typeof d.deal_id !== 'string' || d.deal_id === '') throw shapeError('`deal_id` missing');
  // A boolean check, not truthiness: `created: false` is a real, meaningful answer - it is the
  // already-linked path, and reporting it as "created" would claim a write that never happened.
  if (typeof d.created !== 'boolean') throw shapeError('`created` missing');
  if (typeof d.deal_type !== 'string' || d.deal_type === '') throw shapeError('`deal_type` missing');
  return { dealId: d.deal_id, created: d.created, dealType: d.deal_type as CrmDealType };
}

/**
 * D26 - the AIR CRE contract builder (`/api/contracts/*`).
 *
 * 🚨 THE ENVELOPE HERE IS `{ success, data: … }`, and that is NOT the convention of the
 * `crm-deals` family directly above, which is `{ success, <namedKey> }`. Verified against
 * `backend/src/controllers/contractController.ts` rather than inferred from its neighbours:
 * the preview answers `{ success, data: { fields, unfilledRequired } }` and the generate
 * answers `{ success, data: { fileId, linkId, revision, report } }`. Do NOT "unify" the two
 * families without reading both controllers - the promotion pair above was wrong in exactly
 * that way, and when it was settled the client was the side that moved.
 */

/**
 * CHAT-DOCS: mirrors `DocumentCard` / `Review` / `ReviewItem` in backend/src/services/chatDocumentTypes.ts.
 * ⭐ Mirrored DELIBERATELY (no shared package), like ContractField: a change there must be made here too.
 */
export type ChatDocumentKind = 'listing_agreement' | 'agency_disclosure' | 'loi';
export type ChatDocumentStatus = 'draft' | 'superseded' | 'filed' | 'needs_fixing';
export interface ChatReviewItem { level: 'problem' | 'note' | 'ok'; field?: string; text: string }
export interface ChatReview { verdict: 'ready' | 'needs_fixing'; items: ChatReviewItem[] }
/** What the chat shows under a message when a document was made. `url` is a signed link (one hour). */
export interface DocumentCard {
  id: string;
  kind: ChatDocumentKind;
  title: string;
  version: number;
  status: ChatDocumentStatus;
  url: string;
  review: ChatReview;
}

/** Mirrors `ValueSource` in backend/src/services/contractFillService.ts. */
export const CONTRACT_VALUE_SOURCES = ['deal', 'property', 'broker', 'manual', 'unfilled'] as const;
export type ContractValueSource = (typeof CONTRACT_VALUE_SOURCES)[number];

export const CONTRACT_COLUMNS = ['left', 'right'] as const;
export type ContractColumn = (typeof CONTRACT_COLUMNS)[number];

/** Mirrors `ResolvedField`: one review-screen row - a value, where it came from, and WHERE IT IS. */
export interface ContractField {
  fieldId: string;
  label: string;
  type: string;
  required: boolean;
  value: string;
  source: ContractValueSource;
  /**
   * 🚨 THE LOCATOR. Labels are NOT unique - 95 of the purchase agreement's 213 rows share one,
   * including eight `Address:` on page 11 and 15 rows labelled only `⟦BLANK⟧`. Without these the
   * operator hand-typing a notice address is choosing blind between identical rows, and a wrong
   * pick prints a real value in the counterparty's block.
   */
  page: number;
  column: ContractColumn;
  /** Declared by the MAP. `null` means the blank is not column-specific - never inferred here. */
  side: 'lessor' | 'lessee' | null;
  /** Document-order rank. The screen sorts on it; the array arrives in map order. */
  order: number;
  /** DOCTRACK part 3: only on the APN - "from the title report, not checked" while its report is unchecked. */
  note?: string;
}

/**
 * A "check one" group on the form, with side-suppressed boxes ALREADY REMOVED by the server.
 *
 * 🚨 THE SCREEN RE-EVALUATES THESE, and owns exactly one rule: count the ticks, there must be
 * exactly one. It does NOT re-derive which boxes are in play - the server did that, because
 * suppression is policy and a second implementation of policy on the client is this codebase's
 * most repeated defect class. See `FillResult.elections` in contractFillService.ts.
 */
export interface ContractElection {
  id: string;
  label: string;
  fields: string[];
}

/**
 * A one-way dependency: if `whenFilled` carries a value, `thenTicked` must be marked. NOT an
 * election - an election is "exactly one of these". See `FormCoupling` in backend formMap.ts.
 */
export interface ContractCoupling {
  id: string;
  label: string;
  whenFilled: string[];
  thenTicked: string;
}

/** Mirrors `FillResult`. */
export interface ContractPreview {
  fields: ContractField[];
  /** Field IDs the SERVER considers required-and-blank, before any local edit. */
  unfilledRequired: string[];
  /** "Check one" groups, side-filtered. Re-evaluated live against local edits. */
  elections: ContractElection[];
  /** Value-implies-tick rules. Re-evaluated live, same reasoning as elections. */
  couplings: ContractCoupling[];
}

/** Mirrors `StampReportEntry` in backend/src/services/contractStampService.ts. */
export interface ContractStampEntry {
  fieldId: string;
  status: string;
  fontSize?: number;
  drawnWidth?: number;
}

export interface ContractGeneration {
  fileId: string;
  linkId: string;
  revision: string;
  /** What was actually drawn. `refused_overflow` means a value did NOT print. */
  report: ContractStampEntry[];
}

/** One contract already filed against a deal, with a ready-to-click signed URL. */
export interface FiledContract {
  linkId: string;
  title: string;
  filedAt: string | null;
  url: string;
  /** FILED-LABEL (the 119th): the file's kind (`contract`, `loi`, `offering_memorandum`...), null where none was stored. */
  category: string | null;
}

function readFiledContracts(body: unknown): FiledContract[] {
  const d = isRecord(body) ? body.data : undefined;
  if (!isRecord(d)) throw contractShapeError('no `data` object');
  // Presence, not length. `[]` is the real "nothing filed yet" answer; an ABSENT list reads
  // identically on screen while meaning "the server never said", and the difference matters -
  // one is "you have not sent a contract", the other is "we do not know what you have sent".
  if (!Array.isArray(d.contracts)) throw contractShapeError('`contracts` is not an array');
  return d.contracts.map((c, i) => {
    if (!isRecord(c)) throw contractShapeError(`contracts[${i}] is not an object`);
    if (typeof c.linkId !== 'string' || c.linkId === '') throw contractShapeError(`contracts[${i}] has no \`linkId\``);
    if (typeof c.title !== 'string' || c.title === '') throw contractShapeError(`contracts[${i}] has no \`title\``);
    if (typeof c.url !== 'string' || c.url === '') throw contractShapeError(`contracts[${i}] has no \`url\``);
    return {
      linkId: c.linkId, title: c.title, filedAt: typeof c.filedAt === 'string' ? c.filedAt : null, url: c.url,
      category: typeof c.category === 'string' && c.category !== '' ? c.category : null,
    };
  });
}

/** What `GET /api/contracts/:linkId/download` answers: a short-lived signed URL, plus the name. */
export interface ContractDownload {
  url: string;
  filename: string;
}

/**
 * REFUSE RATHER THAN GUESS, again. An absent `url` renders `href="undefined"`, which navigates
 * to a relative path on the app's own origin and looks like a broken page rather than a missing
 * document - so the operator would conclude the contract is corrupt when it is fine.
 */
function readContractDownload(body: unknown): ContractDownload {
  const d = isRecord(body) ? body.data : undefined;
  if (!isRecord(d)) throw contractShapeError('no `data` object');
  if (typeof d.url !== 'string' || d.url === '') throw contractShapeError('`url` missing');
  if (typeof d.filename !== 'string' || d.filename === '') throw contractShapeError('`filename` missing');
  return { url: d.url, filename: d.filename };
}

/**
 * One request shape for both calls. An options object rather than positional arguments on
 * purpose: `formCode`, `dealId` and `representation` are three adjacent strings, and swapping
 * two of them at a call site is invisible to `tsc`.
 *
 * `representation` is typed as `string` and validated SERVER-side (`contractController.ts`
 * answers `400 representation must be one of: lessor, lessee, both`). That 400 now reaches the
 * operator, so the check is not lost - it is just made in the one place that also decides the
 * agency disclosure.
 */
export interface ContractRequest {
  formCode: string;
  dealId: string;
  representation: string;
  /** The operator's edits, keyed by fieldId. Only `generate` sends these. */
  overrides?: Record<string, string>;
}

/** DOCTRACK part 4: a comparison answer this client does not recognise is an error, never "no changes". */
const changesShapeError = (detail: string) =>
  new Error(`The document-changes API answered in a shape this client does not recognise (${detail}).`);
const LIST_KEYS = ['comparedTerms', 'notes', 'termChanges', 'disagreements', 'onlyInOne', 'sentenceChanges', 'others'] as const;
function readFilingChanges(body: unknown): FilingChanges {
  const d = isRecord(body) ? body.data : undefined;
  if (!isRecord(d)) throw changesShapeError('no `data` object');
  if (typeof d.filingId !== 'string' || typeof d.fileLinkId !== 'string') throw changesShapeError('no `filingId`');
  if (d.view !== null) {
    if (!isRecord(d.view)) throw changesShapeError('`view` is not an object');
    for (const k of LIST_KEYS) if (!Array.isArray(d.view[k])) throw changesShapeError(`\`view.${k}\` is not an array`);
  }
  return d as unknown as FilingChanges;
}
function readChangeSummaries(body: unknown): ChangeSummary[] {
  const d = isRecord(body) ? body.data : undefined;
  if (!isRecord(d) || !Array.isArray(d.summaries)) throw changesShapeError('`summaries` is not an array');
  return d.summaries as ChangeSummary[];
}

const contractShapeError = (detail: string) =>
  new Error(
    `The contract API answered in a shape this client does not recognise (${detail}). ` +
      'Refusing rather than guessing - this screen builds a legal document.'
  );

/**
 * REFUSE RATHER THAN GUESS, at the wire boundary - the same reasoning as the promotion readers
 * above, with higher stakes. Every check below guards a value whose absence fails SILENTLY:
 *
 *   - `required` absent reads as falsy, so a blank that must BLOCK generation stops blocking
 *     it and the contract prints with a required blank empty. Checked as a BOOLEAN and never
 *     for truthiness: `required: false` is a real and common answer.
 *   - `value` absent is not `''`. `undefined !== ''`, so a missing value reads as FILLED -
 *     the opposite of the truth - and it also makes that row's input uncontrolled.
 *   - `source` absent or unknown renders an EMPTY provenance cell, and provenance is the
 *     screen's entire reason to exist. Membership is tested against the real union rather
 *     than `typeof === 'string'`, because `SOURCE_LABEL[<anything else>]` is `undefined`.
 *   - `fieldId` absent collapses the React keys, writes every edit under the same "undefined"
 *     override key, and prints "undefined" in the left-blank list.
 *   - `label` absent leaves the operator editing an unnamed blank on a legal document.
 */
function readContractField(raw: unknown, index: number): ContractField {
  if (!isRecord(raw)) throw contractShapeError(`fields[${index}] is not an object`);
  const { fieldId, label, type, required, value, source } = raw;
  if (typeof fieldId !== 'string' || fieldId === '') {
    throw contractShapeError(`fields[${index}] has no \`fieldId\``);
  }
  if (typeof label !== 'string' || label === '') throw contractShapeError(`\`${fieldId}\` has no \`label\``);
  if (typeof type !== 'string' || type === '') throw contractShapeError(`\`${fieldId}\` has no \`type\``);
  if (typeof required !== 'boolean') throw contractShapeError(`\`${fieldId}\` has no boolean \`required\``);
  if (typeof value !== 'string') throw contractShapeError(`\`${fieldId}\` has no string \`value\``);
  if (!(CONTRACT_VALUE_SOURCES as readonly unknown[]).includes(source)) {
    throw contractShapeError(`\`${fieldId}\` has an unrecognised \`source\``);
  }
  // The locator is checked as strictly as `required` and `value`, and for the same reason: its
  // absence fails SILENTLY. A missing `page` renders "pundefined", a missing `order` collapses
  // every row to the same rank and restores the unreadable map ordering, and a missing `column`
  // puts two identical labels back to being indistinguishable - which is the state that lets a
  // real value land in the counterparty's block. Degrading quietly here is not safer than
  // refusing; it is the defect.
  const { page, column, side, order } = raw;
  if (typeof page !== 'number' || !Number.isFinite(page)) throw contractShapeError(`\`${fieldId}\` has no numeric \`page\``);
  if (typeof order !== 'number' || !Number.isFinite(order)) throw contractShapeError(`\`${fieldId}\` has no numeric \`order\``);
  if (!(CONTRACT_COLUMNS as readonly unknown[]).includes(column)) throw contractShapeError(`\`${fieldId}\` has an unrecognised \`column\``);
  // `null` is a real and common answer here (the blank is not column-specific), so it is
  // enumerated explicitly rather than tested for truthiness. An ABSENT `side` is `undefined`,
  // which this same check rejects - so no separate `'side' in raw` guard is needed, and adding
  // one would be a guard that can never fire. (It was written, and the positive control caught
  // it doing nothing.)
  if (side !== null && side !== 'lessor' && side !== 'lessee') throw contractShapeError(`\`${fieldId}\` has an unrecognised \`side\``);
  return {
    fieldId, label, type, required, value, source: source as ContractValueSource,
    page, column: column as ContractColumn, side, order,
  };
}

function readContractPreview(body: unknown): ContractPreview {
  const d = isRecord(body) ? body.data : undefined;
  if (!isRecord(d)) throw contractShapeError('no `data` object');
  if (!Array.isArray(d.fields)) throw contractShapeError('`fields` is not an array');
  // Presence, not length. `[]` is the legitimate "nothing required is still blank" answer;
  // an ABSENT list reads identically while meaning "the server never said".
  if (!Array.isArray(d.unfilledRequired)) throw contractShapeError('`unfilledRequired` is not an array');
  // Same reasoning as `unfilledRequired` directly above: `[]` is the legitimate "no election is
  // unsettled" answer, while an ABSENT list reads identically and means "the server never said".
  // These gate the Generate button, so silently treating absent as none would re-open the exact
  // hole J6 closed.
  if (!Array.isArray(d.elections)) throw contractShapeError('`elections` is not an array');
  // Same reasoning again: absent reads identically to `[]` while meaning "the server never said",
  // and this gates the Generate button.
  if (!Array.isArray(d.couplings)) throw contractShapeError('`couplings` is not an array');
  return {
    fields: d.fields.map((f, i) => readContractField(f, i)),
    unfilledRequired: d.unfilledRequired.map((id) => String(id)),
    elections: d.elections.map((e, i) => readContractElection(e, i)),
    couplings: d.couplings.map((c, i) => readContractCoupling(c, i)),
  };
}

function readContractCoupling(raw: unknown, i: number): ContractCoupling {
  if (!isRecord(raw)) throw contractShapeError(`coupling ${i} is not an object`);
  if (typeof raw.id !== 'string' || raw.id === '') throw contractShapeError(`coupling ${i}: \`id\` missing`);
  if (typeof raw.label !== 'string' || raw.label === '') throw contractShapeError(`coupling ${raw.id}: \`label\` missing`);
  if (typeof raw.thenTicked !== 'string' || raw.thenTicked === '') {
    throw contractShapeError(`coupling ${raw.id}: \`thenTicked\` missing`);
  }
  if (!Array.isArray(raw.whenFilled) || raw.whenFilled.length === 0) {
    throw contractShapeError(`coupling ${raw.id}: \`whenFilled\` is empty`);
  }
  return {
    id: raw.id, label: raw.label, thenTicked: raw.thenTicked,
    whenFilled: raw.whenFilled.map((f) => String(f)),
  };
}

function readContractElection(raw: unknown, i: number): ContractElection {
  if (!isRecord(raw)) throw contractShapeError(`election ${i} is not an object`);
  if (typeof raw.id !== 'string' || raw.id === '') throw contractShapeError(`election ${i}: \`id\` missing`);
  if (typeof raw.label !== 'string' || raw.label === '') throw contractShapeError(`election ${raw.id}: \`label\` missing`);
  if (!Array.isArray(raw.fields) || raw.fields.length === 0) {
    throw contractShapeError(`election ${raw.id}: \`fields\` is empty`);
  }
  return { id: raw.id, label: raw.label, fields: raw.fields.map((f) => String(f)) };
}

function readContractGeneration(body: unknown): ContractGeneration {
  const d = isRecord(body) ? body.data : undefined;
  if (!isRecord(d)) throw contractShapeError('no `data` object');
  // These three ARE the proof that a contract was filed - `revision` is shown to the operator
  // by name. Any one of them blank turns the success banner into a claim with nothing behind
  // it, which is the precise failure the confirmation exists to prevent.
  for (const key of ['fileId', 'linkId', 'revision'] as const) {
    if (typeof d[key] !== 'string' || d[key] === '') throw contractShapeError(`\`${key}\` missing`);
  }
  if (!Array.isArray(d.report)) throw contractShapeError('`report` is not an array');
  return {
    fileId: d.fileId as string,
    linkId: d.linkId as string,
    revision: d.revision as string,
    report: d.report as ContractStampEntry[],
  };
}

const analysisCheckShapeError = (detail: string) =>
  new Error(
    `The analysis-check API answered in a shape this client does not recognise (${detail}). ` +
      'Refusing rather than guessing - a wrong finding here is worse than none.'
  );

/**
 * The only four verdicts the backend's `compareFigures` can produce - `'noted'` joined the other
 * three 2026-09-19 (Change 2), for an informational figure (currently just `purchase_price`)
 * whose difference from the analysis is expected rather than a disagreement to raise an alarm
 * over. Kept as a runtime array, not just the type, so an unrecognised STRING can actually be
 * told apart from a real one - `raw.verdict as CheckFinding['verdict']` alone lets anything
 * through the cast uninspected. `'noted'` MUST be in this list: leaving it out would make the
 * client normalise every real `noted` finding from the server into `could-not-check` with
 * reason `unrecognised-verdict` - silently defeating the whole point of the backend change.
 */
const REAL_VERDICTS: readonly CheckVerdict[] = ['confirmed', 'disagrees', 'could-not-check', 'noted'];

/**
 * The two figures the checker ever compares (narrowed 2026-09-19 from five to three, then from
 * three to two the same day - `rent_psf` was a units mismatch, not a missing column; see the
 * `CheckedFigure` comment in types/index.ts). Same reasoning as `REAL_VERDICTS` above.
 */
const REAL_FIGURES: readonly CheckedFigure[] = ['purchase_price', 'gross_income'];

/**
 * The five reasons the backend's `compareFigures` can attach to a `could-not-check` verdict
 * (backend/src/services/analysisChecker.ts). Same reasoning as `REAL_VERDICTS`/`REAL_FIGURES`
 * above: kept as a runtime array so an unrecognised STRING can be told apart from a real one -
 * round 1's fix validated `verdict` and `figure` this way and left `reason`, the third union
 * field on this same wire object, cast straight through unchecked.
 *
 * `'unrecognised-verdict'` is deliberately NOT in this list even though it is now a member of
 * the frontend's `CouldNotCheckReason` union (types/index.ts): it is set below, by THIS CLIENT,
 * for a verdict it could not interpret - it is never read off the wire, and the backend has no
 * way to send it.
 */
const REAL_REASONS: readonly string[] = [
  'no-source-document', 'source-lookup-failed', 'document-unreadable', 'document-read-failed',
  'figure-not-stated', 'not-in-analysis',
];

/** IMPORTANT 3: the two possible answers to "what does `figuresFrom` mean". Same reasoning as
 *  `REAL_VERDICTS`/`REAL_FIGURES`/`REAL_REASONS` above - kept as a runtime array so an
 *  unrecognised string can be told apart from a real one. */
const REAL_FIGURES_FROM: readonly FiguresFrom[] = ['screen', 'saved'];

/**
 * T5/T6 - the analysis checker (`POST /api/analysis-check/:propertyId`), `{ success, data }`.
 * Mirrors backend/src/services/analysisChecker.ts's `AnalysisCheck` / `CheckFinding` BY HAND -
 * same reasoning as the contract readers above, no shared-types package is wired up. REFUSE
 * RATHER THAN GUESS: a finding silently dropped or misread would show a wrong verdict on a
 * figure about to go out in a PDF.
 */
function readAnalysisCheck(body: unknown): AnalysisCheck {
  const d = isRecord(body) ? body.data : undefined;
  if (!isRecord(d)) throw analysisCheckShapeError('no `data` object');
  if (typeof d.propertyId !== 'string' || d.propertyId === '') throw analysisCheckShapeError('`propertyId` missing');
  if (typeof d.checkedAt !== 'string' || d.checkedAt === '') throw analysisCheckShapeError('`checkedAt` missing');
  if (!Array.isArray(d.findings)) throw analysisCheckShapeError('`findings` is not an array');
  // CRITICAL 1: refused, never guessed - the panel renders a different sentence for `'screen'`
  // than for `'saved'`, and a silently-defaulted value here would tell the reader the wrong one.
  if (!(REAL_FIGURES_FROM as readonly string[]).includes(d.figuresFrom as string)) {
    throw analysisCheckShapeError('`figuresFrom` is not `screen` or `saved`');
  }
  const figuresFrom = d.figuresFrom as FiguresFrom;

  // `null` is the real "no document" answer; anything else must carry both an id and a title -
  // an untitled source is exactly the wrong-document risk this feature exists to catch, because
  // the panel names the source first so a human can catch a wrong pairing by reading it.
  let sourceDocument: AnalysisCheck['sourceDocument'] = null;
  if (d.sourceDocument !== null) {
    const s = d.sourceDocument;
    if (!isRecord(s) || typeof s.id !== 'string' || typeof s.title !== 'string') {
      throw analysisCheckShapeError('`sourceDocument` is not `null` or `{id, title}`');
    }
    sourceDocument = { id: s.id, title: s.title };
  }

  const findings: CheckFinding[] = d.findings.map((raw, i) => {
    if (!isRecord(raw)) throw analysisCheckShapeError(`findings[${i}] is not an object`);
    if (typeof raw.figure !== 'string' || raw.figure === '') throw analysisCheckShapeError(`findings[${i}] has no \`figure\``);
    if (typeof raw.verdict !== 'string' || raw.verdict === '') throw analysisCheckShapeError(`findings[${i}] has no \`verdict\``);

    // ⛔⛔ THIS FEATURE'S OWN DEFECT CLASS, IN ITS LAST MILE. A `verdict` this client has never
    // seen (a newer backend, a typo) used to sail through the cast below and fall out of all
    // three buckets AnalysisCheckPanel filters into (`disagrees` / `could-not-check` /
    // `confirmed`) - dropped from the screen while `confirmed.length` / `findings.length` still
    // counted it in the denominator, so the panel read "2 of 3 confirmed" with one finding
    // silently gone. Normalised to `could-not-check` rather than thrown (throwing would refuse
    // the WHOLE check over one unrecognised row) or cast through unchecked (the defect this
    // closes): that bucket is the one place "this client does not know what this means" is both
    // VISIBLE and never mistaken for a tick.
    const verdictRecognised = (REAL_VERDICTS as readonly string[]).includes(raw.verdict);
    const verdict: CheckFinding['verdict'] = verdictRecognised
      ? (raw.verdict as CheckFinding['verdict'])
      : 'could-not-check';
    if (!verdictRecognised) {
      console.error('[AnalysisCheck] unrecognised verdict from the server:', raw.verdict);
    }

    // A `figure` this client has never seen has no bucket as safe as `could-not-check` to fall
    // into - LABEL in AnalysisCheckPanel has three keys, not "everything else". Kept as its own
    // raw string rather than thrown away: the panel's label lookup falls back to this same
    // string when it has no entry for it, so the worst case is an unfamiliar word on screen,
    // never the literal text "undefined disagrees".
    if (!(REAL_FIGURES as readonly string[]).includes(raw.figure)) {
      console.error('[AnalysisCheck] unrecognised figure from the server:', raw.figure);
    }

    // Round 2 finding 5 - the SAME defect class as `verdict`/`figure` above, but on `reason`,
    // the third union field the first fix left unchecked. `WHY[f.reason ?? 'figure-not-stated']`
    // downstream (AnalysisCheckPanel.tsx) asserts a SPECIFIC, false claim for a null reason - so
    // neither failure below may resolve to `null`, which would just move that exact bug here:
    //   - `verdict` was unrecognised (above). Whatever `reason` the server sent, if any, was
    //     paired with a verdict this client cannot interpret - restating it would describe a
    //     state that was never established, so it is OVERRIDDEN with this client's own reason
    //     for the row, never read off the wire.
    //   - `reason` itself is outside the five `REAL_REASONS` the backend's compareFigures can
    //     produce (a newer backend, a typo). Kept as its own raw string rather than thrown away -
    //     same treatment as `figure` above; AnalysisCheckPanel's WHY lookup falls back to an
    //     honest generic sentence for any reason not one of its known keys, never a blank.
    let reason: CheckFinding['reason'];
    if (!verdictRecognised) {
      reason = 'unrecognised-verdict';
    } else if (raw.reason === null) {
      reason = null;
    } else if (typeof raw.reason === 'string') {
      if (!REAL_REASONS.includes(raw.reason)) {
        console.error('[AnalysisCheck] unrecognised reason from the server:', raw.reason);
      }
      reason = raw.reason as CheckFinding['reason'];
    } else {
      reason = null;
    }

    return {
      figure: raw.figure as CheckFinding['figure'],
      verdict,
      analysisValue: typeof raw.analysisValue === 'number' ? raw.analysisValue : null,
      documentValue: typeof raw.documentValue === 'number' ? raw.documentValue : null,
      quote: typeof raw.quote === 'string' ? raw.quote : null,
      page: typeof raw.page === 'number' ? raw.page : null,
      reason,
    };
  });

  return { propertyId: d.propertyId, sourceDocument, findings, checkedAt: d.checkedAt, figuresFrom };
}


/**
 * Who a radar row is about, composed SERVER-SIDE so the chip, the GHL tag and the GHL note
 * cannot disagree. ⛔ `landlord` has no phone field because there is no landlord phone in this
 * data - a landlord is a second LEAD, never a second CALL.
 */
export interface RadarLandlordContact {
  name: string;
  title: string | null;
  phone: string | null;
  email: string | null;
  sourceUrl: string | null;
  capturedAt: string | null;
}

export interface RadarParties {
  contact: { kind: 'tenant' | 'owner'; name: string | null; phone: string | null } | null;
  /**
   * ⛔ The landlord ENTITY has no phone field. A number can only appear under `capturedContact`,
   *   which is null until a human researched this landlord and SAVED what they found - so an
   *   un-researched landlord cannot be rendered as callable however this screen is edited.
   */
  landlord: { name: string; rep: string | null; capturedContact: RadarLandlordContact | null } | null;
}

/** The exact words behind one AI field on a development row, and the filing they are in (spec 9.2). */
export interface RadarFactSource {
  quote: string;
  portal_url: string;
  received: string;
}

/** A party a CEQA filing names - the developer or owner, or a consultant. The quote is the filing's own words. */
export interface RadarDevelopmentParty {
  name: string;
  company: string | null;
  quote: string;
  portal_url: string;
}

/** A developer contact a PERSON captured (plan Task 18) - the only home a developer phone has on this screen. */
export interface RadarDeveloperContact {
  name: string;
  title: string | null;
  company: string | null;
  phone: string | null;
  email: string | null;
  sourceUrl: string | null;
  capturedAt: string;
  ghlContactId: string | null;
}

/**
 * A radar row, whichever lane it came from. The lanes differ; the row shape the screen uses does not.
 *
 * ⛔⛔⛔ EVERY OPTIONAL FIELD BELOW IS ONE THE BACKEND ALREADY SENDS. This interface is the only
 *    thing standing between a field being sent and a field being SHOWN - and on 2026-09-21 Joseph
 *    opened the live app and found nine of them missing from the screen because they were never
 *    declared here. `sf_occupied` appeared ZERO times in this file while 568 of 572 lease rows
 *    carried one, so `signalLine()` never had it to render.
 *    ⭐ Adding a field to a lane means adding it HERE, or the screen silently drops it.
 */
export interface RadarRow {
  signal_type: string;
  entity_id: string;
  address: string | null;
  city: string | null;
  band: string | null;
  readiness: string;
  phone: string | null;
  phone_kind?: 'direct' | 'site' | null;
  owner_human_name?: string | null;
  owner_name?: string | null;
  owner_title?: string | null;
  owner_email?: string | null;
  contact_name?: string | null;
  tenant_name?: string | null;
  landlord?: string | null;
  landlord_representative?: string | null;
  /** ⭐⭐ The number the 2,000 SF floor JUDGES a lease row on, and it was shown on 0% of them. */
  sf_occupied?: number | null;
  /** Opens the property. ⛔ At `/data-hub/:id` - this is a `master_properties` id, NOT an analyzer one. */
  master_property_id?: string | null;
  parties?: RadarParties;
  blocker?: string | null;
  /**
   * The LANDLORD half of a lease row - its own signal (`lease_landlord`), its own mark.
   *
   * ⛔⛔ Separate because `radar_marks` is unique on (org_id, signal_type, entity_id) and
   *    re-working a row REPLACES its mark: one shared signal would mean marking the tenant Called
   *    silently ERASED the landlord lead. ⭐ His call: the row stays until BOTH are worked.
   */
  landlordMark?: { id: string; outcome: string } | null;
  landlordSuppressed?: boolean;
  landlordReturnReason?: string | null;
  maturity_date?: string | null;
  lease_expiration?: string | null;
  mortgage_amount?: number | null;
  lender_name?: string | null;
  months_until?: number | null;
  /** Expired lane only. */
  standard_status?: string;
  expiration_date?: string | null;
  list_price?: number | null;
  original_list_price?: number | null;
  /** ⚠️ `cumulative_dom` is live; `days_on_market` is frozen at capture. Prefer the former. */
  cumulative_dom?: number | null;
  days_on_market?: number | null;
  /** ⭐⭐ The number the floor JUDGES an expired row on. A literal 0 means no building - judge the lot. */
  building_sf?: number | null;
  lot_sf?: number | null;
  apns?: string[] | null;
  county?: string | null;
  /**
   * THE NEW-DEVELOPMENT LANE (CEQA, plan Task 17's `toLaneRow`) - every field it sends, by name.
   * ⛔⛔ `ceqaLane.contract.test.ts` (backend) fails when one is sent and not declared here: the 96th's
   *    defect, caught before a screen can drop it. The shared fields (`building_sf`, `apns`, `county`, `city`,
   *    `address`, `readiness`, `phone`) are declared above.
   */
  /** The project id - the same value as `entity_id` on this lane. */
  id?: string;
  name?: string;
  lead_agency?: string;
  sch_numbers?: string[];
  case_numbers?: string[];
  first_received?: string;
  /** The latest filing's document-type CODE. The mark's value is built from it, never from the label (C6). */
  latest_doc_type?: string;
  latest_label?: string;
  latest_received?: string;
  latest_portal_url?: string;
  kind_label?: string | null;
  kind_source?: RadarFactSource | null;
  use_label?: string | null;
  use_source?: RadarFactSource | null;
  building_sf_source?: RadarFactSource | null;
  site_acres?: number | null;
  site_acres_source?: RadarFactSource | null;
  /** ⛔ The developer or owner the filing names: context until a PERSON captures a contact, never callable. */
  developer?: RadarDevelopmentParty | null;
  /** ⛔ Context, never a lead - Kimley-Horn was filed as Project Lion's "applicant". */
  consultants?: RadarDevelopmentParty[];
  filings?: Array<{ doc_type: string; label: string; received: string; portal_url: string }>;
  /** Tracked properties a project parcel matches ("On your property"). `master_properties` ids: `/data-hub/:id`. */
  on_property?: Array<{ master_property_id: string; address: string | null }>;
  capturedContact?: RadarDeveloperContact | null;
  timesFailed?: number;
  unsized?: boolean;
  suppressed: boolean;
  returnReason: string | null;
  routedTo: string;
  mark: { id: string; outcome: string } | null;
  /** ⛔ null = not in GHL at all. [] = in GHL with no campaign tags. DIFFERENT answers. */
  activeCampaigns: string[] | null;
  ghlContactId: string | null;
}

export interface RadarSignalsResponse {
  generatedAt: string;
  counts: Record<string, number>;
  loansPastDue: RadarRow[];
  loansMaturingSoon: RadarRow[];
  leasesExpiringSoon: RadarRow[];
  freshListings: RadarRow[];
  expiredListings: RadarRow[];
  /** ⛔ Optional: a backend older than this screen sends none, and the page reads that as an empty lane. */
  newDevelopments?: RadarRow[];
  /**
   * A lane that FAILED to load, by lane, in words (plan decision 11). ⛔ The other lanes still arrive; the
   *   screen names the failure instead of showing an empty lane as "nothing to do".
   */
  laneErrors?: Record<string, string>;
}

/** What one developer lookup returned (plan Task 18). ⛔ It PROPOSES; a person saves. */
export interface RadarDeveloperResearch {
  entity_id: string;
  status: 'proposed' | 'company_only' | 'nothing' | 'rejected_no_search';
  proposal: {
    name: string; title: string | null; phone: string | null; email: string | null;
    sourceUrl: string; ownDomain: boolean;
  } | null;
  searchCount: number;
  reason?: string;
  /** The developer or owner the lookup found, when it found one (proposed or company_only). */
  company: string | null;
  /** The page a `company_only` answer was found on - where a researcher picks it up. */
  companySource: string | null;
  /** ⭐ What this lookup cost, recorded against the 30-day cap before it was shown (C8). */
  costUsd: number;
}

/** The CEQA reader's caps (spec 7.2) - settings he can see and move, never walls. Task 11's `AiBudget`. */
export interface CeqaBudget {
  feed: 'ceqa';
  cap_load_usd: number;
  cap_night_usd: number;
  cap_30d_usd: number;
  checkpoint_first_month: boolean;
  updated_at: string;
  updated_by: string | null;
}

export type CeqaBudgetPatch = Partial<Pick<CeqaBudget, 'cap_load_usd' | 'cap_night_usd' | 'cap_30d_usd' | 'checkpoint_first_month'>>;

/** The first-month checkpoint's report (plan Task 15's `checkpointReport`). */
export interface CeqaCheckpointReport {
  read: number;
  spend: number;
  costPerRead: number;
  remainingCandidates: number;
  projectedRemaining: number;
  summary: string;
}

/** One run as the status endpoint summarizes it - field for field plan Task 16's `summarizeRun`. */
export interface CeqaRunSummary {
  id: string;
  kind: 'nightly' | 'load';
  status: 'running' | 'paused' | 'done' | 'failed';
  /** A running load whose process has been silent 30 minutes. It can be continued. */
  stalled: boolean;
  window_from: string;
  window_to: string;
  filings_seen: number;
  candidates: number;
  read_count: number;
  unreadable: number;
  screened_out: number;
  spend_usd: number;
  pause_reason: 'checkpoint' | 'cap_run' | 'cap_30d' | 'api_error' | 'unknown_price' | null;
  pause_detail: string | null;
  waiting: number;
  unknown_doc_types: string[];
  report: CeqaCheckpointReport | null;
  months: Array<{ from: string; to: string; candidates: number; read: number; status: 'pending' | 'done' }>;
  started_at: string;
  finished_at: string | null;
}

/** A filing whose read could not be used - listed, never dropped (spec 6.4). */
export interface CeqaUnreadable {
  portal_url: string;
  title: string | null;
  city: string | null;
  doc_type: string;
  received: string;
  outcome: string;
  reason: string | null;
}

export interface CeqaStatusResponse {
  budget: CeqaBudget;
  /** Every CEQA read AND every developer lookup in the last 30 days - the 30-day cap counts both (C8). */
  spend30d: number;
  runs: { load: CeqaRunSummary | null; nightly: CeqaRunSummary | null };
  unreadable: CeqaUnreadable[];
  counts: { public: number; industrial: number; notIndustrial: number; screenedOut: number };
  nightlyEnabled: boolean;
}

export interface RadarActivityRow {
  id: string;
  signal_type: string;
  entity_id: string;
  outcome: string;
  marked_at: string;
  marked_by_name: string | null;
  band_at_mark: string | null;
  value_at_mark: string | null;
  note: string | null;
}

/**
 * TODO1 - the To-dos page's shapes, mirrored by hand from `backend/src/services/todoService.ts` (⛔ never imported: a
 * frontend file that reaches into backend source passes locally and fails Vercel's build).
 */
export type TodoSweepDecision = 'done' | 'open' | 'dropped';
/** ⭐ R11: a Later choice is a TOKEN; the server turns it into a time in Pacific ('YYYY-MM-DD' = 7:30 AM that day). */
export type LaterWhen = 'later_today' | 'tomorrow' | 'monday' | (string & {});
export interface TodoItem {
  ghl_task_id: string;
  title: string;
  contact_name: string | null;
  due_at: string | null;
  date_added: string | null;
  completed: boolean;
  /** ⭐ R14: null = no contact in GoHighLevel - the app cannot close it (Done/Drop are refused). */
  ghl_contact_id: string | null;
  /** PHONE1: its GoHighLevel page - the Call link, ready before the tap. null = no contact (R14) or no location id. */
  ghl_contact_url?: string | null;
  assigned_to: string | null;
  /** Nobody is assigned in GoHighLevel - it is on every person's list (his word). */
  shared: boolean;
  snoozed_until: string | null;
  note: string | null;
  sweep_decision: TodoSweepDecision | null;
}
/** A to-do he closed in the app, kept on the page for 7 days: greyed, with Reopen. */
export interface FinishedTodo {
  ghl_task_id: string;
  title: string;
  contact_name: string | null;
  due_at: string | null;
  closed_at: string;
  how: 'done' | 'dropped';
  ghl_contact_id: string | null;
}
export interface TodoSweep { started: boolean; finished: boolean; pending: number; next: TodoItem[] }
export interface MyTodos {
  /** false = his app login matches no GoHighLevel user by email. */
  linked: boolean;
  /** Open and in the 30-day window, the server's order (most overdue first); snoozed ones included. */
  todos: TodoItem[];
  /** Open but older than the window: a count, never a list. */
  olderCount: number;
  /** Closed in the app in the last 7 days, newest first - the greyed Done section. */
  closed: FinishedTodo[];
  sweep: TodoSweep;
  /** ⭐ R4: minutes since the last complete copy pass that covered him; null = never. */
  copyAgeMinutes: number | null;
  /**
   * Task 10: calls with no Call Summary yet - how many, and the earliest one's start (ISO). null = none known. The
   * server answers its last check at once and never waits for a new one, so a first load after a restart has none yet.
   */
  watchdog: { missing: number; since: string } | null;
}
export interface TodoDetail { body: string | null; callSummary: string | null }


/**
 * LEADS-PAGE - the Leads page's shapes, mirrored by hand from `backend/src/services/leadsBoard.ts` (⛔ never imported:
 * a frontend file that imports backend source passes locally and fails the Vercel build - trap 20).
 */
export type LeadStatus = 'our_move' | 'follow_up' | 'their_move' | 'older';
export type LeadReason = 'unanswered' | 'no_touch' | 'broken_promise' | 'gone_quiet';
export type LeadRole = 'tenant' | 'buyer' | 'seller';
export interface LeadTouch { at: string; channel: 'text' | 'call' | 'email' | 'other'; by: 'joseph' | 'robert' | 'other' | null; preview: string | null }
export interface LeadRow {
  contact_id: string;
  name: string;
  phone: string | null;
  email: string | null;
  ghl_contact_url: string | null;
  properties: Array<{ tag: string; label: string }>;
  roles: LeadRole[];
  is_agent: boolean;
  last_inbound: LeadTouch | null;
  last_outbound: LeadTouch | null;
  days_since_our_touch: number | null;
  open_tasks: number;
  overdue_tasks: number;
  next_task: { title: string; due_at: string | null } | null;
  note: { body: string; pinned: boolean; at: string | null } | null;
  status: LeadStatus;
  reasons: LeadReason[];
  why: string;
  activity: 'read' | 'reading' | 'unreadable';
  last_activity_at: string | null;
}
export interface LeadsBoard {
  rows: LeadRow[];
  tabs: Array<{ tag: string; label: string; count: number }>;
  reading: number;
  refreshing: boolean;
  total_leads: number;
  read_at: string;
}

/**
 * CAMP1 - the Campaign tracker's shapes, mirrored by hand from `backend/src/services/campaignTrackerRules.ts` and
 * `campaignTrackerService.ts` (⛔ never imported: trap 20). GoHighLevel sends; the app tracks.
 */
export type TrackerSectionKey = 'replied' | 'hot' | 'unsubscribed' | 'follow_up' | 'not_sent' | 'waiting';
export type TrackerMemberStatus = 'replied' | 'unsubscribed' | 'hot' | 'not_sent' | 'follow_up' | 'waiting' | 'reading';
export interface TrackedCampaign {
  id: string; name: string; kind: 'email' | 'portal'; ghl_tag: string; ghl_schedule_id: string | null; subject: string | null;
  sent_at: string; area: string | null;
  /** GoHighLevel's own counts for the campaign; null when not read. */
  ghl_counts: { total: number | null; success: number | null; failed: number | null; queued: number | null; read_at: string } | null;
  last_synced_at: string | null; sync_error: string | null; created_at: string;
}
export interface TrackedMember {
  id: string; campaign_id: string; ghl_contact_id: string; name: string | null; email: string | null; additional_emails: string[];
  phone: string | null; dnd_call: boolean; portal_link_id: string | null; sent_at: string | null; reply_at: string | null;
  reply_channel: 'email' | 'sms' | null; reply_subject: string | null; reply_preview: string | null; auto_reply_at: string | null;
  unsubscribed: boolean; unsubscribed_reason: string | null; portal_views: number; portal_downloads: number;
  last_portal_at: string | null; read_state: 'unread' | 'read' | 'unreadable'; synced_at: string | null;
  status: TrackerMemberStatus; hot_reasons: string[]; ghl_contact_url: string | null;
}
export interface TrackerResults {
  members: number; sent: number; not_sent: number; failed_in_ghl: number | null; replied: number; auto_replies: number;
  /** null = not measured (no member has a portal link) - shown as "-". */
  unsubscribed: number; portal_opened: number | null; hot: number;
  /** Members not read yet: in no section, in neither sent nor not sent. */
  reading: number;
}
export interface TrackerBoardAnswer {
  campaign: TrackedCampaign;
  sections: Array<{ key: TrackerSectionKey; members: TrackedMember[] }>;
  results: TrackerResults;
  syncing: boolean;
  /** Whether the viewer's Gmail was read for email replies this time. */
  mail: 'read' | 'not_connected' | 'unreadable' | 'pending';
  read_at: string;
}
export interface GhlEmailCampaign {
  id: string; name: string; subject: string | null; status: string; sentAt: string | null;
  total: number | null; success: number | null; failed: number | null; queued: number | null; hasTracking: boolean;
}

/**
 * MORNING1 - the Morning page's shapes, mirrored by hand from `backend/src/services/morningService.ts` (⛔ never imported).
 * ⛔ An errored read is never "nothing": a section the server could not read is `unreadable`, with words for the screen.
 */
export type MorningSection<T> = { status: 'ok'; items: T[]; more: number } | { status: 'unreadable'; words: string };
/**
 * One Gmail thread waiting on him (no body). `at` is an instant; `gmail_url` opens the thread in Gmail. MORNING2:
 * `from_address` is the bare, lower-case address of the thread's last sender - the server always sends it; optional
 * here only because no screen reads it (the server matches it to a to-do's contact) and MORNING1's fixtures predate it.
 */
export interface MorningMailItem {
  thread_id: string; from: string; from_address?: string; subject: string; at: string; attachment_names: string[]; gmail_url: string;
}
export interface MorningText {
  conversation_id: string; contact_id: string | null; contact_name: string | null; last_message_body: string | null;
  last_message_date: string;
  /** No owner in GoHighLevel - on every linked person's list (R9). */
  shared: boolean;
  ghl_contact_url: string | null;
}
export interface MorningCall {
  conversation_id: string; contact_id: string | null; contact_name: string | null; at: string; shared: boolean;
  ghl_contact_url: string | null;
}
/** `due_date` is a DATE ('YYYY-MM-DD'), not an instant. */
export interface MorningDeadline { deadline_id: string; deal_id: string; deal_name: string | null; label: string; due_date: string; overdue: boolean }
export interface MyMorning {
  /** false = this person's email matches no GoHighLevel user: nothing is read. */
  linked: boolean;
  /**
   * ⛔ `truncated`: Gmail had more threads than were read, so `more` is a floor - say "and more", never a number.
   * `inbox_url`: his inbox by address, so the right Google account opens.
   */
  mail: { status: 'ok'; items: MorningMailItem[]; more: number; truncated: boolean; inbox_url: string } | { status: 'unreadable'; words: string }
    | { status: 'not_connected' };
  texts: MorningSection<MorningText>;
  calls: MorningSection<MorningCall>;
  todos: MorningSection<MorningTodo>;
  /** The due to-dos past the first 5 ("and N more" on the To-dos page). */
  todosMore: number;
  deadlines: MorningSection<MorningDeadline>;
  radar: { status: 'ok'; line: string } | { status: 'unreadable'; words: string };
  /**
   * MORNING2 - the chat block (`MorningChat`). The server always sends it; absent is read as off, so an answer without
   * it (an older server) gets today's page.
   */
  chat?: MorningChat;
}
/**
 * MORNING2-AI Task 14 - "Suggested for you", mirrored BY HAND from `backend/src/services/morningSuggestRules.ts`
 * (`SuggestionRow`, `OlderRow`, `SuggestionView`) and `morningPrefs.ts` (`SuggestRunNote`). ⛔ Never imported.
 */
export interface MorningSuggestion {
  id: string;
  /** null = shared: a deal has no owner in the app, so it is on both lists. */
  user_id: string | null;
  source_kind: 'deadline' | 'call_note';
  source_quote: string;
  title: string;
  due_date: string | null;
  ghl_contact_id: string | null;
  deal_id: string | null;
  status: 'shown' | 'adding' | 'added' | 'dismissed';
  first_shown_at: string;
  acted_by: string | null;
}
export type MorningOlderSuggestion = MorningSuggestion & { outcome: 'left' | 'added' | 'dismissed'; by_name: string | null };
export interface MorningSuggestRun { status: 'ok' | 'skipped' | 'failed'; words: string | null }
export type MySuggestions =
  | { on: false }
  | {
    on: true;
    view: { status: 'ok'; shown: MorningSuggestion[]; more: number; more_names: string[];
      /** Every shown row past the first five, in order: what the more line opens. Optional: absent = none (an older server). */
      rest?: MorningSuggestion[]; older: MorningOlderSuggestion[]; more_line: string | null };
    run: MorningSuggestRun | null;
  };

export type MorningDismissKind = 'mail' | 'text' | 'call' | 'promise';

/**
 * MORNING2 - the Morning chat's shapes, mirrored BY HAND from `backend/src/services/morningService.ts` (`MorningChat`,
 * `MorningTodo`) and `backend/src/services/morningBrief.ts` (`ItemRef`, `MorningGroups`). ⛔ Never imported: a frontend
 * import of backend source is green locally and red on Vercel. Change those files and change this.
 */
/** A to-do on the morning list. For a switched-on person, `reply_thread_id` is the newest waiting email from its contact. */
export type MorningTodo = TodoItem & { reply_thread_id?: string | null };
/** mail = thread id, text and call = conversation id, todo = task id, deadline = deadline id, promise = the sent message's id. */
export type MorningRef = { kind: 'mail' | 'text' | 'call' | 'todo' | 'deadline' | 'promise'; id: string };
/**
 * MORNING2-AI Task 10 (design 4.3): a promise he made in his sent mail, mirrored BY HAND from
 * `backend/src/services/morningPromises.ts` (`PromiseItem`; ⛔ never imported). `sentence` is his own words from the message.
 * `due_date` and `show_from` are Pacific YYYY-MM-DD dates; `sent_at` is an ISO instant.
 */
export interface MorningPromise {
  message_id: string; thread_id: string; to: string; subject: string; sentence: string; due_date: string | null;
  sent_at: string; show_from: string; gmail_url: string;
}
export interface MorningGroups {
  needs_you: MorningRef[];
  can_wait: MorningRef[];
  /** Waiting items the lists did not show (past 30 mail, 30 texts, 5 to-dos): they can wait. */
  can_wait_more: number;
  /** Gmail had more threads than were read, so `can_wait_more` is a floor ("6 or more"), never a number. */
  can_wait_is_floor: boolean;
}
/**
 * `on: false` - a person not switched on gets today's page; with `words` when the setting or today's chat could not be
 * read (the page shows that line above the list). `on: true` - today's chat id (null until the first turn makes it), the
 * template opening lines (the first line, then one per section that could not be read), and the groups.
 */
export type MorningChat =
  | { on: false; words?: string }
  | {
    on: true; chat_id: string | null; opening: string[]; groups: MorningGroups;
    /**
     * MORNING2-AI Task 8: the items that share ONE GoHighLevel contact, 2 or more each (email, text, missed call, task) - the
     * page draws each group as one card. Optional: absent = none (an older server).
     */
    contact_groups?: MorningRef[][];
    /** MORNING2-AI Task 10: the promises showing now, each also a `promise` ref in Needs you. Optional: absent = none. */
    promises?: MorningPromise[];
  };
/**
 * The item his tap started a morning turn from (`backend/src/services/morningCardTypes.ts` `MorningItemRef`): an email
 * thread, and the to-do it covers when he tapped the to-do's card. ⛔ It travels from his TAP in the request body, never
 * through the model.
 */
export type MorningItemRef = { kind: 'mail'; id: string; todo_task_id?: string };

/**
 * MORNING2-AI - the AI title and reason on a Morning card, mirrored BY HAND from `backend/src/services/morningCardText.ts`
 * (`CardText`, `CardTextResult`, `CardTextsAnswer`; ⛔ never imported). `sources` are the INPUT lines the reason came from,
 * copied from what the model was given - never words the model wrote. A card that is `plain` (or absent from the answer)
 * keeps phase 1's own line.
 */
export interface CardInputLine { id: string; text: string }
export interface CardText { title: string; reason: string; sources: CardInputLine[] }
export type CardTextResult = { status: 'ready'; text: CardText } | { status: 'plain' };
/** Why the AI is paused today: the $1 day's limit, or a cost that could not be recorded. Absent/null = not paused. */
export type CardTextPausedReason = 'ceiling' | 'unrecorded' | null;
/** `on: false` = he is not switched on (nothing was read). `texts` is keyed by the card key (`cardKeyOf`, useCardText.ts). */
export interface CardTextAnswer {
  on?: boolean;
  texts: Record<string, CardTextResult>;
  paused: boolean;
  paused_reason?: CardTextPausedReason;
  /** R61: the one morning AI switch is off on the server - nothing was read; plain cards and no paused line. */
  ai_off?: boolean;
}

/**
 * MORNING1 5.3 - Reply, mirrored by hand from `backend/src/services/mailDraftService.ts` (⛔ never imported).
 * ⛔⛔ `version` is the draft as Gmail read it back after the LAST Reply or Save; Send must carry it, or nothing goes.
 */
export interface MorningReplyFile { threadId: string; attachmentId: string; name: string; size: number }
export interface MorningReply {
  draftId: string; threadId: string; to: string; subject: string; body: string;
  /** What is attached now - nothing, until he picks. */
  attachments: { name: string }[];
  /** The email's own attachments he can pick; `attachmentId` is the handle Save takes. */
  available: MorningReplyFile[];
  /** A SUGGESTION from the sender's CRM contact - ⛔ nothing is attached from it. */
  suggestedDealId: string | null;
  suggestedDealName: string | null;
  version: string;
  /** Set when the suggestion could not be read: the screen says to pick the deal. */
  suggestionWords?: string;
}
export type MorningAttachPick = { kind: 'mail'; threadId: string; attachmentId: string } | { kind: 'file'; fileId: string };
/**
 * MORNING2 (P9): `cc` / `bcc` are optional - absent or empty is none (MORNING1's panel sends neither). ⛔ The send card
 * sends both on EVERY save, so a Cc or Bcc is never dropped by one.
 */
export interface MorningReplyEdit { to: string; cc?: string; bcc?: string; subject: string; body: string; attach: MorningAttachPick[] }

/**
 * MORNING2 - the email send card, mirrored BY HAND from `backend/src/services/morningCardTypes.ts` (`SendCard`) and
 * `backend/src/services/morningCardService.ts` (`ListedSendCard`). ⛔ Never imported: a frontend import of backend source is
 * green locally and red on Vercel. Change those files and change this.
 *   - ⛔ `version` is always what the app last showed him; Send carries it, and a draft changed in Gmail since then
 *     (`changed_in_gmail`) is refused until he saves it from Edit.
 *   - A sent or refused card from the server shows no email (to/subject/body are empty): only who, when and the status.
 */
export interface SendCard {
  /** The `morning_drafts` row id: the same card keeps its id through every revise. */
  id: string;
  kind: 'email';
  /** The Gmail draft id - what Edit saves to and Send sends. */
  draft_id: string;
  thread_id: string;
  /** Every recipient, as Gmail holds the draft now (a header each: items split by commas outside quotes and <...>). */
  to: string; cc: string; bcc: string;
  subject: string;
  body: string;
  /** The exact blanks the AI wrote ("[routing number]"); each one still in the text is marked yellow and keeps Send off. */
  blanks: string[];
  /** What is attached now, by file name. */
  attachments: { name: string }[];
  /** The picks saved on the draft: a tick on an offered file saves `[...attach, pick]`, never only the new pick. */
  attach: MorningAttachPick[];
  /** Files the chat offered by name - unticked boxes; nothing is attached until he ticks one. */
  offered: Array<{ pick: MorningAttachPick; name: string }>;
  version: string;
  changed_in_gmail: boolean;
  status: 'open' | 'sent' | 'refused';
  /** Every recipient it went to, joined (To, Cc, Bcc). */
  sent_to: string | null;
  /** An instant. */
  sent_at: string | null;
  /** The thread in HIS mailbox's Gmail. */
  gmail_url: string;
  /** The to-do a send ticks off (the reply was started from its row), or null. */
  todo: { task_id: string; title: string } | null;
}
/**
 * A card as a reload lists it. `unreadable` - the plain words ("Couldn't read the draft in Gmail - try again." or "That
 * draft is gone from Gmail.") - when its Gmail draft could not be read: ⛔ the card then shows no email and offers no Send.
 */
export type ListedSendCard = SendCard & { unreadable?: string };
/** A to-do a send covers, as the card names it. */
export interface MorningTickTodo { task_id: string; title: string }
/** The send's answer (mailDraftService `SentReply`, mirrored by hand). */
export interface MorningSent {
  ok: true;
  /** Every recipient, Cc and Bcc included - the screen says exactly who it went to. */
  sentTo: string;
  /** MORNING2: the to-do the draft was started from, ticked off - null when none was carried, or the tick failed. */
  ticked: MorningTickTodo | null;
  /** Set only when the tick failed: the mail went, the to-do is still open. */
  tick_words?: string;
  /** When no to-do was carried: up to 3 open to-dos for a To recipient, one tap each. Never ticked by the send. */
  offer_tick: MorningTickTodo[];
}

const todoPath = (id: string, action = '') => `/todos/${encodeURIComponent(id)}${action}`;

export const api = {
  // ── TODO1: his to-dos (his GoHighLevel tasks and the unassigned ones), the first sweep and the reminder opt-in ──
  // ⭐ Every write is his tap. Done, Drop and Undo reach GoHighLevel; Later, a note and Still open are the app's own.
  todos: {
    list: () => request<{ success: boolean } & MyTodos>('/todos'),
    /** The task's own words and the call summary - read from GoHighLevel, so it can fail (502) and say so. */
    detail: (id: string) => request<{ success: boolean } & TodoDetail>(todoPath(id)),
    done: (id: string) =>
      request<{ success: boolean; ok: true; how: 'done'; title: string }>(todoPath(id, '/done'), { method: 'POST' }),
    drop: (id: string) =>
      request<{ success: boolean; ok: true; how: 'dropped'; title: string }>(todoPath(id, '/drop'), { method: 'POST' }),
    reopen: (id: string) =>
      request<{ success: boolean; ok: true; title: string }>(todoPath(id, '/reopen'), { method: 'POST' }),
    later: (id: string, when: LaterWhen) =>
      request<{ success: boolean; ok: true; until: string; title: string }>(todoPath(id, '/later'), {
        method: 'POST',
        body: JSON.stringify({ when }),
      }),
    note: (id: string, note: string) =>
      request<{ success: boolean; ok: true; title: string }>(todoPath(id, '/note'), {
        method: 'POST',
        body: JSON.stringify({ note }),
      }),
    sweep: (id: string, decision: TodoSweepDecision) =>
      request<{ success: boolean; ok: true; finished: boolean }>(todoPath(id, '/sweep'), {
        method: 'POST',
        body: JSON.stringify({ decision }),
      }),
    startSweep: () => request<{ success: boolean; ok: true }>('/todos/sweep/start', { method: 'POST' }),
    /** ⭐ R17: "Start reminders" - ends his sweep early. */
    finishSweep: () => request<{ success: boolean; ok: true }>('/todos/sweep/finish', { method: 'POST' }),
    /** null while the app's VAPID keys are unset - reminders are not switched on yet. */
    pushKey: () => request<{ success: boolean; publicKey: string | null }>('/todos/push/key'),
    subscribe: (subscription: PushSubscriptionJSON, device: string) =>
      request<{ success: boolean; ok: true }>('/todos/push/subscribe', {
        method: 'POST',
        body: JSON.stringify({ subscription, device }),
      }),
    unsubscribe: (endpoint: string) =>
      request<{ success: boolean; ok: true }>('/todos/push/subscribe', {
        method: 'DELETE',
        body: JSON.stringify({ endpoint }),
      }),
  },

  // ── LEADS-PAGE: the living leads list. ⛔ READ-ONLY: there is nothing to send, tag or close here. ──
  leads: {
    get: () => request<{ success: boolean } & LeadsBoard>('/leads'),
  },

  // ── CAMP1: the campaign tracker. ⛔ Nothing here writes to GoHighLevel or Gmail; tracking a campaign saves a row here. ──
  campaignTracker: {
    list: () => request<{ success: boolean; campaigns: Array<TrackedCampaign & { results: TrackerResults }> }>('/campaign-tracker'),
    ghlCampaigns: () => request<{ success: boolean; campaigns: GhlEmailCampaign[] }>('/campaign-tracker/ghl-campaigns'),
    create: (body: { name: string; ghl_tag: string; ghl_schedule_id: string | null; kind: 'email' | 'portal' }) =>
      request<{ success: boolean; campaign: TrackedCampaign }>('/campaign-tracker', { method: 'POST', body: JSON.stringify(body) }),
    get: (id: string) => request<{ success: boolean } & TrackerBoardAnswer>(`/campaign-tracker/${encodeURIComponent(id)}`),
    sync: (id: string) => request<{ success: boolean; started: boolean }>(`/campaign-tracker/${encodeURIComponent(id)}/sync`, { method: 'POST' }),
  },

  // ── MORNING1: the Morning page. Loading only READS; "Not needed" is his tap and hides the item for him only. ──
  morning: {
    get: () => request<{ success: boolean } & MyMorning>('/morning'),
    dismiss: (kind: MorningDismissKind, itemId: string) =>
      request<{ success: boolean; ok: true }>('/morning/dismiss', {
        method: 'POST',
        body: JSON.stringify({ kind, item_id: itemId }),
      }),
    /** Reply (5.3): a Gmail draft in the thread, written for him, attached to nothing. ⛔ Never sends. */
    reply: (threadId: string) =>
      request<{ success: boolean } & MorningReply>(`/morning/mail/${encodeURIComponent(threadId)}/reply`, { method: 'POST' }),
    /** His edit replaces the draft whole; answers the new `version`. ⛔ Saving never sends. */
    saveDraft: (draftId: string, edit: MorningReplyEdit) =>
      request<{ success: boolean; ok: true; attachments: { name: string }[]; version: string }>(
        `/morning/mail/drafts/${encodeURIComponent(draftId)}`, { method: 'PUT', body: JSON.stringify(edit) }),
    /**
     * ⛔⛔ The ONLY call that sends mail - made only from the "Send to ...?" dialog's Send, with the version he was shown.
     * `sentTo` is every recipient (To, Cc, Bcc), joined. MORNING2: the answer also says which to-do the send ticked off,
     * or which ones to offer.
     */
    sendDraft: (draftId: string, version: string) =>
      request<{ success: boolean } & MorningSent>(`/morning/mail/drafts/${encodeURIComponent(draftId)}/send`, {
        method: 'POST',
        body: JSON.stringify({ confirm: true, version }),
      }),
    /** MORNING2 - Don't send: the draft is marked refused. ⛔ Sends nothing; the Gmail draft stays in Drafts. */
    refuseDraft: (draftId: string) =>
      request<{ success: boolean }>(`/morning/mail/drafts/${encodeURIComponent(draftId)}/refuse`, { method: 'POST' }),
    /**
     * MORNING2-AI - the AI title and reason for these cards (each card is the items it stands for, one `{ kind, id }` per
     * item; at most 40 cards a call). ⛔ A READ of his own list: it creates no record of his, and the AI spend it causes is
     * recorded in the router's ledger. Only `{ kind, id }` is sent - the server reads everything else from HIS list.
     */
    /** MORNING2-AI Task 14: his suggestions. A READ; Add makes a GoHighLevel task for whoever taps, Not needed hides it. */
    suggestions: () => request<{ success: boolean } & MySuggestions>('/morning/suggestions'),
    addSuggestion: (id: string) =>
      request<{ success: boolean; added?: true; ghl_task_id?: string; on?: false }>(`/morning/suggestions/${encodeURIComponent(id)}/add`, { method: 'POST' }),
    dismissSuggestion: (id: string) =>
      request<{ success: boolean; dismissed?: true; on?: false }>(`/morning/suggestions/${encodeURIComponent(id)}/dismiss`, { method: 'POST' }),
    cardText: (cards: MorningRef[][]) =>
      request<{ success: boolean } & CardTextAnswer>('/morning/card-text', {
        method: 'POST',
        body: JSON.stringify({ cards: cards.map((card) => card.map(({ kind, id }) => ({ kind, id }))) }),
      }),
  },

  // Auth
  // Signup is invite-only (org tenancy Phase 4). The token carries the org and the role.
  signup: (email: string, password: string, fullName: string, inviteToken: string) =>
    request('/auth/signup', {
      method: 'POST',
      body: JSON.stringify({ email, password, full_name: fullName, invite_token: inviteToken }),
    }),

  // Organization membership. Everything except verifyInvite is admin-only server-side.
  verifyInvite: (token: string) =>
    request<{ invite: { org_name: string; email: string; role: string } }>(
      `/org/invites/verify/${encodeURIComponent(token)}`
    ),

  // ── Deal Radar dashboard (design 2026-09-20) ──
  // ⛔ Through `api.*`, never a hand-rolled fetch: this is what gets 401-refresh-and-replay.
  //   DataHub hand-rolls it in three places and gets no token refresh - do not copy that.
  getRadarSignals: (params: { lane?: string; band?: string; city?: string; readiness?: string } = {}) => {
    const q = new URLSearchParams(
      Object.entries(params).filter(([, v]) => v) as [string, string][]
    ).toString();
    return request<RadarSignalsResponse>(`/agents/deal-radar/signals${q ? `?${q}` : ''}`);
  },

  /**
   * The answer to a landlord research lead: a named human at the landlord entity.
   * ⛔ It WRITES - into the app always, and into GHL when there is a phone or an email. Part 4's
   *   AI fills the form; a person presses save.
   */
  saveRadarLandlordContact: (body: {
    entity_id: string; name: string; title?: string; phone?: string; email?: string; source_url?: string;
  }) =>
    request<{
      contact: { name: string; title: string | null; phone: string | null; email: string | null; sourceUrl: string | null };
      ghlContactId: string | null; ghlUrl: string | null; noteWritten: boolean; ghlError?: string;
    }>('/agents/deal-radar/landlord-contact', { method: 'POST', body: JSON.stringify(body) }),

  /**
   * ⛔ READ-ONLY. It PROPOSES a leasing contact; it writes nothing anywhere. A person reads the
   *   citation and presses save, which is `saveRadarLandlordContact` above.
   * ⛔ `ownDomain` is a fact about the citation URL, never a confidence score.
   */
  researchRadarLandlord: (entityId: string) =>
    request<{
      result: {
        status: 'proposed' | 'company_only' | 'nothing' | 'rejected_no_search';
        proposal: {
          name: string; title: string | null; phone: string | null; email: string | null;
          sourceUrl: string; ownDomain: boolean;
        } | null;
        searchCount: number;
        reason?: string;
      };
    }>('/agents/deal-radar/landlord-research', {
      method: 'POST', body: JSON.stringify({ entity_id: entityId }),
    }),

  /**
   * ⛔ READ-ONLY, like the single lookup, and CAPPED server-side at 25 - web search is metered and
   *   a sweep of all 497 landlord leads is a real bill.
   */
  researchRadarLandlordBatch: (entityIds: string[]) =>
    request<{
      results: Array<{
        entity_id: string;
        status: 'proposed' | 'company_only' | 'nothing' | 'rejected_no_search';
        proposal: {
          name: string; title: string | null; phone: string | null; email: string | null;
          sourceUrl: string; ownDomain: boolean;
        } | null;
        searchCount: number;
        reason?: string;
      }>;
      counts: {
        proposed: number; ownDomain: number; needsReview: number;
        companyOnly: number; nothing: number; rejectedNoSearch: number;
      };
    }>('/agents/deal-radar/landlord-research/batch', {
      method: 'POST', body: JSON.stringify({ entity_ids: entityIds }),
    }),

  /**
   * ⛔ METERED and READ-ONLY: it spends against the CEQA 30-day cap (a 409 names the cap in words) and
   *   PROPOSES a developer contact that pre-fills the capture form. A person presses save.
   */
  researchRadarDeveloper: (entityId: string) =>
    request<{ result: RadarDeveloperResearch }>('/agents/deal-radar/developer-research', {
      method: 'POST', body: JSON.stringify({ entity_id: entityId }),
    }),

  /**
   * A developer contact a person saved. ⛔ It WRITES - into the app always, into GHL when there is a phone or
   *   an email - and it clears a research hand-off on the project, never a "not a fit".
   */
  saveRadarDeveloperContact: (body: {
    entity_id: string; name: string; title?: string; company?: string; phone?: string; email?: string; source_url?: string;
  }) =>
    request<{
      contact: {
        name: string; title: string | null; company: string | null; phone: string | null;
        email: string | null; sourceUrl: string | null;
      };
      ghlContactId: string | null; ghlUrl: string | null; noteWritten: boolean; ghlError?: string;
    }>('/agents/deal-radar/developer-contact', { method: 'POST', body: JSON.stringify(body) }),

  // The CEQA reader's controls (plan Task 16). Reading is for anyone signed in; the rest is admin-only
  // server-side, so hiding the controls from others is courtesy, not the enforcement.
  getCeqaStatus: () => request<CeqaStatusResponse>('/dev-intel/ceqa/status'),

  updateCeqaBudget: (patch: CeqaBudgetPatch) =>
    request<{ budget: CeqaBudget }>('/dev-intel/ceqa/budget', { method: 'PATCH', body: JSON.stringify(patch) }),

  /**
   * ⛔ SPENDS: it lands and screens 12 months for free, then reads month one and stops at the checkpoint - or,
   *   with the checkpoint off, reads on until the history load cap pauses it.
   */
  startCeqaLoad: () => request<{ runId: string }>('/dev-intel/ceqa/load', { method: 'POST' }),

  /** ⛔ SPENDS. Past the checkpoint, this press IS his yes to the other 11 months (spec 7.3). */
  continueCeqaLoad: (runId: string) =>
    request<{ success: boolean }>(`/dev-intel/ceqa/load/${encodeURIComponent(runId)}/continue`, { method: 'POST' }),

  markRadarSignal: (body: { signal_type: string; entity_id: string; outcome: string; note?: string }) =>
    request<{ mark: { id: string }; ghlTagWritten: boolean; ghlError?: string }>(
      '/agents/deal-radar/marks',
      { method: 'POST', body: JSON.stringify(body) }
    ),

  // ⛔ Returns whether GHL actually removed the tag. A silent half-undo is worse than a loud one.
  undoRadarMark: (id: string) =>
    request<{ ghlTagRemoved: boolean; ghlError?: string }>(`/agents/deal-radar/marks/${id}`, {
      method: 'DELETE',
    }),

  // Ensures the GHL contact and hands back its url so the dial happens THERE. ⛔ Not a tel: link.
  callRadarSignal: (signalType: string, entityId: string) =>
    request<{ ghlContactId: string; ghlUrl: string | null; phone: string }>(
      `/agents/deal-radar/call/${signalType}/${encodeURIComponent(entityId)}`,
      { method: 'POST' }
    ),

  getRadarActivity: (limit = 100) =>
    request<{ activity: RadarActivityRow[] }>(`/agents/deal-radar/activity?limit=${limit}`),

  getMembers: () =>
    request<{ members: Array<{ id: string; email: string; full_name: string | null; role: string; is_active: boolean; created_at: string }> }>(
      '/org/members'
    ),

  updateMember: (id: string, patch: { role?: string; is_active?: boolean }) =>
    request(`/org/members/${id}`, { method: 'PATCH', body: JSON.stringify(patch) }),

  getInvites: () =>
    request<{ invites: Array<{ id: string; email: string; role: string; status: string; expires_at: string; created_at: string }> }>(
      '/org/invites'
    ),

  createInvite: (email: string, role: string) =>
    request<{ invite: { id: string; email: string; role: string; expires_at: string }; token: string }>(
      '/org/invites',
      { method: 'POST', body: JSON.stringify({ email, role }) }
    ),

  revokeInvite: (id: string) => request(`/org/invites/${id}`, { method: 'DELETE' }),

  login: (email: string, password: string) =>
    request<{ token: string; user: any }>('/auth/login', {
      method: 'POST',
      body: JSON.stringify({ email, password }),
    }),

  getCurrentUser: () => request<{ user: any }>('/auth/me'),

  // Documents - Upload directly to Supabase Storage (bypasses Railway proxy size limits)
  uploadDocument: async (file: File, propertyId?: string, documentType?: string) => {
    const token = localStorage.getItem('token');

    if (!token) {
      window.location.href = '/login';
      throw new APIError(401, 'Please log in to upload documents.');
    }

    console.log('[API] uploadDocument:', file.name, 'size:', file.size);

    // Read file into buffer first
    const fileBuffer = await file.arrayBuffer();
    if (fileBuffer.byteLength === 0) {
      throw new APIError(400, 'File appears to be empty (0 bytes). If the file is stored in cloud storage, use the "From URL" tab instead.');
    }

    // Step 1: Get signed upload URL from backend
    const uploadUrlResponse = await fetch(`${API_BASE}/documents/upload-url`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        Authorization: `Bearer ${token}`,
      },
      body: JSON.stringify({ file_name: file.name, file_size: file.size }),
    });

    const uploadUrlData = await uploadUrlResponse.json();
    if (!uploadUrlResponse.ok) {
      throw new APIError(uploadUrlResponse.status, uploadUrlData.error || 'Failed to prepare upload');
    }

    // Step 2: Upload directly to Supabase Storage (bypasses Railway)
    const storageResponse = await fetch(uploadUrlData.upload_url, {
      method: 'PUT',
      headers: { 'Content-Type': 'application/pdf' },
      body: fileBuffer,
    });

    if (!storageResponse.ok) {
      throw new APIError(storageResponse.status, 'Failed to upload file to storage');
    }

    // Step 3: Create document record in backend
    const createResponse = await fetch(`${API_BASE}/documents/create`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        Authorization: `Bearer ${token}`,
      },
      body: JSON.stringify({
        storage_path: uploadUrlData.storage_path,
        file_name: file.name,
        file_size: file.size,
        property_id: propertyId,
        document_type: documentType,
      }),
    });

    const createData = await createResponse.json();
    if (!createResponse.ok) {
      throw new APIError(createResponse.status, createData.error || 'Failed to create document record');
    }

    console.log('[API] Document uploaded successfully:', createData.document?.id);
    return createData;
  },

  uploadDocumentFromUrl: async (
    url: string,
    fileName?: string,
    propertyId?: string,
    documentType?: string
  ) => {
    return request('/documents/upload-from-url', {
      method: 'POST',
      body: JSON.stringify({
        url,
        file_name: fileName,
        property_id: propertyId,
        document_type: documentType,
      }),
    });
  },

  // Upload with pre-read buffer (prevents mobile browser File blob expiration)
  uploadDocumentWithBuffer: async (
    fileData: { name: string; size: number; type: string; buffer: ArrayBuffer },
    propertyId?: string,
    documentType?: string
  ) => {
    const token = localStorage.getItem('token');

    console.log('[API] uploadDocumentWithBuffer called, token exists:', !!token);
    console.log('[API] Buffer size:', fileData.buffer.byteLength);

    if (!token) {
      console.error('[API] No token found in localStorage');
      window.location.href = '/login';
      throw new APIError(401, 'Please log in to upload documents.');
    }

    // Step 1: Get signed upload URL from backend
    console.log('[API] Getting signed upload URL...');
    let uploadUrlResponse;
    try {
      uploadUrlResponse = await fetch(`${API_BASE}/documents/upload-url`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify({
          file_name: fileData.name,
          file_size: fileData.size,
        }),
      });
    } catch (networkError) {
      console.error('[API] Network error getting upload URL:', networkError);
      throw new APIError(0, 'Network error. Please check your internet connection and try again.');
    }

    const uploadUrlData = await uploadUrlResponse.json();
    if (!uploadUrlResponse.ok) {
      console.error('[API] Failed to get upload URL:', uploadUrlData);
      throw new APIError(uploadUrlResponse.status, uploadUrlData.error || 'Failed to prepare upload');
    }

    console.log('[API] Got signed URL, uploading buffer directly to Supabase Storage...');
    console.log('[API] Buffer byteLength:', fileData.buffer.byteLength);

    // Step 2: Upload buffer directly to Supabase Storage
    let storageResponse;
    try {
      storageResponse = await fetch(uploadUrlData.upload_url, {
        method: 'PUT',
        headers: {
          'Content-Type': fileData.type || 'application/pdf',
        },
        body: fileData.buffer,
      });
    } catch (networkError) {
      console.error('[API] Network error uploading to storage:', networkError);
      throw new APIError(0, 'Failed to upload file. Please try again.');
    }

    console.log('[API] Storage upload response:', storageResponse.status, storageResponse.statusText);

    if (!storageResponse.ok) {
      const errorText = await storageResponse.text();
      console.error('[API] Storage upload failed:', storageResponse.status, errorText);
      throw new APIError(storageResponse.status, 'Failed to upload file to storage');
    }

    console.log('[API] File uploaded to storage, creating document record...');
    console.log('[API] Sending file_size:', fileData.size);

    // Step 3: Create document record in backend
    let createResponse;
    try {
      createResponse = await fetch(`${API_BASE}/documents/create`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify({
          storage_path: uploadUrlData.storage_path,
          file_name: fileData.name,
          file_size: fileData.size,
          property_id: propertyId,
          document_type: documentType,
        }),
      });
    } catch (networkError) {
      console.error('[API] Network error creating document:', networkError);
      throw new APIError(0, 'File uploaded but failed to create record. Please try again.');
    }

    const createData = await createResponse.json();
    if (!createResponse.ok) {
      console.error('[API] Failed to create document:', createData);
      throw new APIError(createResponse.status, createData.error || 'Failed to create document record');
    }

    console.log('[API] Document created successfully:', createData.document?.id);
    return createData;
  },

  listDocuments: (params?: Record<string, string>) => {
    const query = params ? '?' + new URLSearchParams(params).toString() : '';
    return request(`/documents${query}`);
  },

  getDocument: (id: string) => request(`/documents/${id}`),

  extractDocument: (id: string) =>
    request(`/documents/${id}/extract`, { method: 'POST' }),

  importAssetsFromDocument: (documentId: string) =>
    request(`/documents/${documentId}/import-assets`, { method: 'POST' }),

  // Properties
  //
  // A5: all three creates below answer 409 `possible_duplicate` (with `candidates`) instead of
  // inserting when the address or APN already exists. `force_create` overrides that check and
  // is therefore NEVER defaulted here - it is threaded through as an explicit argument so the
  // only thing that can set it is the user picking "Create separate" in the dialog. Note that
  // the check can also be BROKEN, which comes back as 503 `duplicate_check_unavailable`; that
  // is an ordinary error and must never be answered with force_create.
  createProperty: (data: Record<string, unknown>) =>
    request('/properties', {
      method: 'POST',
      body: JSON.stringify(data),
    }),

  createPropertyFromDocument: (
    documentId: string,
    overrides?: any,
    forceCreate?: boolean
  ) =>
    request(`/properties/from-document/${documentId}`, {
      method: 'POST',
      // Spread, not `force_create: forceCreate`: the backend schema is `.strict()` and the
      // controller tests `=== true`, so omitting the key entirely is cleaner than sending
      // `undefined`/`false`.
      //
      // `=== true`, not truthiness, and that is NOT redundant with the backend's own `=== true`
      // check. Normalising any truthy value here to a literal `true` would launder a bad caller
      // - a `'false'` string, a `1`, a stray object - into the exact value the backend accepts,
      // so its deliberate defence-in-depth check could never catch anything. Two independent
      // strict checks is the point: the client refuses to SEND it and the server refuses to
      // HONOUR it, and a duplicate row stays something the user asked for in as many words.
      body: JSON.stringify({ overrides, ...(forceCreate === true ? { force_create: true } : {}) }),
    }),

  createPropertyFromMaster: (masterPropertyId: string, forceCreate?: boolean) =>
    request(`/properties/from-master/${masterPropertyId}`, {
      method: 'POST',
      body: JSON.stringify(forceCreate === true ? { force_create: true } : {}),
    }),

  /**
   * The other half of the duplicate prompt: re-home an already-uploaded document onto the
   * property the user says it belongs to. Without this, answering "attach to the one I already
   * have" would leave the uploaded file pointing at no property at all.
   */
  attachDocumentToProperty: (propertyId: string, documentId: string) =>
    request(`/properties/${propertyId}/attach-document/${documentId}`, { method: 'POST' }),

  listProperties: (params?: Record<string, string>) => {
    const query = params ? '?' + new URLSearchParams(params).toString() : '';
    return request(`/properties${query}`);
  },

  getProperty: (id: string) => request(`/properties/${id}`),

  getPropertyTransactions: (propertyId: string) =>
    request(`/properties/${propertyId}/transactions`),

  updateProperty: (id: string, updates: any) =>
    request<{ success: boolean; property?: unknown; record?: DealRecordOutcome | null }>(`/properties/${id}`, {
      method: 'PATCH',
      body: JSON.stringify(updates),
    }),

  /** DOCTRACK part 3 (D13): the building's record's numbers beside the deal page's own. */
  getDealRecordNumbers: (rowId: string) =>
    request<{ success: boolean; link: DealRecordLinkView; numbers: RecordNumber[] }>(`/properties/${rowId}/record-numbers`),

  /** DOCTRACK part 3 (D13): "Try again" - these fields, from the deal page as it stands, onto the building's record. */
  recordDealNumbers: (rowId: string, fields: DealNumberField[], note?: string | null) =>
    request<{ success: boolean; record: DealRecordOutcome }>(`/properties/${rowId}/record-numbers`, {
      method: 'POST',
      body: JSON.stringify(note && note.trim() !== '' ? { fields, record_note: note.trim() } : { fields }),
    }),

  deleteProperty: (id: string) =>
    request(`/properties/${id}`, { method: 'DELETE' }),

  // Comps
  getComps: (propertyId: string) =>
    request(`/properties/${propertyId}/comps`),

  /**
   * Market comps backing an exit cap suggestion, for properties whose linked comps
   * carry no cap rate. Rows come back in the same shape buildExitCapEvidence takes.
   */
  getExitCapComps: (propertyId: string) =>
    request<{
      success: boolean;
      capRate: number | null;
      sampleSize: number;
      radiusMiles: number | null;
      windowMonths: number | null;
      supporting: Array<{
        cap_rate: number | null;
        price_per_sf: number | null;
        transaction_date: string | null;
        distance_miles: number | null;
        property: { address: string | null; city: string | null } | null;
      }>;
      reason?: string;
    }>(`/data-pool/exit-cap-comps/${propertyId}`),

  addComp: (propertyId: string, compData: any) =>
    request(`/properties/${propertyId}/comps`, {
      method: 'POST',
      body: JSON.stringify(compData),
    }),

  updateComp: (compId: string, updates: any) =>
    request(`/comps/${compId}`, {
      method: 'PATCH',
      body: JSON.stringify(updates),
    }),

  deleteComp: (compId: string) =>
    request(`/comps/${compId}`, { method: 'DELETE' }),

  // Profile / Branding
  updateProfile: (data: { company_name?: string; company_phone?: string; company_email?: string; company_address?: string }) =>
    request('/auth/profile', {
      method: 'PATCH',
      body: JSON.stringify(data),
    }),

  getLogoUploadUrl: (fileName: string, fileSize: number) =>
    request<{ upload_url: string; storage_path: string }>('/auth/profile/logo-upload-url', {
      method: 'POST',
      body: JSON.stringify({ file_name: fileName, file_size: fileSize }),
    }),

  updateProfileLogo: (storagePath: string) =>
    request<{ company_logo_url: string }>('/auth/profile/logo', {
      method: 'PATCH',
      body: JSON.stringify({ storage_path: storagePath }),
    }),

  // Valuation Analysis
  getValuation: (propertyId: string) =>
    request(`/properties/${propertyId}/valuation`),

  analyzeProperty: (propertyId: string) =>
    request(`/properties/${propertyId}/analyze`, { method: 'POST' }),

  // LOI Generation
  /**
   * Generate a letter. With `dealId` the backend FILES it against that deal (it appears in the
   * Deal Room documents list); without one it is generated and handed back to download.
   */
  /**
   * ⭐ The 100th: a recordless letter can be FILED on a property - a record he picked, or a new one
   *   from the facts he was shown. ⛔ Never together with a deal; the server refuses that by name.
   */
  /**
   * ⭐ The LOI override: `skipHouseStyle` names the house-style rules he set aside for this letter ("standard
   *   wording this time") - the same list the preview skipped, so the filed letter is the one he looked at.
   */
  generateLoi: (
    loi: import('../types').LoiGenerateInput,
    dealId?: string,
    filing?: import('../types').LoiFilingRequest,
    skipHouseStyle?: string[],
  ) =>
    request<{ success: boolean; loi: {
      loi_text: string; loi_html: string; docx_base64: string; filename: string;
      title: string; link_id: string | null; filed: boolean;
      filing?: {
        filed_on: string; target_id: string; created_property: boolean;
        already_filed: boolean; changed_from_plan: string | null;
      } | null;
      filing_refused?: string | null;
      changes_recorded?: boolean | null;
    } }>('/loi/generate', {
      method: 'POST',
      body: JSON.stringify({
        loi,
        deal_id: dealId ?? null,
        skip_house_style: skipHouseStyle ?? [],
        ...(filing?.fileToPropertyId ? { file_to_property_id: filing.fileToPropertyId } : {}),
        ...(filing?.createProperty
          ? {
              create_property: {
                address: filing.createProperty.address,
                city: filing.createProperty.city,
                state: filing.createProperty.state,
                zip: filing.createProperty.zip,
                building_sf: filing.createProperty.buildingSf,
                lot_size_acres: filing.createProperty.lotSizeAcres,
              },
            }
          : {}),
      }),
    }),

  /** ⛔ READ-ONLY: where a recordless letter WOULD be filed. Writes nothing. */
  loiFilingPlan: (facts: {
    address: string; city: string; state?: string | null; zip?: string | null;
    buildingSf?: number | null; lotSizeAcres?: number | null;
  }) =>
    request<{ success: boolean; plan: import('../types').LoiFilingPlan }>('/loi/filing-plan', {
      method: 'POST',
      body: JSON.stringify({
        address: facts.address, city: facts.city, state: facts.state ?? null, zip: facts.zip ?? null,
        building_sf: facts.buildingSf ?? null, lot_size_acres: facts.lotSizeAcres ?? null,
      }),
    }),

  /**
   * ⛔ READ-ONLY: an OM's descriptive facts, from a PDF attached in the dialog (bytes, never stored)
   *   or one he attached in the assistant (read back from storage, his org only).
   */
  loiOmFacts: (
    source:
      | { document: { filename: string; base64: string } }
      | { attachment: { storage_path: string; file_name: string } },
  ) =>
    request<{ success: boolean; read: import('../types').LoiOmRead }>('/loi/om-facts', {
      method: 'POST',
      body: JSON.stringify(source),
    }),

  /**
   * The LOI override ("say it my way"). ⛔ `previewLoi` files NOTHING. `draftLoiChange` spends one AI call - only
   *   when he asks - and reads the SAME letter as the preview: his house style applied, `skipHouseStyle` skipped
   *   (Ruling 13). The what-ifs live under the deal; only `promoteDealScenario` changes the deal's price.
   */
  previewLoi: (loi: import('../types').LoiGenerateInput, skipHouseStyle: string[] = []) =>
    request<{ success: boolean; preview: import('../types/loiOverride').LoiPreview }>('/loi/preview', {
      method: 'POST',
      body: JSON.stringify({ loi, skip_house_style: skipHouseStyle }),
    }),
  draftLoiChange: (
    loi: import('../types').LoiGenerateInput,
    instruction: string,
    target: import('../types/loiOverride').LoiSectionKey | null,
    skipHouseStyle: string[] = [],
  ) =>
    request<{ success: boolean; draft: import('../types/loiOverride').LoiDraft }>('/loi/overrides/draft', {
      method: 'POST',
      body: JSON.stringify({ loi, instruction, target, skip_house_style: skipHouseStyle }),
    }),
  getLoiHouseStyle: (era: 'september_2026' | 'june_2026') =>
    request<{ success: boolean; rules: import('../types/loiOverride').HouseStyleRule[] }>(`/loi/house-style?era=${era}`),
  saveLoiHouseStyle: (
    loi: import('../types').LoiGenerateInput,
    // D9: the backend's own types - a key, an action and a new section's key as the server reads them.
    rule: {
      section: import('../types/loiOverride').LoiSectionKey;
      action: import('../types/loiOverride').OverrideAction;
      title?: string;
      paragraphs?: string[];
      newKey?: `custom_${number}`;
      asked: string;
    },
  ) =>
    request<{ success: boolean; rule: import('../types/loiOverride').HouseStyleRule }>('/loi/house-style', {
      method: 'POST',
      body: JSON.stringify({ loi, rule }),
    }),
  removeLoiHouseStyle: (id: string) =>
    request<{ success: boolean }>(`/loi/house-style/${encodeURIComponent(id)}`, { method: 'DELETE' }),
  getDealScenarios: (dealId: string) =>
    request<{ success: boolean; scenarios: import('../types/loiOverride').DealScenario[] }>(`/crm-deals/${dealId}/scenarios`),
  createPriceAtCapScenario: (dealId: string, targetCapPct: number) =>
    request<{ success: boolean; scenario: import('../types/loiOverride').DealScenario }>(`/crm-deals/${dealId}/scenarios`, {
      method: 'POST',
      body: JSON.stringify({ target_cap_pct: targetCapPct }),
    }),
  promoteDealScenario: (dealId: string, scenarioId: string) =>
    request<{ success: boolean; previousOfferPrice: number | null; offerPrice: number }>(
      `/crm-deals/${dealId}/scenarios/${scenarioId}/promote`,
      { method: 'POST' },
    ),

  /**
   * LP1.2 - the lease proposal. ⛔ NOT the listing proposal below: this is the tenant-side
   * Proposal to Lease. It writes NOTHING - filing is LP1.5, and `deal_id` is refused by name
   * rather than accepted and ignored.
   */
  /**
   * LP1.5 - `fileOnPropertyId` FILES the letter on that property's library. Omitted, it downloads.
   * ⛔ Asked for, never assumed: the dialog always sits on a property, so filing on the mere
   * presence of an id would make every Generate write a document nobody chose to write.
   */
  generateLeaseProposal: (
    proposal: import('../types').LeaseProposalGenerateInput,
    fileOnPropertyId?: string | null,
  ) =>
    request<{ success: boolean; proposal: {
      proposal_text: string; docx_base64: string; filename: string; title: string;
      flags: { field: string; note: string }[];
      sections: { number: number; title: string }[];
      filed: boolean;
      link_id: string | null;
      filed_on: string | null;
      already_filed: boolean;
    } }>('/lease-proposal/generate', {
      method: 'POST',
      body: JSON.stringify(
        fileOnPropertyId ? { proposal, file_on_property_id: fileOnPropertyId } : { proposal },
      ),
    }),

  /**
   * LP1.3 - read the documents he attached and the terms he typed, and hand back a PREFILL.
   *
   * ⛔⛔ THE BYTES TRAVEL IN THE BODY AND ARE NEVER STORED. Reading by a `file_id` would mean
   * FILING the upload first, and this lane writes nothing - `wrote: false` comes back saying so.
   */
  readLeaseProposalDocuments: (input: {
    documents: { filename: string; base64: string }[];
    notes: string;
  }) =>
    request<{ success: boolean; read: import('../types').LeaseProposalReadResult }>(
      '/lease-proposal/read',
      { method: 'POST', body: JSON.stringify(input) },
    ),

  /**
   * The listing proposal ("Executive Summary"). With a deal id it is FILED against that deal;
   * without one it is generated and handed back to download.
   */
  generateProposal: (proposal: import('../types').ProposalGenerateInput, dealId?: string) =>
    request<{ success: boolean; proposal: {
      proposal_text: string; proposal_html: string; docx_base64: string; filename: string;
      title: string; link_id: string | null; filed: boolean;
      class_match: { key: string; label: string; exact: boolean; requested: string };
    } }>('/proposals/generate', {
      method: 'POST',
      body: JSON.stringify({ proposal, deal_id: dealId ?? null }),
    }),

  /**
   * The brochure / Offering Memorandum. Which of the two it is is decided by the server, from
   * whether financials were supplied - they are one document, not two.
   *
   * ⚠️ The editor's live preview calls this WITHOUT a deal id on every (debounced) change, so
   * previewing never files. Filing is the explicit save.
   */
  /**
   * File a PDF the browser built (the Investment Analysis, the Valuation Summary) against the
   * property's deal - deal + Deal Room, internal - or the property when it has no deal.
   * ▶ backend dealExportService. His ruling 2026-09-09: every export files automatically.
   */
  fileExport: (input: {
    property_id?: string | null; master_property_id?: string | null; deal_id?: string | null;
    kind: 'Investment Analysis' | 'Valuation Summary' | 'Trade Area Report'; filename: string; pdf_base64: string;
  }) =>
    request<{ success: boolean; filed: {
      file_id: string; link_id: string; where: 'deal_room' | 'property' | 'master_property';
      deal_id: string | null; deal_name: string | null; title: string;
    } }>('/exports/file', { method: 'POST', body: JSON.stringify(input) }),

  generateBrochure: (
    brochure: import('../types').BrochureGenerateInput,
    dealId?: string,
    /** LB4 - present only on the explicit save / file, never on the preview: the INPUT is saved for reloading,
     *  keyed to the deal (above), the analyzer property or the Property Database record named here. */
    save?: { propertyId?: string | null; masterPropertyId?: string | null }
  ) =>
    request<{ success: boolean; brochure: {
      html: string; text: string; filename: string; title: string;
      document_kind: 'Brochure' | 'Offering Memorandum';
      page_count: number; page_size: string; missing: string[];
      link_id: string | null; filed: boolean;
      saved_id: string | null; saved_at: string | null; save_error: string | null;
    } }>('/brochures/generate', {
      method: 'POST',
      body: JSON.stringify({
        brochure, deal_id: dealId ?? null,
        ...(save ? { save: true, property_id: save.propertyId ?? null, master_property_id: save.masterPropertyId ?? null } : {}),
      }),
    }),

  /** LB4 - the builder's saved brochures for ONE surface, newest first. */
  listSavedBrochures: (key: import('../types').SavedBrochureKey) => {
    const p = new URLSearchParams();
    if ('dealId' in key) p.set('deal_id', key.dealId);
    else if ('propertyId' in key) p.set('property_id', key.propertyId);
    else p.set('master_property_id', key.masterPropertyId);
    return request<{ success: boolean; saved: import('../types').SavedBrochureSummary[] }>(`/brochures/saved?${p.toString()}`);
  },

  /** LB4 - one saved brochure with its whole input, for the editor to load. */
  getSavedBrochure: (id: string) =>
    request<{ success: boolean; saved: import('../types').SavedBrochureSummary & {
      input: import('../types').BrochureGenerateInput;
      dealId: string | null; propertyId: string | null; masterPropertyId: string | null; linkId: string | null;
    } }>(`/brochures/saved/${id}`),

  /**
   * Draft the PERSUASION sections from the facts already on the form.
   *
   * ⭐ The whole brochure goes up, not the one card being drafted - his own INVESTMENT
   * HIGHLIGHTS bullet cites "Hwy 79", which is on a different page. The server checks every
   * drafted line back against those facts and returns what it had to drop, and why.
   */
  draftBrochureCopy: (
    brochure: import('../types').BrochureGenerateInput,
    kinds: import('../types').BrochureDraftKind[],
    guidance?: string | null,
    /**
     * ⛔ Build the "WHAT CAN BE BUILT HERE" page from the signed rule book. A SEPARATE argument,
     * not a member of `kinds` - `kinds` is the allow-list of what a MODEL may draft, and a
     * permitted-use list is a fact. Sending it in `kinds` is rejected by the server, correctly.
     */
    zoningPage?: boolean
  ) =>
    request<{ success: boolean;
      drafts: {
        kind: import('../types').BrochureDraftKind;
        title: string;
        body: string;
        blockKind: 'bullets' | 'prose' | 'groups' | 'tagline';
      }[];
      dropped: {
        kind: import('../types').BrochureDraftKind;
        line: string;
        claims: { claim: string; kind: 'figure' | 'name' | 'zoning' }[];
      }[];
      /**
       * ⛔ NOT A DRAFT. The "WHAT CAN BE BUILT HERE" page, assembled server-side from the SIGNED
       * zoning rule book with a citation on every line - no model touches it, because a
       * permitted-use list is a fact. Null when the book has no citable answer for this zone,
       * in which case the request fails with a message naming the zone.
       */
      zoning: {
        city: string;
        code: string;
        /** `section` is the per-line research trail - provenance, NOT printed copy. */
        lines: { use: string; permission: string; section: string }[];
        sourceNote: string;
        withheld: { use: string; reason: string }[];
      } | null;
      /** Why there is no zoning page, when one was asked for and could not be built. */
      zoningRefusal: string | null;
    }>('/brochures/draft', {
      method: 'POST',
      body: JSON.stringify({
        brochure, kinds, guidance: guidance ?? null, zoningPage: zoningPage === true,
      }),
    }),

  /**
   * Preview a filed document in place. ⛔ Same authorization as the download it sits beside -
   * the server resolves both through `resolveDownload`.
   */
  previewDealRoomDocument: (dealId: string, docId: string) =>
    request<{ success: boolean; preview: import('../types').DocumentPreview }>(
      // ⚠️ `/crm-deals`, NOT `/deals` - dealRoomRoutes is mounted at `/api/crm-deals`. The first
      // version of this said `/deals` and 404'd, and no component test could see it because they
      // all mock this module: mocking the callee hides the caller's bug. Pinned in api.test.ts
      // against the download path it sits beside.
      `/crm-deals/${dealId}/room/documents/${docId}/preview`
    ),

  getBrochurePrefill: (propertyId: string) =>
    request<{ success: boolean; prefill: {
      addressLine: string | null; city: string | null; state: string | null; zip: string | null;
      county: string | null; zoning: string | null; apn: string | null; apnSource: string | null;
      coverHeadline: string | null; coverSubhead: string | null;
      suggestedFacts: { label: string; value: string }[];
      photos: {
        hero: { src: string; alt?: string | null } | null;
        sitePlan: { src: string; alt?: string | null } | null;
        extras: { src: string; alt?: string | null }[];
      };
      demographics: {
        block: import('../types').BrochureBlockInput;
        sourceNote: string;
        centre: { lat: number; lng: number };
      } | null;
    } }>(`/brochures/prefill/${propertyId}`),

  /**
   * DOC-READ (the 157th): what the listing proposal form opens with - the record's facts and, where the record is blank,
   * the title report or property profile filed on it. `propertyId` is a Property Database id OR a My Deals id. Never a price.
   */
  getProposalPrefill: (propertyId: string) =>
    request<{ success: boolean; prefill: {
      addressLine: string | null; city: string | null; county: string | null; zoning: string | null;
      ownerName: string | null; salutationName: string | null; ownerAddressLines: string[] | null;
      propertyType: string | null; apn: string | null; yearBuilt: string | null;
      fromDocuments: { title: string; filedAt: string | null; documentType: string | null; fields: string[]; namesReordered: boolean }[];
      recordOwnerName: string | null;
      // DOCTRACK part 3 (rule 9): the boxes the record filled from a title report nobody has checked.
      notChecked?: Array<'ownerName' | 'propertyType' | 'yearBuilt'>;
    } }>(`/proposals/prefill/${propertyId}`),

  /**
   * Read a seller's counter that is already in the document library and get SUGGESTED LOI fields.
   * ⛔ Writes nothing and sends nothing - the letter is still produced by pressing Generate.
   */
  planLoiCounter: (fileId: string, opts?: { priorCounterRounds?: number[]; isAnsweringACounter?: boolean }) =>
    request<{
      success: boolean;
      fileId: string;
      filename: string;
      plan: import('../types').LoiCounterPlan;
      reading: { documentType: string; summary: string; numPages: number; pagesWithText: number };
      rejected: { reason: string; label: string; value: unknown; quote: string; page: number }[];
      possibleHandwriting: boolean;
      /** Handwriting the reader saw and a guard refused (a hand-written date carries no year), by name. */
      handwrittenButUnverified: { reason: string; label: string; value: unknown; quote: string; page: number }[];
      markedUpByHand: boolean;
      printedLayerIsOurOwnLetter: boolean;
    }>('/loi/counter-plan', {
      method: 'POST',
      body: JSON.stringify({
        file_id: fileId,
        prior_counter_rounds: opts?.priorCounterRounds,
        is_answering_a_counter: opts?.isAnsweringACounter,
      }),
    }),

  getDealLoiPrefill: (dealId: string) =>
    request<{ success: boolean; prefill: {
      dealName: string | null;
      terms: { purchasePrice: number | null; emdAmount: number | null };
      // ⭐ The 114th: `acres` and `lotSf` are the record's, for the letter's correction note only - never its boxes.
      // DOCTRACK part 3 (rule 9): `notChecked` names the boxes whose record number came from a title report nobody has checked.
      property: { address: string; city: string; state: string; zip: string | null; buildingSf: number | null; county: string | null; acres?: number | null; lotSf?: number | null; notChecked?: LoiNotCheckedKey[] } | null;
      // DOC3: read off the OM filed on the deal's property. Suggestions, never confirmed facts.
      sellersBrokerage: string | null;
      listingAttn: string | null;
      listingBroker: {
        brokerage: string | null; agentName: string | null; agentPhone: string | null;
        agentEmail: string | null; agentLicense: string | null; source: string;
      } | null;
      brokerSource: string | null;
    } }>(`/crm-deals/${dealId}/loi-prefill`),

  getLOIs: (propertyId: string) =>
    request(`/properties/${propertyId}/lois`),


  // Photos
  getPhotos: (propertyId: string) =>
    request(`/properties/${propertyId}/photos`),

  uploadPhoto: async (
    propertyId: string,
    file: File,
    photoType: string = 'exterior',
    caption?: string,
    isPrimary: boolean = false
  ) => {
    const token = localStorage.getItem('token');
    if (!token) {
      throw new APIError(401, 'Please log in to upload photos.');
    }

    // Step 1: Get signed upload URL
    const uploadUrlResponse = await fetch(`${API_BASE}/properties/${propertyId}/photos/upload-url`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        Authorization: `Bearer ${token}`,
      },
      body: JSON.stringify({
        file_name: file.name,
        file_size: file.size,
      }),
    });

    const uploadUrlData = await uploadUrlResponse.json();
    if (!uploadUrlResponse.ok) {
      throw new APIError(uploadUrlResponse.status, uploadUrlData.error || 'Failed to prepare upload');
    }

    // Step 2: Upload file directly to Supabase Storage
    const fileBuffer = await file.arrayBuffer();
    const storageResponse = await fetch(uploadUrlData.upload_url, {
      method: 'PUT',
      headers: {
        'Content-Type': file.type || 'image/jpeg',
      },
      body: fileBuffer,
    });

    if (!storageResponse.ok) {
      throw new APIError(storageResponse.status, 'Failed to upload photo to storage');
    }

    // Step 3: Create photo record
    const createResponse = await fetch(`${API_BASE}/properties/${propertyId}/photos`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        Authorization: `Bearer ${token}`,
      },
      body: JSON.stringify({
        storage_path: uploadUrlData.storage_path,
        file_name: file.name,
        file_size: file.size,
        photo_type: photoType,
        caption,
        is_primary: isPrimary,
      }),
    });

    const createData = await createResponse.json();
    if (!createResponse.ok) {
      throw new APIError(createResponse.status, createData.error || 'Failed to create photo record');
    }

    return createData;
  },

  updatePhoto: (photoId: string, updates: { photo_type?: string; caption?: string; is_primary?: boolean }) =>
    request(`/photos/${photoId}`, {
      method: 'PATCH',
      body: JSON.stringify(updates),
    }),

  deletePhoto: (photoId: string) =>
    request(`/photos/${photoId}`, { method: 'DELETE' }),

  setPrimaryPhoto: (photoId: string) =>
    request(`/photos/${photoId}/set-primary`, { method: 'POST' }),

  // Deal Analysis
  getDealAnalysis: (propertyId: string) =>
    request(`/properties/${propertyId}/analysis`),

  saveDealAnalysis: (propertyId: string, analysisData: any) =>
    request(`/properties/${propertyId}/analysis`, {
      method: 'PUT',
      body: JSON.stringify(analysisData),
    }),

  /**
   * T5/T6 - the analysis checker. Read-only: compares the saved analysis against its own source
   * document and reports confirmed / disagrees / could-not-check per figure. Returns the bare
   * `AnalysisCheck` (not re-wrapped in `{success, data}`) - `readAnalysisCheck` does the one
   * unwrap. Never blocks the export: `AnalysisCheckPanel` always leaves "Continue to PDF" enabled,
   * and a failed check must not stop a PDF going out - callers catch and export anyway.
   *
   * CRITICAL 1 (final whole-branch review, 2026-09-19): `figures`, when passed, is the SCREEN's
   * own current values - sent so the checker judges what is about to leave the building, not
   * whatever was last saved (autosave is a 2-second debounce; editing a figure and exporting
   * inside that window used to let the panel confidently report on the OLD number). Omitted
   * entirely, the server falls back to the saved row exactly as before this fix - this is what
   * keeps the sweep script and any other caller working unchanged.
   */
  checkAnalysis: async (propertyId: string, figures?: AnalysisFigures): Promise<AnalysisCheck> => {
    const body = await request<unknown>(`/analysis-check/${propertyId}`, {
      method: 'POST',
      ...(figures ? { body: JSON.stringify({ figures }) } : {}),
    });
    return readAnalysisCheck(body);
  },

  // ---- Phase 3C: model versions ----
  listAnalysisVersions: (propertyId: string) =>
    request<{ success: boolean; versions: Array<{
      id: string; label: string; note: string | null;
      metrics: Record<string, unknown> | null; created_at: string;
    }> }>(`/properties/${propertyId}/analysis/versions`),

  getAnalysisVersion: (propertyId: string, versionId: string) =>
    request<{ success: boolean; version: {
      id: string; label: string; note: string | null;
      snapshot: Record<string, unknown>; metrics: Record<string, unknown> | null; created_at: string;
    } }>(`/properties/${propertyId}/analysis/versions/${versionId}`),

  createAnalysisVersion: (propertyId: string, body: {
    label: string; note?: string;
    snapshot: Record<string, unknown>; metrics: Record<string, unknown>;
  }) =>
    request(`/properties/${propertyId}/analysis/versions`, {
      method: 'POST',
      body: JSON.stringify(body),
    }),

  deleteAnalysisVersion: (propertyId: string, versionId: string) =>
    request(`/properties/${propertyId}/analysis/versions/${versionId}`, { method: 'DELETE' }),

  deleteDealAnalysis: (propertyId: string) =>
    request(`/properties/${propertyId}/analysis`, { method: 'DELETE' }),

  // =========================================================================
  // CRM: Companies
  // =========================================================================
  listCompanies: (params?: Record<string, string>) => {
    const query = params ? '?' + new URLSearchParams(params).toString() : '';
    return request<any>(`/companies${query}`);
  },
  getCompany: (id: string) => request<any>(`/companies/${id}`),
  createCompany: (data: any) =>
    request<any>('/companies', { method: 'POST', body: JSON.stringify(data) }),
  updateCompany: (id: string, data: any) =>
    request<any>(`/companies/${id}`, { method: 'PATCH', body: JSON.stringify(data) }),
  deleteCompany: (id: string) =>
    request<any>(`/companies/${id}`, { method: 'DELETE' }),

  // =========================================================================
  // CRM: Contacts
  // =========================================================================
  listContacts: (params?: Record<string, string>) => {
    const query = params ? '?' + new URLSearchParams(params).toString() : '';
    return request<any>(`/contacts${query}`);
  },
  getContact: (id: string) => request<any>(`/contacts/${id}`),
  /** PHONE1: create or match this app contact in GoHighLevel (no tags) and answer its page. ⛔ Never sends anything. */
  addContactToGhl: (id: string) =>
    request<{ success: boolean; ghl_contact_url: string | null; note: string | null }>(
      `/contacts/${encodeURIComponent(id)}/ghl`, { method: 'POST' }),
  createContact: (data: any) =>
    request<any>('/contacts', { method: 'POST', body: JSON.stringify(data) }),
  updateContact: (id: string, data: any) =>
    request<any>(`/contacts/${id}`, { method: 'PATCH', body: JSON.stringify(data) }),
  deleteContact: (id: string) =>
    request<any>(`/contacts/${id}`, { method: 'DELETE' }),
  linkContactProperty: (contactId: string, data: any) =>
    request<any>(`/contacts/${contactId}/properties`, { method: 'POST', body: JSON.stringify(data) }),
  unlinkContactProperty: (contactId: string, linkId: string) =>
    request<any>(`/contacts/${contactId}/properties/${linkId}`, { method: 'DELETE' }),

  // =========================================================================
  // CRM: Deals
  // =========================================================================
  listCrmDeals: (params?: Record<string, string>) => {
    const query = params ? '?' + new URLSearchParams(params).toString() : '';
    return request<any>(`/crm-deals${query}`);
  },
  getCrmDeal: (id: string) => request<any>(`/crm-deals/${id}`),
  getCrmDealPipeline: () => request<any>('/crm-deals/pipeline'),
  getCrmDealAnalytics: () => request<any>('/crm-deals/analytics'),
  createCrmDeal: (data: any) =>
    request<any>('/crm-deals', { method: 'POST', body: JSON.stringify(data) }),
  updateCrmDeal: (id: string, data: any) =>
    request<any>(`/crm-deals/${id}`, { method: 'PATCH', body: JSON.stringify(data) }),
  /** NN0: the deal's property - attach an analyzer row by id, or create one from a database record. */
  attachDealProperty: (dealId: string, body: { master_property_id?: string; property_id?: string; force_create?: true }) =>
    request<any>(`/crm-deals/${dealId}/property`, { method: 'POST', body: JSON.stringify(body) }),
  /** NN0b: start a CRM deal from a database record or an analyzer row - the deal and its working row together. */
  startDealFromProperty: (body: { master_property_id?: string; property_id?: string; force_create?: true; deal_name?: string; deal_type?: string }) =>
    request<any>('/crm-deals/from-property', { method: 'POST', body: JSON.stringify(body) }),
  searchMasterProperties: (search: string, limit = 8) =>
    request<any>(`/master-properties?${new URLSearchParams({ search, limit: String(limit) }).toString()}`),
  /** AIW2 - the Property Database records a My Deals row might be (nothing when it is linked). */
  getMasterCandidates: (propertyId: string) =>
    request<{ success: boolean; linked_master_id: string | null; candidates: MasterCandidate[]; row?: MasterRowAddress }>(`/properties/${propertyId}/master-candidates`),
  /**
   * AIW2 - his click on Link: the row and its deals take the record; nothing is repointed. AIW2-ADD: `confirmed` is the
   * address he typed in the Add form, sent when the record came from Add's question.
   */
  linkPropertyMaster: (propertyId: string, masterPropertyId: string, confirmed?: { address: string; city: string; state?: string }) =>
    request<{ success: boolean; linked_master_id: string }>(`/properties/${propertyId}/master-link`, {
      method: 'POST',
      body: JSON.stringify(confirmed ? { master_property_id: masterPropertyId, confirmed } : { master_property_id: masterPropertyId }),
    }),
  /** AIW2-ADD (the 119th) - "Add to the Property Database": creates + links (201), or asks with what it found (200). */
  // AIW2-NONE (the 120th): `anyway: true` is his "None of these - add it anyway" - the server skips the fuzzy check only.
  addPropertyMaster: (propertyId: string, body: { address: string; city: string; state: string; zip: string | null; anyway?: true; passed_over?: string[] }) =>
    request<{ success: boolean; linked_master_id: string | null; candidates: MasterCandidate[] }>(`/properties/${propertyId}/master-add`, { method: 'POST', body: JSON.stringify(body) }),
  updateCrmDealStage: (id: string, stage: string, notes?: string) =>
    request<any>(`/crm-deals/${id}/stage`, { method: 'PATCH', body: JSON.stringify({ stage, notes }) }),
  deleteCrmDeal: (id: string) =>
    request<any>(`/crm-deals/${id}`, { method: 'DELETE' }),

  // ── Escrow / Deadlines ──
  listEscrowDeals: () => request<any>('/crm-deals/escrow'),
  getEscrowDashboard: () =>
    request<{ success: boolean } & import('../types').EscrowDashboardData>('/crm-deals/escrow/dashboard'),
  getDeskBriefing: () => request<{ success: boolean; briefing: string }>('/crm-deals/escrow/briefing'),
  setEscrowOpenDate: (dealId: string, date: string) =>
    request<{ success: boolean; escrow_opened_date: string; deadlines: import('../types').DealDeadline[] }>(
      `/crm-deals/${dealId}/escrow-date`, { method: 'PATCH', body: JSON.stringify({ escrow_opened_date: date }) }),
  extractContractTimeline: async (dealId: string, file: File, opts: { kind?: 'purchase_agreement' | 'amendment'; reread?: boolean } = {}) => {
    const token = localStorage.getItem('token');
    if (!token) throw new Error('Please log in');
    const base = import.meta.env.VITE_API_URL || '/api';
    const fd = new FormData();
    fd.append('file', file);
    // DOCTRACK: read as an amendment only on his choice (F5); "Read again" pays for a fresh read (F6).
    if (opts.kind) fd.append('kind', opts.kind);
    if (opts.reread) fd.append('reread', 'true');
    const res = await fetch(`${base}/crm-deals/${dealId}/contract/extract`, {
      method: 'POST', headers: { Authorization: `Bearer ${token}` }, body: fd,
    });
    const data = await res.json();
    if (!res.ok) throw new Error(data.error || 'Extraction failed');
    return data as { success: boolean } & import('../types').ContractExtraction;
  },
  extractContractFromExisting: (dealId: string, documentId: string, opts: { kind?: 'purchase_agreement' | 'amendment'; reread?: boolean } = {}) =>
    request<{ success: boolean } & import('../types').ContractExtraction>(
      `/crm-deals/${dealId}/contract/extract-existing`, { method: 'POST', body: JSON.stringify({ document_id: documentId, ...opts }) }),
  /** DOCTRACK part 2: the contract review's first look at the deal - its rows, the proposed matches, its money. Reads only. */
  previewContractApply: (dealId: string, milestones: Array<{ label: string; deadline_type: string }>) =>
    request<{ success: boolean } & import('../types').ContractApplyPreview>(
      `/crm-deals/${dealId}/contract/plan`, { method: 'POST', body: JSON.stringify({ milestones }) }),
  applyContractTimeline: (dealId: string, body: {
    /** Sent only when set: an amendment counted from its own date needs no Day 1. */
    escrow_opened_date?: string;
    milestones: Array<{
      label: string; deadline_type: string; offset_days: number | null; due_date: string | null; source_text?: string | null;
      /** DOCTRACK part 2: the deal row it changes (or 'new'), what it counts from, its day rule and event, its place in the read. */
      match_id?: string; extensions_taken?: number; anchor?: import('../types').DeadlineAnchor | null;
      day_unit?: 'calendar' | 'business'; weekend_roll?: boolean; event_label?: string | null; event_days?: number | null;
      milestone_index?: number | null;
    }>;
    money?: import('../types').ContractMoney;
    /** DOCTRACK part 2: which money lines he left ticked - only those are written (an empty one clears). */
    money_ticks?: Partial<Record<keyof import('../types').ContractMoney, boolean>>;
    /** DOCTRACK part 2: the deal rows he ticked for removal. */
    remove_ids?: string[];
    parties?: Array<{ role: import('../types').ContractPartyRole; name: string | null; email: string | null; link: boolean; correct_name: boolean; firm?: string | null; phone?: string | null }>;
    property?: { action: 'link' | 'create' | 'none'; property_id?: string; address?: string | null; apn?: string | null; city?: string | null; state?: string | null };
    /** DOCTRACK: the kept read Apply records as applied. */
    filing_id?: string | null;
  }) =>
    request<{ success: boolean; applied_recorded?: boolean | null; parties?: import('../types').ContractApplyPartyResult[] }>(
      `/crm-deals/${dealId}/contract/apply`, { method: 'POST', body: JSON.stringify(body) }),
  /** DOCTRACK: his pick for a broker Apply matched by name alone - link the contact he chose, or make a new one. */
  applyContractParties: (dealId: string, parties: Array<{
    role: import('../types').ContractPartyRole; name: string | null; email: string | null; firm?: string | null; phone?: string | null;
    link_contact_id?: string; create?: boolean;
  }>) =>
    request<{ success: boolean; parties: import('../types').ContractApplyPartyResult[] }>(
      `/crm-deals/${dealId}/contract/parties`, { method: 'POST', body: JSON.stringify({ parties }) }),
  getDealCockpit: (id: string) =>
    request<{ success: boolean; cockpit: import('../types').DealCockpitData }>(`/crm-deals/${id}/cockpit`),
  updateCockpitMoney: (id: string, body: { emd_amount?: number | null; loan_amount?: number | null }) =>
    request<{ success: boolean; money: { emd_amount: number | null; loan_amount: number | null } }>(`/crm-deals/${id}/cockpit-money`, { method: 'PATCH', body: JSON.stringify(body) }),
  nudgeParty: (dealId: string, body: { contact_id: string; channel: 'sms' | 'email'; message: string; subject?: string }) =>
    request<{ success: boolean; sent: boolean }>(`/crm-deals/${dealId}/nudge`, { method: 'POST', body: JSON.stringify(body) }),
  draftPartyMessage: (dealId: string, contactId: string, channel: 'sms' | 'email', intent?: string) =>
    request<{ success: boolean; subject: string; message: string }>(`/crm-deals/${dealId}/parties/${contactId}/draft-message`, { method: 'POST', body: JSON.stringify({ channel, intent }) }),
  dealAssistant: (dealId: string, prompt: string) =>
    request<import('../types').DealAssistantResponse>(`/crm-deals/${dealId}/assistant`, { method: 'POST', body: JSON.stringify({ prompt }) }),
  syncDealTasks: (id: string) =>
    request<{ success: boolean; synced: boolean; pulled?: number; pushed?: number; reason?: string }>(`/crm-deals/${id}/sync-tasks`, { method: 'POST' }),
  getDealSyncStatus: (id: string) =>
    request<{ success: boolean; available: boolean; contact_name: string | null }>(`/crm-deals/${id}/sync-status`),
  getPublicEscrowSummary: (token: string) => {
    const base = import.meta.env.VITE_API_URL || '/api';
    return fetch(`${base}/deal-room/${token}/escrow`).then((r) => r.json());
  },
  listDeadlineDocuments: (dlId: string) =>
    request<{ success: boolean; documents: import('../types').DeadlineDocument[] }>(`/crm-deals/deadlines/${dlId}/documents`),
  /** DOCTRACK part 2: the deal's documents that could have started an event row's clock, earliest arrival first. */
  listDeadlineArrivals: (dlId: string) =>
    request<{ success: boolean; candidates: import('../types').ArrivalCandidate[]; preselected: string | null }>(`/crm-deals/deadlines/${dlId}/arrivals`),
  /** DOCTRACK part 2: "Received" - when the document that starts an event row's clock arrived. Never marks the row done. */
  recordDeadlineArrival: (dlId: string, body: { event_date: string; due_date: string | null; source: 'email' | 'filed' | 'manual'; filing_id: string | null }) =>
    request<{ success: boolean; kept: 'new' | 'earlier' | 'typed'; deadline: import('../types').DealDeadline }>(
      `/crm-deals/deadlines/${dlId}/arrival`, { method: 'POST', body: JSON.stringify(body) }),
  attachDeadlineDocument: async (dlId: string, dealId: string, file: File) => {
    const token = localStorage.getItem('token');
    if (!token) throw new Error('Please log in');
    const base = import.meta.env.VITE_API_URL || '/api';
    const urlRes = await fetch(`${base}/crm-deals/${dealId}/room/upload-url`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
      body: JSON.stringify({ file_name: file.name }),
    });
    const urlData = await urlRes.json();
    if (!urlRes.ok) throw new Error(urlData.error || 'Failed to get upload URL');
    const buf = await file.arrayBuffer();
    const put = await fetch(urlData.upload_url, {
      method: 'PUT',
      headers: { 'Content-Type': file.type || 'application/octet-stream' },
      body: buf,
    });
    if (!put.ok) throw new Error('Failed to upload file');
    const createRes = await fetch(`${base}/crm-deals/deadlines/${dlId}/documents`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
      body: JSON.stringify({ storage_path: urlData.storage_path, file_name: file.name, file_size: file.size, file_type: file.type }),
    });
    const created = await createRes.json();
    if (!createRes.ok) throw new Error(created.error || 'Failed to attach document');
    return created;
  },
  importDeadlineDocumentFromUrl: (dlId: string, url: string, fileName?: string) =>
    request<any>(`/crm-deals/deadlines/${dlId}/documents/from-url`, {
      method: 'POST',
      body: JSON.stringify({ url, file_name: fileName }),
    }),
  detachDeadlineDocument: (dlId: string, docId: string) =>
    request<any>(`/crm-deals/deadlines/${dlId}/documents/${docId}/detach`, { method: 'PATCH' }),
  getDeadlineDocumentDownloadUrl: (dlId: string, docId: string) =>
    request<{ success: boolean; url: string }>(`/crm-deals/deadlines/${dlId}/documents/${docId}/download`),
  openEscrow: (oppId: string, contractDate?: string) =>
    request<any>(`/crm-deals/opportunities/${oppId}/open-escrow`, {
      method: 'POST', body: JSON.stringify({ contract_date: contractDate }),
    }),
  /**
   * D26: the bridge from a GoHighLevel opportunity to a `crm_deals` row, because the AIR
   * contract builder reads `crm_deals` and Joseph's real deals live in `ghl_opportunities`.
   * Read-only - it proposes, it does not write.
   *
   * ✅ Contract reconciled 2026-08-17 against the live controller, and it moved THIS side: the
   * backend's `{ success, proposal: {…snake_case} }` / flat `{ success, deal_id, … }` is the
   * convention already used everywhere in the `crm-deals` family, so the client was the side
   * that was wrong. `readPromotionProposal` / `readPromotionResult` are the only place those
   * wire names appear; callers below see camelCase. They REFUSE on an unrecognised shape rather
   * than reading `undefined` into a deal record - a silently blank `already_linked_deal_id`
   * would offer to promote an opportunity that already has a deal.
   */
  getPromotionProposal: async (oppId: string): Promise<{ success: boolean; data: PromotionProposal }> => {
    const body = await request<unknown>(`/crm-deals/opportunities/${oppId}/promotion-proposal`);
    return { success: true, data: readPromotionProposal(body) };
  },
  /**
   * Creates the deal. `dealType` is REQUIRED and has no client-side default: it selects the
   * AIR form the contract is built from, so guessing it would produce the wrong instrument.
   */
  promoteOpportunity: async (oppId: string, dealType: CrmDealType): Promise<{ success: boolean; data: PromotionResult }> => {
    const body = await request<unknown>(`/crm-deals/opportunities/${oppId}/promote`, {
      method: 'POST',
      body: JSON.stringify({ dealType }),
    });
    return { success: true, data: readPromotionResult(body) };
  },
  /**
   * D26: the AIR contract builder's two calls.
   *
   * 🚨 These exist BECAUSE ContractBuilder.tsx used to call `fetch` directly, with no
   * Authorization header. Measured in the running app, both routes answered
   * `401 {"error":"No authorization token provided"}` - so the review screen had never once
   * worked. `request()` is the only place the token is attached and the only place the
   * refresh-once-then-retry lives; nothing on this screen may bypass it again.
   *
   * No `revision` is sent, deliberately. `loadFormMap` resolves the map by formCode when none
   * is given, and an empty string is NOT "no revision": it slugs to nothing, builds the
   * filename `64..json`, and 500s every preview. See `hasRevision` in formMap.ts.
   */
  previewContract: async (input: ContractRequest): Promise<{ success: boolean; data: ContractPreview }> => {
    const body = await request<unknown>('/contracts/preview', {
      method: 'POST',
      body: JSON.stringify({
        formCode: input.formCode,
        dealId: input.dealId,
        representation: input.representation,
        ...(input.overrides && { overrides: input.overrides }),
      }),
    });
    return { success: true, data: readContractPreview(body) };
  },
  /**
   * D26: read back a contract that was filed. Takes the LINK id from `generateContract`, because
   * that is what `resolveDownload` org-scopes and visibility-checks server-side; a file id would
   * skip both. Answers a signed URL - the same shape as the escrow document download.
   */
  /**
   * D26: every contract already filed against this deal. Without it a generated contract is
   * write-only - it is filed as `target_type: 'deal'` and the only other reader asks for
   * `'deal_room'`.
   */
  listDealContracts: async (dealId: string): Promise<{ success: boolean; data: FiledContract[] }> => {
    const body = await request<unknown>(`/contracts/deal/${dealId}`);
    return { success: true, data: readFiledContracts(body) };
  },

  /** DOCTRACK part 4: one line per compared document row, 200 rows per call; none asked for, no call. */
  documentChangeSummaries: async (fileLinkIds: string[]): Promise<ChangeSummary[]> => {
    const out: ChangeSummary[] = [];
    for (let i = 0; i < fileLinkIds.length; i += 200) {
      out.push(...readChangeSummaries(await request<unknown>(`/document-changes?links=${fileLinkIds.slice(i, i + 200).join(',')}`)));
    }
    return out;
  },
  /** DOCTRACK part 4: one document's kept comparison (a GET never works one out). */
  documentChanges: async (filingId: string): Promise<FilingChanges> =>
    readFilingChanges(await request<unknown>(`/document-changes/${filingId}`)),
  /** "Compare with the earlier version" - his tap. */
  compareDocument: async (filingId: string): Promise<FilingChanges> =>
    readFilingChanges(await request<unknown>(`/document-changes/${filingId}/compare`, { method: 'POST' })),
  /** His Yes to a guessed family. */
  confirmDocumentFamily: async (filingId: string): Promise<FilingChanges> =>
    readFilingChanges(await request<unknown>(`/document-changes/${filingId}/confirm`, { method: 'POST' })),
  /** His "Pick another". */
  compareDocumentWith: async (filingId: string, previousFilingId: string): Promise<FilingChanges> =>
    readFilingChanges(await request<unknown>(`/document-changes/${filingId}/compare-with`, {
      method: 'POST', body: JSON.stringify({ previous_filing_id: previousFilingId }),
    })),
  /** "Mark checked". */
  markDocumentChecked: async (filingId: string): Promise<FilingChanges> =>
    readFilingChanges(await request<unknown>(`/document-changes/${filingId}/checked`, { method: 'POST' })),
  /** "Read again" - a manual re-read on his tap. */
  readDocumentAgain: async (filingId: string): Promise<FilingChanges> =>
    readFilingChanges(await request<unknown>(`/document-changes/${filingId}/read-again`, { method: 'POST' })),
  downloadContract: async (linkId: string): Promise<{ success: boolean; data: ContractDownload }> => {
    const body = await request<unknown>(`/contracts/${linkId}/download`);
    return { success: true, data: readContractDownload(body) };
  },
  /**
   * Files a stamped PDF against the deal. Answers 201; `request()` treats any 2xx as success.
   *
   * A non-2xx throws `APIError` carrying the SERVER's message, which for the common refusal
   * names the unfilled required fields - the caller is expected to show that text verbatim
   * rather than replace it with a generic one.
   */
  generateContract: async (input: ContractRequest): Promise<{ success: boolean; data: ContractGeneration }> => {
    const body = await request<unknown>('/contracts/generate', {
      method: 'POST',
      body: JSON.stringify({
        formCode: input.formCode,
        dealId: input.dealId,
        representation: input.representation,
        ...(input.overrides && { overrides: input.overrides }),
      }),
    });
    return { success: true, data: readContractGeneration(body) };
  },
  listDealDeadlines: (dealId: string) => request<any>(`/crm-deals/${dealId}/deadlines`),
  addDealDeadline: (dealId: string, data: { label: string; due_date: string; deadline_type?: string }) =>
    request<any>(`/crm-deals/${dealId}/deadlines`, { method: 'POST', body: JSON.stringify(data) }),
  updateDealDeadline: (dlId: string, data: Record<string, unknown>) =>
    request<any>(`/crm-deals/deadlines/${dlId}`, { method: 'PATCH', body: JSON.stringify(data) }),
  deleteDealDeadline: (dlId: string) =>
    request<any>(`/crm-deals/deadlines/${dlId}`, { method: 'DELETE' }),
  addDealContact: (dealId: string, contactId: string, role: string) =>
    request<any>(`/crm-deals/${dealId}/contacts`, { method: 'POST', body: JSON.stringify({ contact_id: contactId, role }) }),
  removeDealContact: (dealId: string, dcId: string) =>
    request<any>(`/crm-deals/${dealId}/contacts/${dcId}`, { method: 'DELETE' }),

  // =========================================================================
  // CRM: Activities
  // =========================================================================
  listActivities: (params?: Record<string, string>) => {
    const query = params ? '?' + new URLSearchParams(params).toString() : '';
    return request<any>(`/activities${query}`);
  },
  getUpcomingTasks: () => request<any>('/activities/upcoming-tasks'),
  createActivity: (data: any) =>
    request<any>('/activities', { method: 'POST', body: JSON.stringify(data) }),
  updateActivity: (id: string, data: any) =>
    request<any>(`/activities/${id}`, { method: 'PATCH', body: JSON.stringify(data) }),
  completeActivity: (id: string) =>
    request<any>(`/activities/${id}/complete`, { method: 'PATCH' }),
  deleteActivity: (id: string) =>
    request<any>(`/activities/${id}`, { method: 'DELETE' }),

  // =========================================================================
  // Document Generator (Phase 2)
  // =========================================================================
  generateDocContent: (masterPropertyId: string, contentTypes: string[], stylePrompt?: string) =>
    request<any>('/generate/content', {
      method: 'POST',
      body: JSON.stringify({
        master_property_id: masterPropertyId,
        content_types: contentTypes,
        ...(stylePrompt && { style_prompt: stylePrompt }),
      }),
    }),

  createMasterProperty: (data: Record<string, unknown>) =>
    request<any>('/master-properties', {
      method: 'POST',
      body: JSON.stringify(data),
    }),

  saveGeneratedDoc: (data: {
    master_property_id?: string;
    template_type: string;
    title: string;
    file_url?: string;
    content_snapshot?: Record<string, unknown>;
  }) =>
    request<any>('/generate/save', {
      method: 'POST',
      body: JSON.stringify(data),
    }),

  listGeneratedDocs: (params?: Record<string, string>) => {
    const query = params ? '?' + new URLSearchParams(params).toString() : '';
    return request<any>(`/generate/documents${query}`);
  },

  getGeneratedDoc: (id: string) => request<any>(`/generate/documents/${id}`),

  deleteGeneratedDoc: (id: string) =>
    request<any>(`/generate/documents/${id}`, { method: 'DELETE' }),

  // =========================================================================
  // Listing Sites (Phase 4)
  // =========================================================================
  createListingSite: (data: {
    master_property_id: string;
    custom_headline?: string;
    custom_description?: string;
    template_style?: string;
    lead_capture_email?: string;
    virtual_tour_url?: string;
    brochure_doc_id?: string;
    om_doc_id?: string;
  }) =>
    request<any>('/listing-sites', { method: 'POST', body: JSON.stringify(data) }),

  listListingSites: () => request<any>('/listing-sites'),

  getListingSite: (id: string) => request<any>(`/listing-sites/${id}`),

  updateListingSite: (id: string, data: Record<string, unknown>) =>
    request<any>(`/listing-sites/${id}`, { method: 'PATCH', body: JSON.stringify(data) }),

  deleteListingSite: (id: string) =>
    request<any>(`/listing-sites/${id}`, { method: 'DELETE' }),

  getListingLeads: (id: string) => request<any>(`/listing-sites/${id}/leads`),

  // Public listing endpoints (no auth header needed, but `request` adds it harmlessly)
  fetchPublicListing: (slug: string) => {
    const base = import.meta.env.VITE_API_URL || '/api';
    return fetch(`${base}/public/listings/${slug}`).then(r => r.json());
  },

  submitListingLead: (slug: string, data: { name: string; email: string; phone?: string; company?: string; message?: string }) => {
    const base = import.meta.env.VITE_API_URL || '/api';
    return fetch(`${base}/public/listings/${slug}/leads`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(data),
    }).then(r => r.json());
  },

  // =========================================================================
  // Prospecting Lists (Phase 5)
  // =========================================================================
  previewProspectFilter: (filters: Record<string, any>) =>
    request<any>('/prospect-lists/preview', {
      method: 'POST',
      body: JSON.stringify({ filters }),
    }),

  listProspectLists: () => request<any>('/prospect-lists'),

  getProspectList: (id: string) => request<any>(`/prospect-lists/${id}`),

  createProspectList: (data: { name: string; description?: string; filters: Record<string, any> }) =>
    request<any>('/prospect-lists', {
      method: 'POST',
      body: JSON.stringify(data),
    }),

  updateProspectList: (id: string, data: Record<string, any>) =>
    request<any>(`/prospect-lists/${id}`, {
      method: 'PATCH',
      body: JSON.stringify(data),
    }),

  deleteProspectList: (id: string) =>
    request<any>(`/prospect-lists/${id}`, { method: 'DELETE' }),

  refreshProspectList: (id: string) =>
    request<any>(`/prospect-lists/${id}/refresh`, { method: 'POST' }),

  exportProspectListCSV: async (id: string) => {
    const token = localStorage.getItem('token');
    const base = import.meta.env.VITE_API_URL || '/api';
    const response = await fetch(`${base}/prospect-lists/${id}/export`, {
      headers: { ...(token && { Authorization: `Bearer ${token}` }) },
    });
    if (!response.ok) throw new Error('Export failed');
    const blob = await response.blob();
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = response.headers.get('content-disposition')?.match(/filename="(.+)"/)?.[1] || 'prospect_list.csv';
    document.body.appendChild(a);
    a.click();
    a.remove();
    window.URL.revokeObjectURL(url);
  },

  updateProspectListItem: (listId: string, itemId: string, data: { status?: string; notes?: string }) =>
    request<any>(`/prospect-lists/${listId}/items/${itemId}`, {
      method: 'PATCH',
      body: JSON.stringify(data),
    }),

  bulkUpdateProspectListItems: (listId: string, data: { item_ids: string[]; status?: string; notes?: string }) =>
    request<any>(`/prospect-lists/${listId}/bulk-update`, {
      method: 'POST',
      body: JSON.stringify(data),
    }),

  // =========================================================================
  // Owner Research (Phase 6)
  // =========================================================================
  getOwnerResearch: (propertyId: string) =>
    request<any>(`/owner-research/${propertyId}`),

  runAIOwnerResearch: (propertyId: string) =>
    request<any>(`/owner-research/${propertyId}/ai`, { method: 'POST' }),

  createOwnerResearch: (propertyId: string, data: Record<string, any>) =>
    request<any>(`/owner-research/${propertyId}`, {
      method: 'POST',
      body: JSON.stringify(data),
    }),

  updateOwnerResearch: (id: string, data: Record<string, any>) =>
    request<any>(`/owner-research/${id}`, {
      method: 'PATCH',
      body: JSON.stringify(data),
    }),

  deleteOwnerResearch: (id: string) =>
    request<any>(`/owner-research/${id}`, { method: 'DELETE' }),

  // =========================================================================
  // Email Campaigns (Phase 7)
  // =========================================================================
  listCampaigns: () => request<any>('/campaigns'),

  getCampaign: (id: string) => request<any>(`/campaigns/${id}`),

  createCampaign: (data: {
    name: string;
    campaign_type?: string;
    subject?: string;
    html_body?: string;
    master_property_id?: string;
  }) =>
    request<any>('/campaigns', { method: 'POST', body: JSON.stringify(data) }),

  updateCampaign: (id: string, data: Record<string, any>) =>
    request<any>(`/campaigns/${id}`, { method: 'PATCH', body: JSON.stringify(data) }),

  deleteCampaign: (id: string) =>
    request<any>(`/campaigns/${id}`, { method: 'DELETE' }),

  generateCampaignContent: (data: {
    campaign_type: string;
    master_property_id?: string;
    custom_instructions?: string;
  }) =>
    request<any>('/campaigns/generate-content', {
      method: 'POST',
      body: JSON.stringify(data),
    }),

  addCampaignRecipients: (campaignId: string, data: {
    contact_ids?: string[];
    contact_type?: string;
    tag?: string;
  }) =>
    request<any>(`/campaigns/${campaignId}/recipients`, {
      method: 'POST',
      body: JSON.stringify(data),
    }),

  removeCampaignRecipient: (campaignId: string, recipientId: string) =>
    request<any>(`/campaigns/${campaignId}/recipients/${recipientId}`, { method: 'DELETE' }),

  sendCampaign: (id: string) =>
    request<any>(`/campaigns/${id}/send`, { method: 'POST' }),

  // =========================================================================
  // Reports (Phase 8)
  // =========================================================================
  getReportPipelineForecast: () =>
    request<any>('/reports/pipeline-forecast'),

  getReportBrokerProduction: (params?: { start?: string; end?: string }) => {
    const query = params ? '?' + new URLSearchParams(Object.fromEntries(Object.entries(params).filter(([, v]) => v))).toString() : '';
    return request<any>(`/reports/broker-production${query}`);
  },

  getReportRevenue: (params?: { start?: string; end?: string }) => {
    const query = params ? '?' + new URLSearchParams(Object.fromEntries(Object.entries(params).filter(([, v]) => v))).toString() : '';
    return request<any>(`/reports/revenue${query}`);
  },

  getReportActivitySummary: (params?: { start?: string; end?: string }) => {
    const query = params ? '?' + new URLSearchParams(Object.fromEntries(Object.entries(params).filter(([, v]) => v))).toString() : '';
    return request<any>(`/reports/activity-summary${query}`);
  },

  getReportPropertyAnalytics: () =>
    request<any>('/reports/property-analytics'),

  getReportProspecting: () =>
    request<any>('/reports/prospecting'),

  // =========================================================================
  // Deal Room (Phase 9)
  // =========================================================================
  getDealRoom: (dealId: string) =>
    request<any>(`/crm-deals/${dealId}/room`),

  getDealRoomUploadUrl: (dealId: string, fileName: string) =>
    request<any>(`/crm-deals/${dealId}/room/upload-url`, {
      method: 'POST',
      body: JSON.stringify({ file_name: fileName }),
    }),

  addDealRoomDocument: (dealId: string, data: {
    storage_path: string;
    file_name: string;
    file_size?: number;
    file_type?: string;
    category?: string;
    /** Literal `true` shares on upload; anything else files internal (fail-closed, B48). */
    is_visible_to_external?: boolean;
  }) =>
    request<any>(`/crm-deals/${dealId}/room/documents`, {
      method: 'POST',
      body: JSON.stringify(data),
    }),

  setDealRoomDocumentSharing: (dealId: string, docId: string, shared: boolean) =>
    request<{ success: boolean; visibility: string; shared: boolean }>(
      `/crm-deals/${dealId}/room/documents/${docId}/visibility`,
      { method: 'PATCH', body: JSON.stringify({ shared }) }
    ),

  deleteDealRoomDocument: (dealId: string, docId: string) =>
    request<any>(`/crm-deals/${dealId}/room/documents/${docId}`, { method: 'DELETE' }),

  downloadDealRoomDocument: (dealId: string, docId: string) =>
    request<{ success: boolean; url: string }>(`/crm-deals/${dealId}/room/documents/${docId}/download`),

  /**
   * LOI-PDF1b (the 130th): a Deal Room letter as a real PDF made from its Word file - header, footer and all. Answers
   * a signed link for a window opened inside the tap; the first open of a letter makes the PDF (a few seconds).
   * ⛔ Same gate as the download above, one word apart (pinned in `api.letterPdf.test.ts`). A refusal carries words
   * for him and the page then opens the Word file instead.
   */
  dealRoomDocumentPdf: (dealId: string, docId: string) =>
    request<{ success: boolean; url: string; filename: string }>(`/crm-deals/${dealId}/room/documents/${docId}/pdf`),

  createDealRoomInvite: (dealId: string, data: { email: string; name?: string; expires_in_days?: number }) =>
    request<any>(`/crm-deals/${dealId}/room/invites`, {
      method: 'POST',
      body: JSON.stringify(data),
    }),

  revokeDealRoomInvite: (dealId: string, inviteId: string) =>
    request<any>(`/crm-deals/${dealId}/room/invites/${inviteId}`, { method: 'DELETE' }),

  getDealRoomActivity: (dealId: string) =>
    request<any>(`/crm-deals/${dealId}/room/activity`),

  // Public deal room (no auth)
  fetchPublicDealRoom: (token: string) => {
    const base = import.meta.env.VITE_API_URL || '/api';
    return fetch(`${base}/deal-room/${token}`).then(r => r.json());
  },

  downloadPublicDealRoomFile: (token: string, docId: string) => {
    const base = import.meta.env.VITE_API_URL || '/api';
    return fetch(`${base}/deal-room/${token}/download/${docId}`, { method: 'POST' }).then(r => r.json());
  },

  // Client portal (no auth - the token IS the credential; the payload is already tier-filtered
  // server-side, so whatever arrives here is what this link is allowed to see).
  // A 404 body carries { success: false, error }, which the page renders as a dead link -
  // hence .json() on every status rather than a throw.
  // TP-RL2: a 429 is NOT a dead link - the page was asked for too often - and its body looks just like one, so it is read
  // from the STATUS and answered as `rateLimited` (its body may be an edge's own page rather than JSON).
  fetchPublicPortal: async (
    token: string
  ): Promise<{ success?: boolean; portal?: any; error?: string; rateLimited?: boolean }> => {
    const base = import.meta.env.VITE_API_URL || '/api';
    const r = await fetch(`${base}/portal/${token}`);
    if (r.status === 429) {
      const body = await r.json().catch(() => null);
      return { success: false, rateLimited: true, ...(typeof body?.error === 'string' ? { error: body.error } : {}) };
    }
    return r.json();
  },

  /** Public note write - same no-auth pattern as fetchPublicPortal; the token is the credential. */
  createPortalNote: (
    token: string,
    body: { anchorType: string; anchorKey: string; authorName: string; body: string }
  ) => {
    const base = import.meta.env.VITE_API_URL || '/api';
    return fetch(`${base}/portal/${token}/notes`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(body),
    }).then(r => r.json());
  },

  // Client portals - broker admin (authenticated).
  listPortals: () => request<{ success: boolean; portals: PortalAdminRow[] }>('/portals'),

  /**
   * Mints a share link. The RAW TOKEN comes back exactly once - only its sha256 hash is
   * stored - so whatever calls this must show it to the user before dropping it.
   */
  mintPortalLink: (portalId: string, body: { tier: string; label: string; expiresAt?: string | null }) =>
    request<{ success: boolean; token: string; link: PortalLinkRow }>(`/portals/${portalId}/links`, {
      method: 'POST',
      body: JSON.stringify(body),
    }),

  // Admin-only server-side; a non-admin gets a 403, which surfaces as an APIError message.
  revokePortalLink: (portalId: string, linkId: string) =>
    request<{ success: boolean }>(`/portals/${portalId}/links/${linkId}`, { method: 'DELETE' }),

  getPortalActivity: (portalId: string) =>
    request<{ success: boolean; activity: PortalActivityRow[] }>(`/portals/${portalId}/activity`),

  getAreaRollup: (area: string) =>
    request<{ success: boolean; area: string; rollup: AreaRollup }>(`/portals/areas/${encodeURIComponent(area)}/rollup`),

  /** Broker view of the family notes - unlike the portal payload, this INCLUDES hidden rows. */
  listPortalNotesAdmin: (portalId: string) =>
    request<{ success: boolean; notes: PortalNoteAdminRow[] }>(`/portals/${portalId}/notes`),

  // Moderation is hide/unhide, never deletion - the row survives so the trail does.
  setPortalNoteHidden: (portalId: string, noteId: string, hidden: boolean) =>
    request<{ success: boolean }>(`/portals/${portalId}/notes/${noteId}`, {
      method: 'PATCH',
      body: JSON.stringify({ hidden }),
    }),

  /** VP9 - the editor's view of one portal (admin-only server-side): never the whole contract, never a figure. */
  getPortalEditor: (portalId: string) =>
    request<{ success: boolean; editor: PortalEditorView }>(`/portals/${portalId}/editor`),

  /**
   * VP9 - save ONE change. A refusal throws an APIError whose `body.violations` lists every reason, word for word; a
   * 409 means the portal changed since the view was loaded (reload), or was written around the editor.
   */
  savePortalEdit: (portalId: string, body: PortalEditBody) =>
    request<{ success: boolean; editor: PortalEditorView }>(`/portals/${portalId}/edits`, {
      method: 'POST',
      body: JSON.stringify(body),
    }),

  /**
   * HM1 S3 - the change queue: intentions an agent filed, waiting for a human.
   *
   * ⚠️ NOT the owner-facing listing PROPOSAL (`/api/proposals`, `generateProposal`). Different thing, older
   *    word - which is why this one is "change" throughout, on both sides of the wire.
   */
  getChangeQueue: (status?: ChangeProposalStatus) =>
    request<{ success: boolean; data: { proposals: ChangeProposal[]; counts: Record<string, number> } }>(
      `/change-queue${status ? `?status=${encodeURIComponent(status)}` : ''}`
    ),

  /**
   * Runs the real write path. A refusal throws an APIError whose `body.violations` lists every reason.
   *
   * LEADS1 - a lead message also needs the version on screen (`expectedUpdatedAt`, the row's `updated_at`: none is a
   * 400, another is a 409) and, when its receipts are not all green, `confirmUnverified: true`. Every other kind
   * ignores both fields server-side, so the one-argument call keeps working.
   */
  approveChange: (id: string, confirmUnverified = false, expectedUpdatedAt?: string) =>
    request<{ success: boolean; data: { proposal: ChangeProposal } }>(`/change-queue/${id}/approve`, {
      method: 'POST',
      body: JSON.stringify({ confirmUnverified, expectedUpdatedAt }),
    }),

  rejectChange: (id: string, note?: string) =>
    request<{ success: boolean; data: { proposal: ChangeProposal } }>(`/change-queue/${id}/reject`, {
      method: 'POST',
      body: JSON.stringify({ note }),
    }),

  undoChange: (id: string) =>
    request<{ success: boolean; data: { proposal: ChangeProposal } }>(`/change-queue/${id}/undo`, {
      method: 'POST',
    }),

  /**
   * LEADS1 - change a lead message's words before approving. ⛔ Against the version on screen, like approve. An
   * undefined `subject` drops out of the JSON, which the server reads as "keep the subject as filed".
   */
  editLeadMessage: (id: string, body: string, subject: string | null | undefined, expectedUpdatedAt: string) =>
    request<{ success: boolean; data: { proposal: ChangeProposal } }>(`/change-queue/${id}/edit`, {
      method: 'POST',
      body: JSON.stringify({ body, subject, expectedUpdatedAt }),
    }),

  /** LEADS1 - pull an approved message back to Waiting before the worker sends it. Too late = a 409 saying so. */
  cancelLeadMessage: (id: string) =>
    request<{ success: boolean; data: { proposal: ChangeProposal } }>(`/change-queue/${id}/cancel`, { method: 'POST' }),

  /** LEADS1 - ⛔ against the version on screen too, like approve and edit: a skip decides about a draft he SAW. */
  skipLeadMessage: (id: string, reason: 'handled' | 'snooze' | 'not_a_lead', expectedUpdatedAt: string) =>
    request<{ success: boolean; data: { proposal: ChangeProposal } }>(`/change-queue/${id}/skip`, {
      method: 'POST',
      body: JSON.stringify({ reason, expectedUpdatedAt }),
    }),

  /**
   * LEADS1 - the lead-sending switch. ⛔ `readable: false` = the server could not read it and treats it as OFF - which
   * is NOT the same as off: the switch may really be on. The screen says UNKNOWN then, never "OFF".
   */
  getLeadSending: () =>
    request<{ success: boolean; data: { enabled: boolean; readable: boolean } }>('/change-queue/lead-sending'),

  /** Answers what the database now holds, not what was asked for. */
  setLeadSending: (enabled: boolean) =>
    request<{ success: boolean; data: { enabled: boolean } }>('/change-queue/lead-sending', {
      method: 'PUT',
      body: JSON.stringify({ enabled }),
    }),

  /** VP9 - take back the latest change to a thing, through the same checks as a save. */
  undoPortalEdit: (portalId: string, seq: number, expectedUpdatedAt: string) =>
    request<{ success: boolean; editor: PortalEditorView }>(`/portals/${portalId}/edits/${seq}/undo`, {
      method: 'POST',
      body: JSON.stringify({ expectedUpdatedAt }),
    }),

  uploadDealRoomFile: async (dealId: string, file: File, category: string = 'other', description?: string) => {
    const token = localStorage.getItem('token');
    if (!token) throw new Error('Please log in');

    // Step 1: Get signed upload URL
    const base = import.meta.env.VITE_API_URL || '/api';
    const urlRes = await fetch(`${base}/crm-deals/${dealId}/room/upload-url`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
      body: JSON.stringify({ file_name: file.name }),
    });
    const urlData = await urlRes.json();
    if (!urlRes.ok) throw new Error(urlData.error || 'Failed to get upload URL');

    // Step 2: Upload to storage
    const fileBuffer = await file.arrayBuffer();
    const storageRes = await fetch(urlData.upload_url, {
      method: 'PUT',
      headers: { 'Content-Type': file.type || 'application/octet-stream' },
      body: fileBuffer,
    });
    if (!storageRes.ok) throw new Error('Failed to upload file');

    // Step 3: Create document record
    const createRes = await fetch(`${base}/crm-deals/${dealId}/room/documents`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
      body: JSON.stringify({
        storage_path: urlData.storage_path,
        file_name: file.name,
        file_size: file.size,
        file_type: file.type,
        category,
        description,
      }),
    });
    const createData = await createRes.json();
    if (!createRes.ok) throw new Error(createData.error || 'Failed to create document');
    return createData;
  },

  // =========================================================================
  // Playbooks (Phase 9)
  // =========================================================================
  listPlaybooks: (params?: Record<string, string>) => {
    const query = params ? '?' + new URLSearchParams(params).toString() : '';
    return request<any>(`/playbooks${query}`);
  },

  getPlaybook: (id: string) => request<any>(`/playbooks/${id}`),

  createPlaybook: (data: { name: string; description?: string; deal_type?: string }) =>
    request<any>('/playbooks', { method: 'POST', body: JSON.stringify(data) }),

  updatePlaybook: (id: string, data: Record<string, any>) =>
    request<any>(`/playbooks/${id}`, { method: 'PATCH', body: JSON.stringify(data) }),

  deletePlaybook: (id: string) =>
    request<any>(`/playbooks/${id}`, { method: 'DELETE' }),

  addPlaybookTask: (playbookId: string, data: Record<string, any>) =>
    request<any>(`/playbooks/${playbookId}/tasks`, { method: 'POST', body: JSON.stringify(data) }),

  updatePlaybookTask: (playbookId: string, taskId: string, data: Record<string, any>) =>
    request<any>(`/playbooks/${playbookId}/tasks/${taskId}`, { method: 'PATCH', body: JSON.stringify(data) }),

  deletePlaybookTask: (playbookId: string, taskId: string) =>
    request<any>(`/playbooks/${playbookId}/tasks/${taskId}`, { method: 'DELETE' }),

  // =========================================================================
  // Deal Tasks (Phase 9)
  // =========================================================================
  listDealTasks: (dealId: string) =>
    request<any>(`/crm-deals/${dealId}/tasks`),

  createDealTask: (dealId: string, data: Record<string, any>) =>
    request<any>(`/crm-deals/${dealId}/tasks`, { method: 'POST', body: JSON.stringify(data) }),

  updateDealTask: (dealId: string, taskId: string, data: Record<string, any>) =>
    request<any>(`/crm-deals/${dealId}/tasks/${taskId}`, { method: 'PATCH', body: JSON.stringify(data) }),

  deleteDealTask: (dealId: string, taskId: string) =>
    request<any>(`/crm-deals/${dealId}/tasks/${taskId}`, { method: 'DELETE' }),

  applyPlaybook: (dealId: string, playbookId: string) =>
    request<any>(`/crm-deals/${dealId}/apply-playbook`, {
      method: 'POST',
      body: JSON.stringify({ playbook_id: playbookId }),
    }),

  getOfficialChecklist: (dealId: string) =>
    request<import('../types').OfficialChecklistInfo>(`/crm-deals/${dealId}/official-checklist`),

  applyOfficialChecklist: (dealId: string) =>
    request<{ success: boolean; message: string; playbook: { id: string; name: string }; added: number; skipped: number }>(
      `/crm-deals/${dealId}/apply-official-checklist`, { method: 'POST' }),

  // =========================================================================
  // Syndication (Phase 10)
  // =========================================================================
  listSyndicationPlatforms: () =>
    request<any>('/syndication/platforms'),

  listSyndications: (params?: Record<string, string>) => {
    const query = params ? '?' + new URLSearchParams(params).toString() : '';
    return request<any>(`/syndication${query}`);
  },

  getSyndication: (id: string) =>
    request<any>(`/syndication/${id}`),

  createSyndication: (data: { listing_site_id: string; platform_id: string }) =>
    request<any>('/syndication', { method: 'POST', body: JSON.stringify(data) }),

  publishSyndication: (id: string) =>
    request<any>(`/syndication/${id}/publish`, { method: 'POST' }),

  syncSyndication: (id: string) =>
    request<any>(`/syndication/${id}/sync`, { method: 'POST' }),

  delistSyndication: (id: string) =>
    request<any>(`/syndication/${id}/delist`, { method: 'POST' }),

  deleteSyndication: (id: string) =>
    request<any>(`/syndication/${id}`, { method: 'DELETE' }),

  generateSyndicationExport: (id: string, format: 'csv' | 'json' = 'csv') =>
    request<any>(`/syndication/${id}/export`, {
      method: 'POST',
      body: JSON.stringify({ format }),
    }),

  getSyndicationActivity: (id: string) =>
    request<any>(`/syndication/${id}/activity`),

  bulkPublishSyndication: (data: { listing_site_id: string; platform_ids: string[] }) =>
    request<any>('/syndication/bulk-publish', {
      method: 'POST',
      body: JSON.stringify(data),
    }),

  // =========================================================================
  // Asset Services: Vendors (Phase 11A)
  // =========================================================================
  listVendors: (params?: Record<string, string>) => {
    const query = params ? '?' + new URLSearchParams(params).toString() : '';
    return request<any>(`/vendors${query}`);
  },

  getVendor: (id: string) => request<any>(`/vendors/${id}`),

  createVendor: (data: Record<string, any>) =>
    request<any>('/vendors', { method: 'POST', body: JSON.stringify(data) }),

  updateVendor: (id: string, data: Record<string, any>) =>
    request<any>(`/vendors/${id}`, { method: 'PUT', body: JSON.stringify(data) }),

  deleteVendor: (id: string) =>
    request<any>(`/vendors/${id}`, { method: 'DELETE' }),

  // =========================================================================
  // Asset Services: Managed Properties (Phase 11A)
  // =========================================================================
  listManagedProperties: () =>
    request<any>('/master-properties/managed'),

  getMasterProperty: (id: string) =>
    request<any>(`/master-properties/${id}`),

  /** DOCTRACK part 3: every number the record keeps, per field, and the open "use its numbers?" questions. */
  getFieldValues: (id: string) =>
    request<{ success: boolean } & FieldValuesView>(`/master-properties/${id}/field-values`),

  /** DOCTRACK part 3: his "Use this" - that number goes in use and becomes his. */
  pickFieldValue: (id: string, valueId: string) =>
    request<{ success: boolean; inUseChanged: boolean }>(`/master-properties/${id}/field-values/${valueId}/use`, { method: 'POST' }),

  /** DOCTRACK part 3: his yes or no to a document that names another property. */
  answerValueAsk: (id: string, askId: string, answer: 'yes' | 'no') =>
    request<{ success: boolean; state: 'used' | 'declined' }>(`/master-properties/${id}/value-asks/${askId}`, {
      method: 'POST',
      body: JSON.stringify({ answer }),
    }),

  updatePropertyManagement: (id: string, data: Record<string, any>) =>
    request<any>(`/master-properties/${id}/management`, { method: 'PATCH', body: JSON.stringify(data) }),

  updatePipelineStatus: (id: string, pipeline_status: string) =>
    request<any>(`/master-properties/${id}/pipeline`, { method: 'PATCH', body: JSON.stringify({ pipeline_status }) }),

  // AI agents
  agentCallPrep: (data: { propertyId?: string; contactId?: string; ownerName?: string; question?: string }) =>
    request<any>('/agents/call-prep', { method: 'POST', body: JSON.stringify(data) }),

  agentDealRadar: () =>
    request<any>('/agents/deal-radar', { method: 'POST', body: JSON.stringify({}) }),

  agentOwnerOutreach: (data: { filter?: Record<string, any>; angle?: string; channel?: 'email' | 'letter' }) =>
    request<any>('/agents/owner-outreach', { method: 'POST', body: JSON.stringify(data) }),

  // HUMAN-SUBMIT ONLY — called from the per-draft confirm on /ai/outreach.
  // Upserts the owner as a GHL contact and sends the reviewed draft via GHL email.
  agentOutreachPushToGhl: (data: {
    propertyId: string;
    ownerName: string;
    ownerEmail: string;
    ownerPhone?: string | null;
    propertyAddress?: string;
    subject: string;
    body: string;
  }) =>
    request<any>('/agents/owner-outreach/push-to-ghl', { method: 'POST', body: JSON.stringify(data) }),

  // =========================================================================
  // Asset Services: Tenants (Phase 11B)
  // =========================================================================
  listTenants: (propertyId: string) =>
    request<any>(`/tenants/property/${propertyId}`),

  getTenant: (id: string) => request<any>(`/tenants/${id}`),

  createTenant: (data: Record<string, any>) =>
    request<any>('/tenants', { method: 'POST', body: JSON.stringify(data) }),

  updateTenant: (id: string, data: Record<string, any>) =>
    request<any>(`/tenants/${id}`, { method: 'PUT', body: JSON.stringify(data) }),

  deleteTenant: (id: string) =>
    request<any>(`/tenants/${id}`, { method: 'DELETE' }),

  getRentRoll: (propertyId: string) =>
    request<any>(`/tenants/property/${propertyId}/rent-roll`),

  // =========================================================================
  // Asset Services: Rent Payments (Phase 11B)
  // =========================================================================
  listRentPayments: (params?: Record<string, string>) => {
    const query = params ? '?' + new URLSearchParams(params).toString() : '';
    return request<any>(`/rent-payments${query}`);
  },

  createRentPayment: (data: Record<string, any>) =>
    request<any>('/rent-payments', { method: 'POST', body: JSON.stringify(data) }),

  updateRentPayment: (id: string, data: Record<string, any>) =>
    request<any>(`/rent-payments/${id}`, { method: 'PUT', body: JSON.stringify(data) }),

  bulkCreateRentPayments: (data: { property_id: string; period_start: string; period_end: string }) =>
    request<any>('/rent-payments/bulk', { method: 'POST', body: JSON.stringify(data) }),

  // =========================================================================
  // Asset Services: Operating Expenses (Phase 11C)
  // =========================================================================
  listExpenses: (params?: Record<string, string>) => {
    const query = params ? '?' + new URLSearchParams(params).toString() : '';
    return request<any>(`/expenses${query}`);
  },

  createExpense: (data: Record<string, any>) =>
    request<any>('/expenses', { method: 'POST', body: JSON.stringify(data) }),

  updateExpense: (id: string, data: Record<string, any>) =>
    request<any>(`/expenses/${id}`, { method: 'PUT', body: JSON.stringify(data) }),

  deleteExpense: (id: string) =>
    request<any>(`/expenses/${id}`, { method: 'DELETE' }),

  bulkCreateExpenses: (data: { property_id: string; expenses: Record<string, any>[] }) =>
    request<any>('/expenses/bulk', { method: 'POST', body: JSON.stringify(data) }),

  categorizeExpenses: (data: { expenses: Array<{ description: string; amount: number; vendor_name?: string }> }) =>
    request<any>('/expenses/categorize', { method: 'POST', body: JSON.stringify(data) }),

  // =========================================================================
  // Asset Services: CAM Reconciliation (Phase 11D)
  // =========================================================================
  listCamReconciliations: (params?: Record<string, string>) => {
    const query = params ? '?' + new URLSearchParams(params).toString() : '';
    return request<any>(`/cam-reconciliations${query}`);
  },

  getCamReconciliation: (id: string) => request<any>(`/cam-reconciliations/${id}`),

  createCamReconciliation: (data: Record<string, any>) =>
    request<any>('/cam-reconciliations', { method: 'POST', body: JSON.stringify(data) }),

  calculateCamReconciliation: (id: string) =>
    request<any>(`/cam-reconciliations/${id}/calculate`, { method: 'POST' }),

  updateCamReconciliation: (id: string, data: Record<string, any>) =>
    request<any>(`/cam-reconciliations/${id}`, { method: 'PUT', body: JSON.stringify(data) }),

  finalizeCamReconciliation: (id: string) =>
    request<any>(`/cam-reconciliations/${id}/finalize`, { method: 'POST' }),

  getCamExpenseBreakdown: (id: string) =>
    request<any>(`/cam-reconciliations/${id}/expense-breakdown`),

  extractLeaseTerms: (data: { tenant_id: string; document_text: string }) =>
    request<any>('/cam-reconciliations/extract-lease-terms', { method: 'POST', body: JSON.stringify(data) }),

  getCamReport: (id: string) =>
    request<any>(`/cam-reconciliations/${id}/report`),

  getLeaseTerms: (tenantId: string) =>
    request<any>(`/tenants/${tenantId}/lease-terms`),

  upsertLeaseTerms: (tenantId: string, data: Record<string, any>) =>
    request<any>(`/tenants/${tenantId}/lease-terms`, { method: 'PUT', body: JSON.stringify(data) }),

  deleteLeaseTerms: (tenantId: string) =>
    request<any>(`/tenants/${tenantId}/lease-terms`, { method: 'DELETE' }),

  // =========================================================================
  // Asset Services: Financial Reports (Phase 11D)
  // =========================================================================
  getOwnerStatement: (params: { property_id: string; month: string }) => {
    const query = '?' + new URLSearchParams(params).toString();
    return request<any>(`/financial-reports/owner-statement${query}`);
  },

  getYearEndReport: (params: { property_id: string; year: string }) => {
    const query = '?' + new URLSearchParams(params).toString();
    return request<any>(`/financial-reports/year-end${query}`);
  },

  getBudgetVsActual: (params: { property_id: string; year: string }) => {
    const query = '?' + new URLSearchParams(params).toString();
    return request<any>(`/financial-reports/budget-vs-actual${query}`);
  },

  // =========================================================================
  // Asset Services: Budgets (Phase 11D)
  // =========================================================================
  listBudgets: (propertyId: string) =>
    request<any>(`/budgets?property_id=${propertyId}`),

  getBudget: (id: string) => request<any>(`/budgets/${id}`),

  createBudget: (data: Record<string, any>) =>
    request<any>('/budgets', { method: 'POST', body: JSON.stringify(data) }),

  updateBudget: (id: string, data: Record<string, any>) =>
    request<any>(`/budgets/${id}`, { method: 'PUT', body: JSON.stringify(data) }),

  deleteBudget: (id: string) =>
    request<any>(`/budgets/${id}`, { method: 'DELETE' }),

  // =========================================================================
  // Asset Services: Inspections (Phase 11E)
  // =========================================================================
  listInspections: (params?: Record<string, string>) => {
    const query = params ? '?' + new URLSearchParams(params).toString() : '';
    return request<any>(`/inspections${query}`);
  },

  getInspection: (id: string) => request<any>(`/inspections/${id}`),

  createInspection: (data: Record<string, any>) =>
    request<any>('/inspections', { method: 'POST', body: JSON.stringify(data) }),

  updateInspection: (id: string, data: Record<string, any>) =>
    request<any>(`/inspections/${id}`, { method: 'PUT', body: JSON.stringify(data) }),

  deleteInspection: (id: string) =>
    request<any>(`/inspections/${id}`, { method: 'DELETE' }),

  createConditionItem: (inspectionId: string, data: Record<string, any>) =>
    request<any>(`/inspections/${inspectionId}/conditions`, { method: 'POST', body: JSON.stringify(data) }),

  updateConditionItem: (inspectionId: string, itemId: string, data: Record<string, any>) =>
    request<any>(`/inspections/${inspectionId}/conditions/${itemId}`, { method: 'PUT', body: JSON.stringify(data) }),

  // =========================================================================
  // Asset Services: Work Orders (Phase 11E)
  // =========================================================================
  listWorkOrders: (params?: Record<string, string>) => {
    const query = params ? '?' + new URLSearchParams(params).toString() : '';
    return request<any>(`/work-orders${query}`);
  },

  getWorkOrder: (id: string) => request<any>(`/work-orders/${id}`),

  createWorkOrder: (data: Record<string, any>) =>
    request<any>('/work-orders', { method: 'POST', body: JSON.stringify(data) }),

  updateWorkOrder: (id: string, data: Record<string, any>) =>
    request<any>(`/work-orders/${id}`, { method: 'PUT', body: JSON.stringify(data) }),

  deleteWorkOrder: (id: string) =>
    request<any>(`/work-orders/${id}`, { method: 'DELETE' }),

  // =========================================================================
  // Asset Services: Capital Projects (Phase 11E)
  // =========================================================================
  listCapitalProjects: (params?: Record<string, string>) => {
    const query = params ? '?' + new URLSearchParams(params).toString() : '';
    return request<any>(`/capital-projects${query}`);
  },

  createCapitalProject: (data: Record<string, any>) =>
    request<any>('/capital-projects', { method: 'POST', body: JSON.stringify(data) }),

  updateCapitalProject: (id: string, data: Record<string, any>) =>
    request<any>(`/capital-projects/${id}`, { method: 'PUT', body: JSON.stringify(data) }),

  deleteCapitalProject: (id: string) =>
    request<any>(`/capital-projects/${id}`, { method: 'DELETE' }),

  // =========================================================================
  // Asset Services: Compliance (Phase 11F)
  // =========================================================================
  listComplianceItems: (params?: Record<string, string>) => {
    const query = params ? '?' + new URLSearchParams(params).toString() : '';
    return request<any>(`/compliance${query}`);
  },

  getComplianceItem: (id: string) => request<any>(`/compliance/${id}`),

  createComplianceItem: (data: Record<string, any>) =>
    request<any>('/compliance', { method: 'POST', body: JSON.stringify(data) }),

  updateComplianceItem: (id: string, data: Record<string, any>) =>
    request<any>(`/compliance/${id}`, { method: 'PUT', body: JSON.stringify(data) }),

  deleteComplianceItem: (id: string) =>
    request<any>(`/compliance/${id}`, { method: 'DELETE' }),

  getUpcomingCompliance: (days: number = 30) =>
    request<any>(`/compliance/upcoming?days=${days}`),

  exportReportCSV: async (type: string, params?: { start?: string; end?: string }) => {
    const token = localStorage.getItem('token');
    const base = import.meta.env.VITE_API_URL || '/api';
    const query = params ? '?' + new URLSearchParams(Object.fromEntries(Object.entries(params).filter(([, v]) => v))).toString() : '';
    const response = await fetch(`${base}/reports/${type}/export${query}`, {
      headers: { ...(token && { Authorization: `Bearer ${token}` }) },
    });
    if (!response.ok) throw new Error('Export failed');
    const blob = await response.blob();
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = response.headers.get('content-disposition')?.match(/filename="(.+)"/)?.[1] || `${type}_report.csv`;
    document.body.appendChild(a);
    a.click();
    a.remove();
    window.URL.revokeObjectURL(url);
  },

  // ─── Executive Summaries ────────────────────────────────────────────
  listExecutiveSummaries: (propertyId: string) =>
    request<{ success: boolean; data: any[] }>(`/executive-summaries/${propertyId}`),

  uploadExecutiveSummary: async (
    propertyId: string,
    pdfBlob: Blob,
    meta: { owner_name: string; honorific: string; entity_name?: string }
  ) => {
    const token = localStorage.getItem('token');
    if (!token) throw new APIError(401, 'Please log in');

    const fileName = `Executive_Summary_${meta.owner_name.replace(/[^a-zA-Z0-9]/g, '_')}_${new Date().toISOString().split('T')[0]}.pdf`;

    // Step 1: Get signed upload URL
    const urlRes = await fetch(`${API_BASE}/executive-summaries/upload-url`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
      body: JSON.stringify({ property_id: propertyId, file_name: fileName }),
    });
    const urlData = await urlRes.json();
    if (!urlRes.ok) throw new APIError(urlRes.status, urlData.error);

    // Step 2: Upload PDF directly to Supabase Storage
    const uploadRes = await fetch(urlData.upload_url, {
      method: 'PUT',
      headers: { 'Content-Type': 'application/pdf' },
      body: await pdfBlob.arrayBuffer(),
    });
    if (!uploadRes.ok) throw new APIError(uploadRes.status, 'Failed to upload PDF');

    // Step 3: Create database record
    const createRes = await fetch(`${API_BASE}/executive-summaries`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
      body: JSON.stringify({
        property_id: propertyId,
        owner_name: meta.owner_name,
        honorific: meta.honorific,
        entity_name: meta.entity_name,
        storage_path: urlData.storage_path,
        file_name: fileName,
      }),
    });
    const createData = await createRes.json();
    if (!createRes.ok) throw new APIError(createRes.status, createData.error);

    return createData.data;
  },

  deleteExecutiveSummary: (id: string) =>
    request<{ success: boolean }>(`/executive-summaries/${id}`, { method: 'DELETE' }),

  // =========================================================================
  // App-Wide AI Assistant
  // =========================================================================
  assistantChat: (body: { message: string; chat_id?: string | null; page_context?: string; attachments?: any[]; letter_on_screen?: 'none' | 'writing' | 'finished'; subject?: AssistantSubject }) =>
    request<{
      success: boolean; chat_id: string; message_id: string; message: string;
      /** Dialogs the answer asked the app to open. ⛔ Fields only - never a record id; the page
       *  the question was asked from supplies the record. Older responses omit it. */
      actions?: { name: 'open_brochure_editor' | 'open_loi_dialog'; fields: Record<string, string | number> }[];
      /** CHAT-DOCS: the paperwork this answer made - the stream's `document` events, on the fallback path. */
      documents?: DocumentCard[];
      /** MORNING2: the email cards this turn showed - the stream's `send_card` events, on the fallback path. */
      send_cards?: ListedSendCard[];
    }>('/assistant/chat', {
      method: 'POST',
      body: JSON.stringify(body),
    }),

  /**
   * The streaming chat: returns the RAW Response so the caller can read ndjson events off
   * `response.body` as the model writes. The page falls back to `assistantChat` if this
   * errors before any event arrives, so a proxy that breaks streaming degrades, not dies.
   */
  assistantChatStream: (body: {
    message: string; chat_id?: string | null; page_context?: string; attachments?: any[]; letter_on_screen?: 'none' | 'writing' | 'finished'; subject?: AssistantSubject;
    /** MORNING2: a turn from the Morning page's chat - it lands in today's morning chat, whatever `chat_id` says. */
    morning?: true;
    /** MORNING2: the item his tap started this turn from. */
    morning_item?: MorningItemRef;
  }) => {
    const base = import.meta.env.VITE_API_URL || '/api';
    const token = localStorage.getItem('token');
    return fetch(`${base}/assistant/chat/stream`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', ...(token ? { Authorization: `Bearer ${token}` } : {}) },
      body: JSON.stringify(body),
    });
  },

  /**
   * CHAT-DOCS Task 12: the paperwork a chat made, in creation order, for a reloaded chat. ⭐ Every URL is signed
   *   afresh by the server - a stream card's link lives one hour, this one is new each time it is asked.
   */
  listChatDocuments: (chatId: string) =>
    request<{ success: boolean; data: DocumentCard[] }>(`/assistant/chat/${encodeURIComponent(chatId)}/documents`),

  /**
   * MORNING2 (Task 7): the chat's email cards for a reload, oldest first, each open one re-read from Gmail. ⛔ A card whose
   *   draft could not be read is listed with `unreadable` words, never dropped.
   */
  assistantSendCards: (chatId: string) =>
    request<{ success: boolean; cards: ListedSendCard[] }>(`/assistant/chat/${encodeURIComponent(chatId)}/send-cards`),

  /** VOICE1-B (slice 0): is a voice set up on the server, and whose. */
  voiceStatus: () =>
    request<{ success: boolean; voice: { available: boolean; supplier?: string } }>('/voice'),

  /**
   * VOICE1-B (slice 0): one sentence as a clip - a raw fetch, because the answer is sound, not JSON.
   * ⛔ NEVER THROWS, and answers null for everything that is not a clip (no voice set up, a fault, the limit, signed
   * out, the network, the request given up): the caller's other voice is the phone's own, and an error thrown here
   * would be a sentence nobody says.
   */
  voiceClip: async (text: string, signal?: AbortSignal): Promise<Blob | null> => {
    try {
      const base = import.meta.env.VITE_API_URL || '/api';
      const token = localStorage.getItem('token');
      const res = await fetch(`${base}/voice/speak`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', ...(token ? { Authorization: `Bearer ${token}` } : {}) },
        body: JSON.stringify({ text }),
        signal,
      });
      if (!res.ok) return null;
      const clip = await res.blob();
      return clip.size > 0 ? clip : null;
    } catch {
      return null;
    }
  },

  assistantHistory: () =>
    request<{ success: boolean; chats: any[] }>('/assistant/history'),

  assistantNewChat: (body?: { page_context?: string }) =>
    request<{ success: boolean; chat_id: string }>('/assistant/new-chat', {
      method: 'POST',
      body: JSON.stringify(body || {}),
    }),

  assistantChatMessages: (chatId: string) =>
    request<{ success: boolean; messages: any[] }>(`/assistant/chat/${chatId}/messages`),

  assistantUploadUrl: (body: { files: Array<{ file_name: string; file_size: number; mime_type: string }> }) =>
    request<{ success: boolean; uploads: Array<{ file_name: string; signedUrl: string; storagePath: string; token: string }> }>('/assistant/upload-url', {
      method: 'POST',
      body: JSON.stringify(body),
    }),

  // Deal Advisor
  dealAdvisorAsk: (body: { property_id: string; message: string; chat_id?: string | null; deal_context: Record<string, any>; attachments?: any[] }) =>
    request<{ success: boolean; chat_id: string; message_id: string; message: string; suggestions: any[] }>('/deal-advisor/ask', {
      method: 'POST',
      body: JSON.stringify(body),
    }),

  dealAdvisorApplySuggestion: (body: { message_id: string }) =>
    request<{ success: boolean }>('/deal-advisor/apply-suggestion', {
      method: 'POST',
      body: JSON.stringify(body),
    }),

  dealAdvisorStarMessage: (body: { message_id: string; starred: boolean }) =>
    request<{ success: boolean; starred: boolean }>('/deal-advisor/star-message', {
      method: 'PATCH',
      body: JSON.stringify(body),
    }),

  dealAdvisorStarredMessages: (propertyId: string) =>
    request<{ success: boolean; messages: any[] }>(`/deal-advisor/starred/${propertyId}`),

  dealAdvisorHistory: (propertyId: string) =>
    request<{ success: boolean; chats: any[] }>(`/deal-advisor/history/${propertyId}`),

  dealAdvisorUploadUrl: (body: { files: Array<{ file_name: string; file_size: number; mime_type: string }> }) =>
    request<{ success: boolean; uploads: Array<{ file_name: string; signedUrl: string; storagePath: string; token: string }> }>('/deal-advisor/upload-url', {
      method: 'POST',
      body: JSON.stringify(body),
    }),

  dealAdvisorKeyConsiderations: (body: Record<string, any>) =>
    request<{
      success: boolean;
      narrative: string;
      generated_at: string;
      /** What the draft reached for that the record cannot support, and where it broke the
       *  house posture. REPORTED, never silently removed - cutting the sentence would delete
       *  the finding it carries. See docs/INVESTMENT-ANALYSIS-HOUSE-STYLE.md. */
      review?: { advocacy: string[]; attribution: string[] };
    }>('/deal-advisor/key-considerations', {
      method: 'POST',
      body: JSON.stringify(body),
    }),

  /** Turn a plain-language risk into a modellable scenario. The response carries LEVERS and
   *  labels, never modelled figures - the engine computes those, so the scenario page cannot
   *  disagree with the rest of the document. 422 means the model proposed something the engine
   *  cannot run, and the message says which. */
  buildScenario: (body: { risk_text: string; city?: string; property_type?: string; holding_period?: number }) =>
    request<{
      success: boolean;
      scenario: {
        title: string;
        assumptions: Array<{ label: string; lever: string; value: number; year?: number }>;
        rationale: string;
        notEstablished: string[];
        advocacy: string[];
        attribution: string[];
      };
      facts: Array<{ label: string; value: string | null; source: string }>;
      generated_at: string;
    }>('/deal-advisor/scenario', { method: 'POST', body: JSON.stringify(body) }),

  // AI Conversations (unified)
  aiConversations: (type?: string) =>
    request<{ success: boolean; conversations: any[] }>(`/ai/conversations${type ? `?type=${type}` : ''}`),

  aiConversationMessages: (type: string, chatId: string) =>
    request<{ success: boolean; messages: any[] }>(`/ai/conversations/${type}/${chatId}/messages`),

  // ── Data Pool ──

  getPoolStats: () =>
    request<any>('/data-pool/stats'),

  getDataHealth: () =>
    request<any>('/data-pool/health'),

  getVerificationQueue: (params?: Record<string, string>) => {
    const query = params ? '?' + new URLSearchParams(params).toString() : '';
    return request<any>(`/data-pool/verification-queue${query}`);
  },

  recomputeFreshness: () =>
    request<any>('/data-pool/recompute-freshness', { method: 'POST' }),

  searchTransactions: (params?: Record<string, string>) => {
    const query = params ? '?' + new URLSearchParams(params).toString() : '';
    return request<any>(`/data-pool/transactions${query}`);
  },

  getPooledComps: (subjectPropertyId: string) =>
    request<any>(`/data-pool/comps/${subjectPropertyId}`),

  addPooledComp: (subjectPropertyId: string, data: any) =>
    request<any>(`/data-pool/comps/${subjectPropertyId}`, {
      method: 'POST',
      body: JSON.stringify(data),
    }),

  linkExistingComp: (subjectPropertyId: string, data: any) =>
    request<any>(`/data-pool/comps/${subjectPropertyId}/link`, {
      method: 'POST',
      body: JSON.stringify(data),
    }),

  removeCompLink: (linkId: string) =>
    request<any>(`/data-pool/comp-links/${linkId}`, { method: 'DELETE' }),

  findNearbyProperties: (params: Record<string, string>) => {
    const query = new URLSearchParams(params).toString();
    return request<any>(`/data-pool/nearby?${query}`);
  },

  findNearbyComps: (propertyId: string, params?: Record<string, string>) => {
    const query = params ? '?' + new URLSearchParams(params).toString() : '';
    return request<any>(`/data-pool/nearby-comps/${propertyId}${query}`);
  },

  // AI comp finder: natural-language -> ranked comps.
  searchCompsAI: (body: { query?: string; subjectPropertyId?: string; limit?: number }) =>
    request<any>('/comp-finder/search', { method: 'POST', body: JSON.stringify(body) }),

  // Comp-adjusted valuation range + AI narrative for a property.
  compValuation: (body: { subjectPropertyId: string; withNarrative?: boolean }) =>
    request<any>('/comp-finder/valuation', { method: 'POST', body: JSON.stringify(body) }),

  // Richer comp data (property + latest transaction) for a set of ids.
  getCompData: (ids: string[]) =>
    request<any>(`/data-pool/comp-data?ids=${ids.join(',')}`),

  // Link many properties as comps to a subject property (from the map).
  bulkLinkComps: (subjectId: string, compPropertyIds: string[]) =>
    request<any>(`/data-pool/comps/${subjectId}/link-bulk`, {
      method: 'POST', body: JSON.stringify({ comp_property_ids: compPropertyIds }),
    }),

  getSubmarketAnalytics: (params?: Record<string, string>) => {
    const query = params ? '?' + new URLSearchParams(params).toString() : '';
    return request<any>(`/data-pool/analytics/submarkets${query}`);
  },

  getAuditTrail: (tableName: string, recordId: string) =>
    request<any>(`/data-pool/audit-trail/${tableName}/${recordId}`),

  // ── Market Intel ──

  listMarketIntel: (params?: Record<string, string>) => {
    const query = params ? '?' + new URLSearchParams(params).toString() : '';
    return request<any>(`/market-intel${query}`);
  },

  getMarketIntel: (id: string) =>
    request<any>(`/market-intel/${id}`),

  createMarketIntel: (data: any) =>
    request<any>('/market-intel', {
      method: 'POST',
      body: JSON.stringify(data),
    }),

  updateMarketIntel: (id: string, data: any) =>
    request<any>(`/market-intel/${id}`, {
      method: 'PATCH',
      body: JSON.stringify(data),
    }),

  deleteMarketIntel: (id: string) =>
    request<any>(`/market-intel/${id}`, { method: 'DELETE' }),

  verifyMarketIntel: (id: string) =>
    request<any>(`/market-intel/${id}/verify`, { method: 'POST' }),

  getIntelForProperty: (propertyId: string) =>
    request<any>(`/market-intel/for-property/${propertyId}`),

  // ── Master Property Verification ──

  verifyProperty: (propertyId: string) =>
    request<any>(`/master-properties/${propertyId}/verify`, { method: 'POST' }),

  // ══════════════════════════════════════════════════════════════
  // Intelligence Hub
  // ══════════════════════════════════════════════════════════════

  // ── County Transfers ──

  listCountyTransfers: (params?: Record<string, string>) =>
    request<any>(`/county-transfers?${new URLSearchParams(params || {})}`),

  getCountyTransferStats: () =>
    request<any>('/county-transfers/stats'),

  importCountyTransfers: (data: { rows: Record<string, string>[]; county: string }) =>
    request<any>('/county-transfers/import', {
      method: 'POST',
      body: JSON.stringify(data),
    }),

  pushTransferToPool: (id: string) =>
    request<any>(`/county-transfers/${id}/push-to-pool`, { method: 'POST' }),

  batchPushTransfers: (ids: string[]) =>
    request<any>('/county-transfers/batch-push', {
      method: 'POST',
      body: JSON.stringify({ ids }),
    }),

  // ── Permits ──

  listPermits: (params?: Record<string, string>) =>
    request<any>(`/permits?${new URLSearchParams(params || {})}`),

  getMapPermits: (params?: Record<string, string>) =>
    request<any>(`/permits/map?${new URLSearchParams(params || {})}`),

  getMapTenantProspects: () => request<any>('/tenant-prospects/map'),
  pushTenantToGhl: (id: string) => request<any>(`/tenant-prospects/${id}/push-to-ghl`, { method: 'POST' }),

  // ── Saved map searches ──
  listSavedSearches: () => request<any>('/saved-searches'),
  createSavedSearch: (name: string, criteria: unknown) =>
    request<any>('/saved-searches', { method: 'POST', body: JSON.stringify({ name, criteria }) }),
  updateSavedSearch: (id: string, patch: { name?: string; criteria?: unknown }) =>
    request<any>(`/saved-searches/${id}`, { method: 'PATCH', body: JSON.stringify(patch) }),
  deleteSavedSearch: (id: string) =>
    request<any>(`/saved-searches/${id}`, { method: 'DELETE' }),

  getPermit: (id: string) =>
    request<any>(`/permits/${id}`),

  createPermit: (data: Record<string, unknown>) =>
    request<any>('/permits', {
      method: 'POST',
      body: JSON.stringify(data),
    }),

  updatePermit: (id: string, data: Record<string, unknown>) =>
    request<any>(`/permits/${id}`, {
      method: 'PATCH',
      body: JSON.stringify(data),
    }),

  deletePermit: (id: string) =>
    request<any>(`/permits/${id}`, { method: 'DELETE' }),

  importPermits: (data: { rows: Record<string, string>[]; jurisdiction: string }) =>
    request<any>('/permits/import', {
      method: 'POST',
      body: JSON.stringify(data),
    }),

  getPermitStats: () =>
    request<any>('/permits/stats'),

  scanPermitPDF: async (file: File, jurisdiction: string, save = false) => {
    const token = localStorage.getItem('token');
    const formData = new FormData();
    formData.append('file', file);
    formData.append('jurisdiction', jurisdiction);
    const res = await fetch(`${API_BASE}/permits/scan-pdf?save=${save}`, {
      method: 'POST',
      headers: { ...(token && { Authorization: `Bearer ${token}` }) },
      body: formData,
    });
    const data = await res.json();
    if (!res.ok) throw new APIError(res.status, data.error || 'Scan failed');
    return data;
  },

  scanPermitURL: (url: string, jurisdiction: string, save = false) =>
    request<any>(`/permits/scan-url?save=${save}`, {
      method: 'POST',
      body: JSON.stringify({ url, jurisdiction }),
    }),

  scrapePermitPortal: (url: string, jurisdiction: string, save = false) =>
    request<any>(`/permits/scrape-portal?save=${save}`, {
      method: 'POST',
      body: JSON.stringify({ url, jurisdiction }),
    }),

  // ── Dev Intel (PermitStack feed) ──

  searchLivePermits: (params: { city?: string; address?: string; date_after?: string }) =>
    request<any>(`/dev-intel/permits/search?${new URLSearchParams(
      Object.fromEntries(Object.entries(params).filter(([, v]) => v)) as Record<string, string>)}`),

  permitIngestNow: () =>
    request<any>('/dev-intel/ingest/permitstack', { method: 'POST' }),

  permitIngestArcgis: () =>
    request<any>('/dev-intel/ingest/arcgis', { method: 'POST' }),

  permitRunAlerts: () =>
    request<any>('/dev-intel/permits/run-alerts', { method: 'POST' }),

  // ── Trade-area demographics ──
  getDemographics: (lat: number, lng: number, radii: number[]) =>
    request<any>(`/demographics?${new URLSearchParams({ lat: String(lat), lng: String(lng), radii: radii.join(',') })}`),

  // Drive-time trade-area demographics (Mapbox isochrones + ACS).
  getDriveTimeDemographics: (lat: number, lng: number, minutes: number[]) =>
    request<any>(`/demographics/drive-time?${new URLSearchParams({ lat: String(lat), lng: String(lng), minutes: minutes.join(',') })}`),

  // Premium (paid, on-demand) Esri consumer spending + Tapestry segmentation.
  getSpending: (lat: number, lng: number, radii: number[]) =>
    request<any>(`/demographics/spending?${new URLSearchParams({ lat: String(lat), lng: String(lng), radii: radii.join(',') })}`),
  getSpendingForPolygons: (polygons: { minutes?: number; geometry: any }[]) =>
    request<any>('/demographics/spending-polygon', {
      method: 'POST',
      body: JSON.stringify({ polygons }),
    }),
  // Mapbox static trade-area map (base64 PNG) for the Trade Area Report PDF.
  getStaticMap: (lat: number, lng: number, radii: number[]) =>
    request<{ image: string }>(`/demographics/static-map?${new URLSearchParams({ lat: String(lat), lng: String(lng), radii: radii.join(',') })}`),

  // Distress: loan detail for a property + push owners to GHL for outreach.
  getPropertyLoans: (propertyId: string) =>
    request<any>(`/property-loans/property/${propertyId}`),
  pushLoansToGhl: (propertyIds: string[]) =>
    request<any>('/property-loans/push-to-ghl', { method: 'POST', body: JSON.stringify({ propertyIds }) }),

  permitIngestStatus: () =>
    request<any>('/dev-intel/ingest/status'),

  // ── CRM Intelligence ──

  getCRMAlerts: (params?: Record<string, string>) =>
    request<any>(`/crm-intelligence/alerts?${new URLSearchParams(params || {})}`),

  dismissCRMAlert: (id: string) =>
    request<any>(`/crm-intelligence/alerts/${id}/dismiss`, { method: 'POST' }),

  recomputeCRMScores: () =>
    request<any>('/crm-intelligence/recompute', { method: 'POST' }),

  getPipelineHealth: () =>
    request<any>('/crm-intelligence/pipeline-health'),

  getPriorityLeaderboard: (limit?: number) =>
    request<any>(`/crm-intelligence/priority-leaderboard?limit=${limit || 20}`),

  getAtRiskDeals: () =>
    request<any>('/crm-intelligence/at-risk-deals'),

  // ── News Intelligence ──

  listNewsItems: (params?: Record<string, string>) =>
    request<any>(`/news-intel?${new URLSearchParams(params || {})}`),

  fetchAllNewsSources: () =>
    request<any>('/news-intel/fetch-all', { method: 'POST' }),

  fetchNewsSource: (id: string) =>
    request<any>(`/news-intel/sources/${id}/fetch`, { method: 'POST' }),

  listNewsSources: () =>
    request<any>('/news-intel/sources'),

  addNewsSource: (data: Record<string, unknown>) =>
    request<any>('/news-intel/sources', {
      method: 'POST',
      body: JSON.stringify(data),
    }),

  updateNewsSource: (id: string, data: Record<string, unknown>) =>
    request<any>(`/news-intel/sources/${id}`, {
      method: 'PATCH',
      body: JSON.stringify(data),
    }),

  deleteNewsSource: (id: string) =>
    request<any>(`/news-intel/sources/${id}`, { method: 'DELETE' }),

  toggleNewsBookmark: (id: string) =>
    request<any>(`/news-intel/${id}/bookmark`, { method: 'POST' }),

  dismissNewsItem: (id: string) =>
    request<any>(`/news-intel/${id}/dismiss`, { method: 'POST' }),

  pushNewsToMarketIntel: (id: string) =>
    request<any>(`/news-intel/${id}/push-to-intel`, { method: 'POST' }),

  // ── CRE Signal Radar ──

  runSignalScan: () =>
    request<any>('/signal-radar/scan', { method: 'POST' }),

  listSignals: (params?: Record<string, string>) =>
    request<any>(`/signal-radar/signals?${new URLSearchParams(params || {})}`),

  listScanRuns: (limit = 10) =>
    request<any>(`/signal-radar/runs?limit=${limit}`),

  updateSignal: (id: string, status: string) =>
    request<any>(`/signal-radar/signals/${id}`, {
      method: 'PATCH',
      body: JSON.stringify({ status }),
    }),

  // ── Imagery / Street Analysis ──

  getStreetMetadata: (address: string) =>
    request<any>(`/imagery/street-metadata?address=${encodeURIComponent(address)}`),

  /**
   * OO2a: the Street View image for an address, as a blob object URL.
   *
   * ⭐ It goes through OUR backend, not straight to Google, for two reasons: the Static endpoint
   * needs the API key IN THE URL (so a client-built URL publishes the key), and the frontend has
   * no maps key in local dev - a client-built URL would render nothing wherever one is missing.
   * The proxy is authenticated, so this fetches with the token and hands back an object URL.
   * ⚠️ The CALLER owns the object URL and must `URL.revokeObjectURL` it.
   */
  getStreetViewImage: async (address: string, w = 960, h = 540): Promise<string> => {
    const token = localStorage.getItem('token');
    const base = import.meta.env.VITE_API_URL || '/api';
    const res = await fetch(
      `${base}/imagery/street-view?address=${encodeURIComponent(address)}&w=${w}&h=${h}`,
      { headers: { ...(token && { Authorization: `Bearer ${token}` }) } },
    );
    if (!res.ok) throw new Error('Street View image unavailable');
    return URL.createObjectURL(await res.blob());
  },

  getVacancySignals: (address: string) =>
    request<any>(`/imagery/vacancy-signals?address=${encodeURIComponent(address)}`),

  runStreetAnalysis: (propertyId: string, address: string) =>
    request<any>(`/imagery/analyze/${propertyId}`, {
      method: 'POST',
      body: JSON.stringify({ address }),
    }),

  getAnalysisHistory: (propertyId: string) =>
    request<any>(`/imagery/history/${propertyId}`),

  getMapProperties: (params?: Record<string, string>) =>
    request<any>(`/imagery/map-properties?${new URLSearchParams(params || {})}`),

  // ── GHL Sync ──

  listOpportunities: (params?: Record<string, string>) => {
    const query = params ? '?' + new URLSearchParams(params).toString() : '';
    return request<any>(`/ghl-sync/opportunities${query}`);
  },

  getOpportunity: (id: string) =>
    request<any>(`/ghl-sync/opportunities/${id}`),

  updateOpportunity: (id: string, body: Record<string, unknown>) =>
    request<any>(`/ghl-sync/opportunities/${id}`, { method: 'PATCH', body: JSON.stringify(body) }),

  boardOpportunities: (pipelineId: string, status = 'open') =>
    request<any>(`/ghl-sync/opportunities/board?pipeline_id=${encodeURIComponent(pipelineId)}&status=${encodeURIComponent(status)}`),

  getContactNotes: (ghlId: string) =>
    request<any>(`/ghl-sync/contacts/${ghlId}/notes`),

  addContactNote: (ghlId: string, body: string) =>
    request<any>(`/ghl-sync/contacts/${ghlId}/notes`, { method: 'POST', body: JSON.stringify({ body }) }),

  suggestPersona: (ghlId: string) =>
    request<any>(`/ghl-sync/contacts/${ghlId}/persona/suggest`, { method: 'POST' }),

  setPersona: (ghlId: string, body: Record<string, unknown>) =>
    request<any>(`/ghl-sync/contacts/${ghlId}/persona`, { method: 'PATCH', body: JSON.stringify(body) }),

  seedBoardPersonas: (pipelineId: string) =>
    request<any>(`/ghl-sync/opportunities/board/persona-seed`, { method: 'POST', body: JSON.stringify({ pipeline_id: pipelineId }) }),

  triggerGHLSync: () =>
    request<any>('/ghl-sync/trigger', { method: 'POST' }),

  getGHLSyncStatus: () =>
    request<any>('/ghl-sync/status'),

  listGHLConflicts: (resolution?: string) =>
    request<any>(`/ghl-sync/conflicts?resolution=${resolution || 'pending'}`),

  resolveGHLConflict: (id: string, resolution: string) =>
    request<any>(`/ghl-sync/conflicts/${id}/resolve`, {
      method: 'POST',
      body: JSON.stringify({ resolution }),
    }),

  listGHLCrossrefs: (params?: Record<string, string>) =>
    request<any>(`/ghl-sync/crossref?${new URLSearchParams(params || {})}`),

  listConversations: (params?: Record<string, string>) => {
    const query = params ? '?' + new URLSearchParams(params).toString() : '';
    return request<any>(`/ghl-sync/conversations${query}`);
  },

  getConversationMessages: (id: string) =>
    request<any>(`/ghl-sync/conversations/${id}/messages`),

  // CONV2 (c): `auto` is the page's automatic refresh - the server syncs only if its copy is stale (one sync per
  // 20 s for every open tab together). His Refresh button sends no `auto` and always syncs.
  syncConversations: (mode: 'backfill' | 'incremental' = 'incremental', opts: { auto?: boolean } = {}) =>
    request<any>(`/ghl-sync/conversations/sync`, {
      method: 'POST',
      body: JSON.stringify(opts.auto ? { mode, auto: true } : { mode }),
    }),

  sendConversationMessage: (
    id: string,
    body: { channel: string; message: string; subject?: string; idempotencyKey?: string; fromNumber?: string }
  ) =>
    request<any>(`/ghl-sync/conversations/${id}/messages`, { method: 'POST', body: JSON.stringify(body) }),

  getContactDeals: (ghlId: string) =>
    request<any>(`/ghl-sync/contacts/${ghlId}/deals`),

  getGhlContact: (ghlId: string) =>
    request<any>(`/ghl-sync/contacts/${ghlId}`),

  /**
   * TODO1: with `todoTaskId`, a draft for one of his to-dos. ⛔ Only the id is sent - the server builds what the message
   * must do from HIS task, and answers "To-do not found" for anyone else's.
   */
  draftConversationReply: (id: string, channel: string, todoTaskId?: string) =>
    request<any>(`/ghl-sync/conversations/${id}/draft`, {
      method: 'POST',
      body: JSON.stringify(todoTaskId ? { channel, todo_task_id: todoTaskId } : { channel }),
    }),

  getEmailBody: (emailId: string) =>
    request<any>(`/ghl-sync/conversations/email/${emailId}`),

  setContactTags: (ghlId: string, body: { add?: string[]; remove?: string[] }) =>
    request<any>(`/ghl-sync/contacts/${ghlId}/tags`, { method: 'POST', body: JSON.stringify(body) }),

  suggestConversationTags: (id: string) =>
    request<any>(`/ghl-sync/conversations/${id}/suggest-tags`, { method: 'POST', body: JSON.stringify({}) }),

  // ── ATTOM Enrichment ──

  getATTOMStatus: () =>
    request<any>('/attom/status'),

  getATTOMData: (propertyId: string) =>
    request<any>(`/attom/${propertyId}`),

  enrichWithATTOM: (propertyId: string) =>
    request<any>(`/attom/enrich/${propertyId}`, { method: 'POST' }),

  enrichBatchATTOM: (propertyIds: string[]) =>
    request<any>('/attom/enrich-batch', {
      method: 'POST',
      body: JSON.stringify({ property_ids: propertyIds }),
    }),

  // ── Property Imports (source-aware merge engine) ──

  propertyImportDryRun: (payload: {
    source_type: string;
    source_date: string;
    source_date_is_estimate?: boolean;
    file_name?: string;
    file_contents_base64?: string;
    records: Array<{
      apn: string | null;
      county: string | null;
      address: string;
      city: string;
      state: string;
      fields: Record<string, string | number | null>;
    }>;
  }) =>
    request<{
      success: boolean;
      upload_id: string;
      duplicate_upload: boolean;
      report: {
        created: { record_index: number; apn: string | null; address: string }[];
        updated: { property_id: string; changed_fields: { field: string; from: string | null; to: string | null }[] }[];
        blocked: { property_id: string; field: string; incoming_value: string | null; existing_value: string | null; reason: string }[];
        flagged: { property_id: string; field: string; conflict_type: string; incoming_value: string | null; existing_value: string | null }[];
        skipped_older: { property_id: string; field: string }[];
        no_change: number;
        duplicate_upload: boolean;
      };
    }>('/property-imports/dry-run', {
      method: 'POST',
      body: JSON.stringify(payload),
    }),

  propertyImportCommit: (payload: {
    upload_id: string;
    records: Array<{
      apn: string | null;
      county: string | null;
      address: string;
      city: string;
      state: string;
      fields: Record<string, string | number | null>;
    }>;
  }) =>
    request<{ success: boolean; upload_id: string; report: any }>('/property-imports/commit', {
      method: 'POST',
      body: JSON.stringify(payload),
    }),

  // ── Lead Watcher (Needs Attention) ──

  listLeadFlags: (params?: Record<string, string>) => {
    const query = params ? '?' + new URLSearchParams(params).toString() : '';
    return request<any>(`/ghl-sync/lead-flags${query}`);
  },

  refreshLeadFlags: () => request<any>('/ghl-sync/lead-flags/refresh', { method: 'POST' }),

  dismissLeadFlag: (id: string) => request<any>(`/ghl-sync/lead-flags/${id}/dismiss`, { method: 'POST' }),

  // ── Investor Profiles & Deal Brief Parsing ──

  investorProfiles: () =>
    request<{ success: boolean; profiles: InvestorProfile[] }>('/investor-profiles'),

  createInvestorProfile: (body: Partial<InvestorProfile>) =>
    request<{ success: boolean; profile: InvestorProfile }>('/investor-profiles', {
      method: 'POST',
      body: JSON.stringify(body),
    }),

  updateInvestorProfile: (id: string, body: Partial<InvestorProfile>) =>
    request<{ success: boolean; profile: InvestorProfile }>(`/investor-profiles/${id}`, {
      method: 'PATCH',
      body: JSON.stringify(body),
    }),

  // `confidence` is Record<string, number> (0-1), not the strings the model sometimes
  // emits - briefParserService normalizes words like "high" to 0.9 before responding.
  parseDealBrief: (brief: string) =>
    request<{
      success: boolean;
      archetype: string | null;
      capital_structure: string | null;
      down_payment_target: number | null;
      investor_profile_id: string | null;
      is_1031: boolean;
      holding_period: number | null;
      /** Added 2026-08-27. Null when the brief did not mention one, in which case the screen
       *  keeps its own derived default - going-in cap plus an age buffer, which is Joseph's
       *  own rule and correct. */
      exit_cap_rate: number | null;
      confidence: Record<string, number>;
      unresolved: string[];
      reasoning: string;
    }>('/deal-analysis/parse-brief', {
      method: 'POST',
      body: JSON.stringify({ brief }),
    }),

  // ── Property Threads ──
  // subject is nullable: the underlying property/deal can fail to resolve (deleted, or
  // belongs to another org) - see subjectResolver.ts on the backend.
  getThreadInbox: () =>
    request<{
      success: boolean;
      threads: Array<{
        id: string;
        subject_type: SubjectType;
        subject_id: string;
        title: string | null;
        created_by: string;
        created_at: string;
        last_message_at: string;
        unread: number;
        muted: boolean;
        subject: { type: SubjectType; id: string; label: string; city: string | null; href: string } | null;
        // Newest message on the thread, already truncated server-side. Null only when
        // nobody has posted yet. Hand-synced with InboxPreview in threadService.ts.
        last_message: {
          body: string;
          kind: 'human' | 'ai_answer' | 'ai_volunteer';
          author_id: string | null;
          created_at: string;
        } | null;
      }>;
    }>('/threads'),

  // --- Zoning rules (land-play radar, Layer 2) ---
  /**
   * `use_cases` is SERVED rather than duplicated here. `use_case` is free text server-side, so a
   * rule saved as `truck_terminal` and looked up as `truck_yard` returns nothing - and nothing
   * reads as "nobody has researched this", not as a typo. A second hardcoded list in the client
   * is how the two drift apart silently.
   */
  getZoningRules: (params?: { city?: string; code?: string; use_case?: string }) => {
    const q = params ? '?' + new URLSearchParams(params as Record<string, string>).toString() : '';
    return request<{
      success: boolean; rules: any[]; permissions: string[];
      use_cases: string[]; status_notes: string[];
    }>(
      `/zoning-rules${q}`
    );
  },

  getZoningCoverage: (city?: string) =>
    request<{
      success: boolean;
      coverage: Array<{
        city: string;
        code: string;
        property_count: number;
        rule_count: number;
        approved_count: number;
      }>;
    }>(`/zoning-rules/coverage${city ? `?city=${encodeURIComponent(city)}` : ''}`),

  /**
   * ⚠️ THREE states, and collapsing any two hides something that changes what you do:
   *
   *   'none'            nobody has looked. Go research it.
   *   'pending_review'  drafted, unsigned. `rule` is null - it says NOTHING - but do not
   *                     re-research it, someone already has.
   *   'approved'        signed by a human. Usable.
   *
   * `researched` is true ONLY for 'approved'. And note that `permission: 'unknown'` inside an
   * approved rule is a different statement again: we read the code and could not tell.
   */
  lookupZoningRule: (city: string, zoning: string, useCase: string) =>
    request<{
      success: boolean;
      rule: any | null;
      status: 'none' | 'pending_review' | 'approved';
      researched: boolean;
    }>(
      `/zoning-rules/lookup?city=${encodeURIComponent(city)}&zoning=${encodeURIComponent(zoning)}&use_case=${encodeURIComponent(useCase)}`
    ),

  /**
   * ZS2 - every use case for one parcel's zone, in ONE call. What the property page renders.
   *
   * ⛔ A HELD RULE ARRIVES AS ITS QUESTION, NOT ITS ANSWER. `entries[].held` carries only
   * `status_note`; the server does not put `permission` or `conditions` in the payload at all,
   * because every held rule is a by-right-with-a-caveat and a wrong yes is the one that reaches
   * an owner. There is nothing here for the UI to accidentally render.
   */
  /**
   * DOC0. The documents filed on a property, and opening one.
   *
   * ⛔ THE PROPERTY IS IN THE PATH, not a query parameter, because it is part of the
   * authorization: the server refuses any document that property does not resolve, with the same
   * words it uses for one that does not exist. `docId` is source-qualified (`filing:<uuid>` /
   * `legacy:<uuid>`) and encoded, since it carries a colon.
   */
  /**
   * DOC2. Put a document on a Property Database record.
   *
   * ⛔ A SEPARATE FLOW FROM `uploadDocumentWithBuffer`, and the schema is why:
   * `documents.property_id` is FOREIGN-KEYED to `properties`, so the legacy table refuses a
   * `master_properties` id outright (a 23503, returned as a 500 AFTER the bytes are uploaded).
   * This registers the object into `files` + `file_links` instead, which is where the whole
   * document programme is heading anyway.
   */
  uploadMasterPropertyDocument: async (
    masterPropertyId: string,
    fileData: { name: string; size: number; type: string; buffer: ArrayBuffer },
    documentType?: string
  ) => {
    const token = localStorage.getItem('token');
    if (!token) {
      window.location.href = '/login';
      throw new APIError(401, 'Please log in to upload documents.');
    }
    const auth = { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` };

    const urlRes = await fetch(`${API_BASE}/documents/upload-url`, {
      method: 'POST', headers: auth,
      body: JSON.stringify({ file_name: fileData.name, file_size: fileData.size }),
    });
    if (!urlRes.ok) throw new APIError(urlRes.status, 'Could not start the upload.');
    const { upload_url, storage_path } = await urlRes.json();

    const put = await fetch(upload_url, {
      method: 'PUT',
      headers: { 'Content-Type': fileData.type || 'application/pdf' },
      body: fileData.buffer,
    });
    if (!put.ok) throw new APIError(put.status, 'The file could not be uploaded to storage.');

    return request<{ success: boolean; file_id: string }>(
      `/master-properties/${masterPropertyId}/documents`,
      { method: 'POST', body: JSON.stringify({
          storage_path, file_name: fileData.name, document_type: documentType }) }
    );
  },

  /**
   * DOC1 - the org-wide library. ⚠️ `/files`, not `/documents`: `documents` is the GENERATOR's
   * namespace and the legacy table's, and conflating the two is what made the nav's "Documents"
   * open a generator.
   */
  /**
   * E1 retention: what is about to drop out of the review queue. ⭐ Computed on request, so it is
   * always current and there is no cron. `warning` is null when there is nothing to say.
   */
  libraryRetention: () =>
    request<{ success: boolean; data: { warning: string | null; ageing: number; due: number; keptForGood: number } }>(
      '/files/retention'
    ),

  listLibraryFiles: (q: {
    search?: string; targetType?: string; targetId?: string; documentType?: string;
    needsAHome?: boolean; limit?: number; offset?: number;
  } = {}) => {
    const p = new URLSearchParams();
    if (q.search) p.set('search', q.search);
    if (q.targetType) p.set('target_type', q.targetType);
    // FV: one record's folder - the id alone, whichever surface filed the document.
    if (q.targetId) p.set('target_id', q.targetId);
    if (q.documentType) p.set('document_type', q.documentType);
    if (q.needsAHome) p.set('needs_a_home', 'true');
    if (q.limit != null) p.set('limit', String(q.limit));
    if (q.offset != null) p.set('offset', String(q.offset));
    const qs = p.toString();
    return request<{ success: boolean; files: unknown[]; total: number }>(
      `/files${qs ? `?${qs}` : ''}`
    );
  },

  /**
   * FV - the folder view's map of the library: places, the records inside each, kinds - every
   * one with the count of documents it holds. "like a mac almost" (Joseph, 2026-09-08).
   */
  listLibraryFolders: () =>
    request<{
      success: boolean;
      total: number;
      needsAHome: number;
      places: Array<{
        place: 'property' | 'master_property' | 'deal' | 'portal' | 'contact' | 'company';
        documents: number;
        folders: Array<{
          targetType: string; targetId: string; label: string; resolved: boolean; count: number;
        }>;
      }>;
      kinds: Array<{ key: string; count: number }>;
    }>('/files/folders'),

  downloadLibraryFile: (fileId: string) =>
    request<{ success: boolean; url: string; filename: string }>(`/files/${fileId}/download`),

  /** LOI-PDF1b (the 130th): a letter in the library as a real PDF made from its Word file. The download's gate. */
  libraryFilePdf: (fileId: string) =>
    request<{ success: boolean; url: string; filename: string }>(`/files/${fileId}/pdf`),

  /**
   * DOC1b - the records a document could be filed on.
   * ⛔ A SEARCH, never a suggestion: measured, the house number `1101` alone matches five
   * buildings in the Property Database. The caller shows candidates; a human picks one.
   */
  searchFilingTargets: (kind: 'property' | 'master_property' | 'deal', q: string) =>
    request<{
      success: boolean;
      candidates: Array<{
        targetType: string; targetId: string; name: string;
        detail: string | null; documentCount: number;
      }>;
    }>(`/files/targets?kind=${kind}&q=${encodeURIComponent(q)}`),

  fileDocument: (
    fileId: string,
    body: { targetType: string; targetId: string; category?: string }
  ) =>
    request<{ success: boolean; linkId: string }>(`/files/${fileId}/links`, {
      method: 'POST', body: JSON.stringify(body),
    }),

  /** Takes a filing back off. ⭐ The document and its bytes STAY - unlink, never hard-delete. */
  unfileDocument: (fileId: string, linkId: string) =>
    request<{ success: boolean }>(`/files/${fileId}/links/${linkId}`, { method: 'DELETE' }),

  listPropertyDocuments: (kind: 'deal' | 'master', propertyId: string) =>
    request<{ success: boolean; documents: unknown[] }>(
      `/${kind === 'deal' ? 'properties' : 'master-properties'}/${propertyId}/documents`
    ),

  downloadPropertyDocument: (kind: 'deal' | 'master', propertyId: string, docId: string) =>
    request<{ success: boolean; url: string; filename: string }>(
      `/${kind === 'deal' ? 'properties' : 'master-properties'}/${propertyId}` +
      `/documents/${encodeURIComponent(docId)}/download`
    ),

  /**
   * MOB4: a property's filed document rendered for the app's own viewer - the Deal Room's preview,
   * reached from a property page. ⛔ Same gate as the download above; the server resolves both alike.
   * Driven for real in `api.propertyPreview.test.ts` (every component test mocks this module).
   */
  previewPropertyDocument: (kind: 'deal' | 'master', propertyId: string, docId: string) =>
    request<{ success: boolean; preview: import('../types').DocumentPreview }>(
      `/${kind === 'deal' ? 'properties' : 'master-properties'}/${propertyId}` +
      `/documents/${encodeURIComponent(docId)}/preview`
    ),

  /**
   * LOI-PDF1 (the 129th): a filed letter as a real PDF made from its Word file - header, footer and all. Answers a
   * signed link for a window opened inside the tap; the first open of a letter makes the PDF (a few seconds).
   * ⛔ Same gate as the download and the preview above. A refusal carries words for him (no PDF for this document,
   * no converter on this server) and the page then opens the Word file instead.
   */
  propertyDocumentPdf: (kind: 'deal' | 'master', propertyId: string, docId: string) =>
    request<{ success: boolean; url: string; filename: string }>(
      `/${kind === 'deal' ? 'properties' : 'master-properties'}/${propertyId}` +
      `/documents/${encodeURIComponent(docId)}/pdf`
    ),

  // FZ - feasibility and land upside. The answers are STORED server-side; nothing here computes one.
  getPropertyFeasibility: (propertyId: string) =>
    request<{ success: boolean; rows: FeasibilityRow[] }>(`/feasibility/property/${encodeURIComponent(propertyId)}`),
  getLandUpside: (q: LandUpsideQuery) =>
    request<{ success: boolean; rows: LandUpsideRow[]; total: number; coverage: FeasibilityCoverage }>(
      `/feasibility/land-upside?${landUpsideParams(q)}`,
    ),
  getFeasibilityAssumptions: () =>
    request<{ success: boolean; assumptions: Assumption[]; history: AssumptionChange[] }>('/feasibility/assumptions'),
  updateFeasibilityAssumption: (key: string, value: number, note?: string | null) =>
    request<{ success: boolean; assumption: Assumption }>(`/feasibility/assumptions/${encodeURIComponent(key)}`, {
      method: 'PATCH', body: JSON.stringify({ value, note: note ?? null }),
    }),
  getZoneResearch: (city: string) =>
    request<{ success: boolean; standards: ZoneStandardView[]; notices: ZoneNoticeView[]; questions: PlanningQuestion[] }>(
      `/feasibility/research?city=${encodeURIComponent(city)}`,
    ),
  recomputeFeasibility: (jurisdiction?: string) =>
    request<{ success: boolean; summary: { considered: number; written: number; byApproval: Record<string, number> } }>(
      '/feasibility/recompute', { method: 'POST', body: JSON.stringify({ jurisdiction: jurisdiction ?? null }) },
    ),

  lookupParcelZoning: (city: string, zoning?: string | null, mapped = false) =>
    request<{
      success: boolean;
      city: string;
      code: string | null;
      entries: Array<{
        use_case: string;
        status: 'none' | 'pending_review' | 'approved';
        rule: any | null;
        held: { use_case: string; status_note: string | null } | null;
      }>;
      use_cases: string[];
      status_notes: string[];
    }>(
      `/zoning-rules/parcel?city=${encodeURIComponent(city)}${zoning ? `&zoning=${encodeURIComponent(zoning)}` : ''}${mapped ? '&mapped=1' : ''}`
    ),

  /**
   * Withdraw a rule that is wrong on a real parcel. Returns it to draft immediately, so it stops
   * answering everywhere the moment it is doubted.
   *
   * ⚠️ NOT admin-gated, unlike approve, and the asymmetry is the point: starting an answer takes
   * a signature, stopping a suspect one should not.
   */
  flagZoningRule: (id: string, note: string) =>
    request<{ success: boolean; rule: any }>(`/zoning-rules/${id}/flag`, {
      method: 'POST',
      body: JSON.stringify({ note }),
    }),

  saveZoningRule: (rule: Record<string, unknown>) =>
    request<{ success: boolean; rule: any }>('/zoning-rules', {
      method: 'POST',
      body: JSON.stringify(rule),
    }),

  /** The sign-off. Until this runs, the rules answer nothing. Admin only. */
  approveZoningRules: (target: { city?: string; ids?: string[] }) =>
    request<{ success: boolean; approved: number }>('/zoning-rules/approve', {
      method: 'POST',
      body: JSON.stringify(target),
    }),

  deleteZoningRule: (id: string) =>
    request<{ success: boolean }>(`/zoning-rules/${id}`, { method: 'DELETE' }),

  getUnreadCount: () => request<{ success: boolean; count: number }>('/threads/unread-count'),

  // OO1a: the sidebar's four counts. Every value is `number | null`; null means the count
  // could not be read, and the nav prints nothing rather than a wrong number.
  getNavCounts: () => request<{ success: boolean; counts: NavCounts }>('/nav-counts'),

  getThreadsForSubject: (type: SubjectType, id: string) =>
    request<{
      success: boolean;
      threads: Array<{
        id: string;
        subject_type: SubjectType;
        subject_id: string;
        title: string | null;
        created_by: string;
        created_at: string;
        last_message_at: string;
      }>;
    }>(`/threads/subject/${type}/${id}`),

  // The get-or-create counterpart of getThreadsForSubject above, at the same path: resolves
  // to the existing thread for a subject, or creates one if there is none yet. This is what
  // ThreadPanel calls rather than listing and then deciding for itself whether to create -
  // see getOrCreateThreadForSubject's doc comment in backend/src/services/threadService.ts
  // for the race that decision client-side used to allow.
  getOrCreateThreadForSubject: (type: SubjectType, id: string) =>
    request<{
      success: boolean;
      thread: {
        id: string;
        subject_type: SubjectType;
        subject_id: string;
        title: string | null;
        created_by: string;
        created_at: string;
        last_message_at: string;
      };
    }>(`/threads/subject/${type}/${id}`, { method: 'POST' }),

  // Always creates a new thread, unconditionally - unlike getOrCreateThreadForSubject
  // above, most callers want that one instead. Kept for a possible future "start another
  // thread on this property" action; not used by ThreadPanel today.
  createThread: (subjectType: SubjectType, subjectId: string, title?: string) =>
    request<{
      success: boolean;
      thread: {
        id: string;
        subject_type: SubjectType;
        subject_id: string;
        title: string | null;
        created_by: string;
        created_at: string;
        last_message_at: string;
      };
    }>('/threads', {
      method: 'POST',
      body: JSON.stringify({ subject_type: subjectType, subject_id: subjectId, title }),
    }),

  // author_id is null when the AI posts (kind 'ai_answer' | 'ai_volunteer'), not just when
  // a user account is deleted - keep it string | null rather than widening to string.
  getThreadMessages: (threadId: string) =>
    request<{
      success: boolean;
      messages: Array<{
        id: string;
        thread_id: string;
        author_id: string | null;
        kind: 'human' | 'ai_answer' | 'ai_volunteer';
        body: string;
        suggestion_data: ThreadTaskSuggestion | null;
        applied: boolean;
        created_at: string;
      }>;
    }>(`/threads/${threadId}/messages`),

  postThreadMessage: (threadId: string, body: string) =>
    request<{
      success: boolean;
      message: {
        id: string;
        thread_id: string;
        author_id: string | null;
        kind: 'human' | 'ai_answer' | 'ai_volunteer';
        body: string;
        suggestion_data: ThreadTaskSuggestion | null;
        applied: boolean;
        created_at: string;
      };
    }>(`/threads/${threadId}/messages`, {
      method: 'POST',
      body: JSON.stringify({ body }),
    }),

  // Accept an AI task suggestion attached to a message. Mounted under /threads rather than
  // /messages - see postApplySuggestion in backend/src/controllers/threadController.ts.
  applySuggestion: (messageId: string) =>
    request<{
      success: boolean;
      task: { id: string; title: string; assigned_to: string | null; due_date: string | null };
    }>(`/threads/messages/${messageId}/apply-suggestion`, { method: 'POST' }),

  markThreadRead: (threadId: string) =>
    request<{ success: boolean }>(`/threads/${threadId}/read`, { method: 'POST' }),

  muteThread: (threadId: string, muted: boolean) =>
    request<{ success: boolean }>(`/threads/${threadId}/mute`, {
      method: 'PATCH',
      body: JSON.stringify({ muted }),
    }),

  // ==========================================================================
  // WT1 - walk-throughs (/api/tours). Contract: docs/planning/2026-09-23-wt1-build-plan.md section C.
  // Shapes are hand-mirrored in ../types/tours.ts. The PHONE's queue talks to these through
  // lib/tourTransport.ts; the photo bytes themselves never pass through here (a signed url takes them).
  // ==========================================================================

  /** Start a walk-through - only ever with signal, so the tour id is the SERVER's (the photo ids are the phone's). */
  createTour: (body: CreateTourBody) =>
    request<{ success: boolean; tour: TourRow }>('/tours', {
      method: 'POST',
      body: JSON.stringify(body),
    }),

  /** Shared across Apex (his ruling 2026-09-23), newest first. */
  listTours: (params: { master_property_id?: string; limit?: number } = {}) => {
    const q = new URLSearchParams();
    if (params.master_property_id) q.set('master_property_id', params.master_property_id);
    if (params.limit !== undefined) q.set('limit', String(params.limit));
    const qs = q.toString();
    return request<{ success: boolean; tours: TourSummary[] }>(`/tours${qs ? `?${qs}` : ''}`);
  },
  // LD1 Renewals (the 120th).
  /** M3: `ended` counts the agreements imported that are no longer live - "none live" is not "none imported". */
  listRenewals: () => request<{ success: boolean; renewals: RenewalRow[]; ended: number }>('/renewals'),
  getRenewal: (id: string) => request<{ success: boolean } & RenewalDetail>(`/renewals/${id}`),
  getRenewalDocument: (id: string) => request<{ success: boolean; url: string; page: number | null }>(`/renewals/${id}/document`),
  startRenewalDeal: (id: string) => request<{ success: boolean; deal_id: string; created: boolean }>(`/renewals/${id}/start-deal`, { method: 'POST' }),
  getRenewalBuildings: (id: string) => request<{ success: boolean; linked: string | null; candidates: MasterCandidate[] }>(`/renewals/${id}/buildings`),
  linkRenewalBuilding: (id: string, masterId: string) =>
    request<{ success: boolean; master_property_id: string }>(`/renewals/${id}/link-building`, { method: 'POST', body: JSON.stringify({ master_property_id: masterId }) }),
  /** DOCTRACK (D15): the agreements read in the app and not in Renewals yet, and the cards whose document reads differently. */
  listRenewalReads: () => request<{ success: boolean; reads: SignedReadItem[]; differs: SignedReadDiff[] }>('/renewals/reads'),
  /** DOCTRACK (D15): Apply one read on his tap, with his pick when it matched more than one agreement. */
  applyRenewalRead: (filingId: string, linkTo?: string) =>
    request<{ success: boolean } & ({ action: 'linked' | 'inserted'; agreementId: string } | SignedReadDecision | { do: 'not_a_signed_agreement' })>(
      `/renewals/reads/${filingId}/apply`, { method: 'POST', body: JSON.stringify(linkTo ? { link_to: linkTo } : {}) }),

  // LISTINGS-AI - his Current Listings and the exact words GoHighLevel's agents are given.
  listCurrentListings: () => request<CurrentListingsResponse>('/current-listings'),
  updateCurrentListing: (id: string, patch: CurrentListingPatch) =>
    request<{ success: boolean; listing: CurrentListingRow }>(`/current-listings/${encodeURIComponent(id)}`, { method: 'PATCH', body: JSON.stringify(patch) }),
  confirmCurrentListing: (id: string) =>
    request<{ success: boolean; listing: CurrentListingRow }>(`/current-listings/${encodeURIComponent(id)}/confirm`, { method: 'POST' }),

  /** NB1 - the market benchmark for a single-tenant deal; only the inputs that are set travel. */
  getNetLeaseBenchmark: (q: NetLeaseBenchmarkQuery) => request<{ success: boolean; benchmark: NetLeaseBenchmark }>(
    `/net-lease/benchmark?${new URLSearchParams(Object.entries(q).filter(([, v]) => v !== null && v !== undefined && v !== '').map(([k, v]) => [k, String(v)]))}`),

  getTour: (tourId: string) =>
    request<{ success: boolean; tour: TourDetail }>(`/tours/${encodeURIComponent(tourId)}`),

  /**
   * Notes, tag set, the phone's captured count, the cover photo, or the status. `status: 'complete'` is
   * refused with 409 `{ code: 'photos_outstanding', captured, uploaded }` while anything has not landed.
   */
  patchTour: (tourId: string, body: PatchTourBody) =>
    request<{ success: boolean; tour: TourRow }>(`/tours/${encodeURIComponent(tourId)}`, {
      method: 'PATCH',
      body: JSON.stringify(body),
    }),

  /**
   * A FRESH signed upload url for one photo. ⛔ Asked for at DRAIN time and never stored: the url expires,
   * and one fetched before walking into the building is dead by the time signal returns.
   */
  tourPhotoUploadUrl: (tourId: string, photoId: string, body: TourPhotoUploadUrlBody) =>
    request<{ success: boolean } & TourPhotoUploadUrl>(
      `/tours/${encodeURIComponent(tourId)}/photos/${encodeURIComponent(photoId)}/upload-url`,
      { method: 'POST', body: JSON.stringify(body) },
    ),

  /**
   * Record a photo whose bytes have landed. Idempotent on the phone-made id: a retry answers 200 with the
   * same row. 409 `{ code: 'upload_missing' }` when the bytes are not in storage - the queue re-PUTs.
   */
  saveTourPhoto: (tourId: string, photoId: string, body: SaveTourPhotoBody) =>
    request<{ success: boolean; photo: TourPhotoRow }>(
      `/tours/${encodeURIComponent(tourId)}/photos/${encodeURIComponent(photoId)}`,
      { method: 'PUT', body: JSON.stringify(body) },
    ),

  /** An edit after upload - the tag or the dictated line. */
  patchTourPhoto: (tourId: string, photoId: string, body: PatchTourPhotoBody) =>
    request<{ success: boolean; photo: TourPhotoRow }>(
      `/tours/${encodeURIComponent(tourId)}/photos/${encodeURIComponent(photoId)}`,
      { method: 'PATCH', body: JSON.stringify(body) },
    ),

  /** "Add to property photos" - one, or several selected. Each photo answers for itself. */
  promoteTourPhotos: (tourId: string, photoIds: string[]) =>
    request<{ success: boolean; results: PromoteResult[] }>(
      `/tours/${encodeURIComponent(tourId)}/photos/promote`,
      { method: 'POST', body: JSON.stringify({ photo_ids: photoIds }) },
    ),

  /** The report to view and print. Writes nothing. */
  getTourReport: (tourId: string) =>
    request<{ success: boolean } & TourReportResponse>(`/tours/${encodeURIComponent(tourId)}/report`),

  /**
   * The report, FILED on the property when `fileOnPropertyId` is given (LP1.5: the box is off and he ticks
   * it). ⛔ Omitted, the key is not sent at all - never `null` - exactly as generateLeaseProposal.
   */
  fileTourReport: (tourId: string, fileOnPropertyId?: string | null) =>
    request<{ success: boolean } & FiledTourReportResponse>(`/tours/${encodeURIComponent(tourId)}/report`, {
      method: 'POST',
      body: JSON.stringify(fileOnPropertyId ? { file_on_property_id: fileOnPropertyId } : {}),
    }),

  /** The duplicate check before "Not in the app" creates a record (PD2 needed a 154-group merge). */
  checkPropertyDuplicates: (address: string) =>
    request<{ success: boolean; matches: PropertyDuplicateMatch[] }>(
      `/master-properties/duplicates?${new URLSearchParams({ address }).toString()}`,
    ),
};
